/*
(C) Copyright 2015–2022 Potsdam Institute for Climate Impact Research (PIK), authors, and contributors, see AUTHORS file.

This file is part of vodle.

vodle is free software: you can redistribute it and/or modify it under the 
terms of the GNU Affero General Public License as published by the Free 
Software Foundation, either version 3 of the License, or (at your option) 
any later version.

vodle is distributed in the hope that it will be useful, but WITHOUT ANY 
WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR 
A PARTICULAR PURPOSE. See the GNU Affero General Public License for more 
details.

You should have received a copy of the GNU Affero General Public License 
along with vodle. If not, see <https://www.gnu.org/licenses/>. 
*/

/*
TODO:
- encrypt ALL data, also email address, in local storage and pouchdbs except password, but allow for backwards compat.
- only store password when checked
- store emailandpasswordhash for performance
*/

import { Injectable, Inject, OnDestroy, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoadingController, AlertController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';

// rxjs 7 deprecates toPromise(); firstValueFrom is its replacement for a
// source that emits once and completes, which is what HttpClient.get does
import { firstValueFrom } from 'rxjs';

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

import { environment } from '../environments/environment';
import { GlobalService } from './global.service';
import { Poll, Option } from "./poll.service";

import PouchDB from 'pouchdb/dist/pouchdb';

import BLAKE2s from 'blake2s-js'; // TODO: replace by sodium later?

import * as CryptoES from 'crypto-es'; // 3.0 has named exports only: no default, no enc/algo/lib namespaces
const iv = CryptoES.Hex.parse("101112131415161718191a1b1c1d1e1f"); // this needs to be some arbitrary but GLOBALLY CONSTANT value

// 0.8's ES module puts the crypto_* functions on its default export once `ready` resolves;
// the named exports are the helpers only, so a namespace import does not find them.
import Sodium from 'libsodium-wrappers';

import { MatrixService, hashEmail } from './matrix.service';


/** DATA STORAGE DESIGN
 * 
 * 
 * REDUNDANCY
 * 
 * Most data is stored in three places simultaneously, which are continuously synchronized:
 * - a session-specific local temporal cache 
 * - a device-specific local persistent PouchDB
 * - a set of documents with contiguous doc-ids in some remote CouchDB
 * 
 *   
 * SEPARATION BETWEEN USER, POLL, AND VOTER DATA
 * 
 * The data is divided into several portions:
 * - "user data" is data that is not poll-specific, such as overall settings.
 * - "poll data" is data that is poll-specific but not voter-specific, such as poll titel and options
 * - "voter data" is data that is poll- and voter-specific, such as ratings and delegations
 * 
 * User data is stored in a single user cache+PouchDB+CouchDB. A few user data items are stored in the cache only.
 * 
 * Poll and voter data is stored in a poll-specific cache+PouchDB+CouchDB, 
 * i.e. for each poll there is a separate cache+PouchDB+CouchDB.
 * 
 * 
 * FLAT KEY-VALUE DATA MODEL
 * 
 * All data is stored as simple key-value pairs.
 * 
 * Keys are strings that can be hierarchically structures by dots ('.') as separators, 
 * such as 'language' or 'poll.78934865986.db_server_url'.
 * Keys of voter data start with 'voter.' followed by the vid (voter id) and a paragraph sign ("§"), 
 * such as 'voter.968235§option.235896.rating'. Otherwise the colon does not appear in keys.
 * 
 * In the local caches, there is one entry per key, and they key is used without any further prefix.
 * 
 * 
 * MAPPING KEYS TO DOCUMENTS
 * 
 * In the local PouchDBs and remote CouchDBs, there is one document per key that has the following structure:
 * - user data documents: { _id: "~vodle.user.UUU§KEY", value: XXX }
 * - poll data documents: { _id: "~vodle.poll.PPP§KEY", value: YYY }
 * - voter data documents: { _id: "~vodle.poll.PPP.voter.VVV§REST_OF_KEY", value: YYY }
 * 
 * In this, UUU is the hash of the user's email address plus "§" plus their password,
 * PPP is a poll id, and VVV is a voter id.
 * KEY the full key, REST_OF_KEY the key without the part "voter.ZZZ§".
 * XXX is a value encrypted with the user's password, and YYY is a value encrypted with the poll password. 
 * In this way, no-one can infer the actual owner of a document 
 * and no unauthorized person can read the actual values.
 * Note that voter documents are encrypted with the poll password rather than the voter's own password
 * so that all voters in the poll can read all other voters' ratings and delegations. 
 * 
 * 
 * MAPPING DOCUMENTS TO DATABASE USERS
 * 
 * The part of the document _id between '~' and "§" is the database username that is used to 
 * create or update the document: 'vodle.user.UUU', 'vodle.poll.PPP', and 'vodle.poll.PPP.voter.VVV'.
 * The database users 'vodle.user.UUU' and 'vodle.poll.PPP.voter.VVV' have the user's password 
 * as their password, while the database user 'vodle.poll.PPP' has the poll password as its password.
 * Other database users only have read access to the document, but are only able to make 
 * sense of its contents if they have the correct password used for encrypting the value.
 * In this way, no unauthorized person can modify any value.
 * 
 * 
 * REMOTE COUCHDB CONFIGURATION
 * 
 * Each used remote CouchDB is identified by the URL of a CouchDB server (!) 
 * (rather than the URL of a database contained in that server!).
 * The CouchDB server must provide:
 * - a user database named '_users' (which is the standard name for user databases)
 * - a database named 'vodle' that will contain the data
 * - a user named 'vodle' that has write access to both (!) these databases.
 * 
 * The user 'vodle' will be used by the vodle app to automatically create the other database users
 * ('vodle.user.UUU', 'vodle.poll.PPP', and 'vodle.poll.PPP.voter.VVV')
 * when a user logs in the first time, changes their password, creates a new poll, 
 * or starts participating in a poll as a voter.
 * This way the database administrator only needs to be involved when setting up the database initially,
 * but not later on to create users.
 * 
 * Although the database user 'vodle' can create users, it cannot delete or modify users or read their passwords.
 * Also, neither the database user 'vodle' or 'vodle.poll.PPP' can change their own password.
 * This way, no-one can delete or overtake the database users 'vodle.user.UUU' or 'vodle.poll.PPP.voter.VVV'
 * of any other person or prevent others from accessing their personal, poll, or voter data.
 * 
 */

/** TODO:
- ignore vids that have not provided a valid signature document
- if voter keys are individualized (not at first), ignore vids that use a signature some other vid uses as well
- at poll creation, write a pubkey document for each valid voter key, giving each key a random key id
- a valid signature document has _id ~vodle.voter.<vid>§signature_<key id> and value signed(key id)
*/


const user_doc_id_prefix = "~vodle.user.", poll_doc_id_prefix = "~vodle.poll.";

function get_poll_key_prefix(pid:string) {
  return 'poll.' + pid + '.';
}

function make_consistency_failure_error(message: string): Error {
  /** Error signalling that the local cache could not be verified as fully
   *  consistent (e.g. a queued change or tombstone recheck could not be
   *  applied), as opposed to a mere transport/replication failure. Marked
   *  with is_consistency_failure so that callers such as Poll.end() can
   *  distinguish it from an unreachable remote (#292): */
  const err = new Error(message);
  err['is_consistency_failure'] = true;
  return err;
}

// sudo docker run -e COUCHDB_USER=admin -e COUCHDB_PASSWORD=password -p 5984:5984 -d --name test-couchdb couchdb

// some user data keys are only stored locally and not synced to a remote CouchDB:
// (pending_user_data_move: a credential change whose data move has not
// completed yet, see move_user_data / perform_user_data_move, #330;
// ai_api_key: the voter's key for an AI API, which must not leave the
// device, planning/AI_ASSESSMENT.md §7)
const local_only_user_keys = ['local_language', 'email', 'password', 'db', 'db_from_pid', 'db_other_server_url', 'db_custom_password', 'db_server_url', 'db_password', 'pending_user_data_move', 'ai_api_key'];
/** Keys the ACCOUNT owns rather than the device: on these the user room wins
 *  the sync, where every other key is local-wins.
 *
 *  They are the person's SETTINGS — what the settings page writes. A device
 *  has no business holding an opinion about any of them, and yet each one
 *  arrives on a device with a value nobody chose: the language is taken from
 *  the browser without asking on a first start (#193), and the default wap
 *  used to be seeded from the deployment's setting. save_state persists the
 *  whole cache, so a device that has merely been STARTED once carried those
 *  values into its next login, and under local-wins the first device to sync
 *  pushed them over what the person had actually chosen somewhere else. The
 *  owner reported it twice, once for the language and then, with that fixed,
 *  for the default wap — the same defect, one key along, which is why this
 *  is now the rule for the settings rather than an exception for one of them
 *  (#327).
 *
 *  The poll memberships are NOT here and must not be: a device that has just
 *  created or joined a poll is the only one that knows, and its push is how
 *  the account finds out.
 *
 *  A choice made on THIS device does not need the sync to carry it either:
 *  setu writes it to the user room the moment it is made, and queues and
 *  retries it if the server is not there. The one thing the account can win
 *  wrongly is a choice made while offline and still in that queue at the next
 *  start, which shows the old value until the queued write lands. */
// (the e-mail notification settings too: a device's stale copy must not
// undo turning the e-mails off, or the opt-out from poll titles in them;
// EmailService.EMAIL_OFF)
const account_wins_user_keys = ['language', 'theme', 'default_wap',
  'email_notify_address', 'email_notify_token', 'email_notify_titles',
  'email_notify_of_closing_soon', 'email_notify_of_closed', 'email_notify_of_new_option',
  'email_notify_of_delegation_answered'];
// some of these trigger a move from one remote user dvb to another when changed:
const keys_triggering_data_move = ['email', 'password', 'db', 'db_from_pid', 'db_from_pid_server_url', 'db_from_pid_password', 'db_other_server_url','db_custom_password'];

// replication watchdog parameters (issue #292):
const replication_watchdog_interval_ms = 10 * 1000; // how often to check for stalled replications
const replication_stall_threshold_ms = 30 * 1000; // consider a replication stalled after this long without progress
const change_retry_max_attempts = 3; // failed change docs are retried this often before being dropped
const confirmed_put_max_attempts = 5; // confirmed writes (e.g. draft→running data moves) are attempted this often before giving up

// poll states advance monotonically through this sequence and must never move backwards:
const poll_state_order = ['', 'draft', 'running', 'closing', 'closed'];
function poll_state_is_at_least(stored: string, requested: string): boolean {
  return poll_state_order.indexOf(stored) >= poll_state_order.indexOf(requested);
}

// some poll and voter data keys are stored in the user db rather than in the poll db:
const poll_keystarts_in_user_db = [
  'creator', 
  'db', 'db_from_pid', 'db_other_server_url', 'db_custom_password', 'db_server_url', 'db_password', 
  'password', 'myvid', 
  'del_private_key', 'del_nickname', 'del_from', 
  'have_seen', 'have_acted', 'has_been_notified_of_end', 'has_results', 'have_seen_results',
  'is_archived',
  'is_test',
  'origin_server',
  'poll_page',
  'simulated_ratings',
  'start_date',
  'final_rand', 'winner', 'allocation'
];

const user_keys_unencrypted = ['consent', 'last_access'];

const poll_keystarts_requiring_due = ['state', 'option'];
const voter_subkeystarts_requiring_due = ['rating', 'del_request', 'del_response']; 

// ENCRYPTION:

const textEncoder = new TextEncoder();

function encrypt_deterministically(value, password:string) {
  const aesEncryptor = CryptoES.AESAlgo.createEncryptor(CryptoES.Utf8.parse(password), { iv: iv });
  const result = aesEncryptor.process(''+value).toString()+aesEncryptor.finalize().toString(); 
  return result;
}

function encrypt(value, password:string): string {
  try {
    const result = CryptoES.AES.encrypt(''+value, password).toString(); 
    return result;
  } catch (error) {
    return null;
  }
}

function decrypt(value:string, password:string): string {
  try {
    const temp = CryptoES.AES.decrypt(value, password);
    // FIXME: sometimes we get a malformed UTF-8 error on toString: 
    const result = temp.toString(CryptoES.Utf8);
    return result;
  } catch (error) {
    return null;
  }
}

function myhash(what): string {
  // we use Blake2s since it is fast and more reliable than MD5
  const blake2s = new BLAKE2s(environment.data_service.hash_n_bytes); // 16? 32?
  blake2s.update(textEncoder.encode(what.toString())); 
  return blake2s.hexDigest();
}

// TYPES:

/** the credentials a device used until a change of e-mail address or
 *  password — what a pending user data move starts from (#330) */
export type credentials_t = {email: string, password: string, guest?: boolean, attempts?: number};

/** Start the app over at its first page, as the end of a session must.
 *
 * Not a plain window.location.reload(): that comes back to the page it was
 * on, and both pages that end a session — logout and delete-all — are then
 * handed to the login page as the place to return to afterwards, so signing
 * in again landed the person straight back on "do you really want to log
 * out?" / "do you really want to delete everything?". The hash goes to the
 * root first, so the login sends them to their polls instead.
 */
export function restart_at_the_beginning() {
  window.location.hash = '#/';
  window.location.reload();
}

/** the consent recorded when a user logs in or takes part as a guest */
export const consent_statement = 'Yes, I have read the data protection declaration and terms of use. I consent to the processing of my data on user devices and database servers in the described manner, in order to participate in polls. I agree that some of my data will be transmitted to other participants in pseudonymized form. I am aware that my right to have my data deleted is hence constrained insofar as these copies may not be deleted on all user devices. I can revoke this consent by e-mail.';

/** Which delegation this deployment offers — environment.delegation.mode.
 *
 *  #285 made it three per-poll settings that whoever wrote the poll switched
 *  on in the draft. How a voter's wap may reach another voter is a rule of
 *  the vote, though, not a property of the question being asked, and an
 *  author free to pick the rule can pick the one they expect to win under.
 *  So it belongs to the deployment, beside the weight limit.
 *
 *    "simple"     one delegate at a time, for all of the poll's options
 *    "different"  a different delegate for different options
 *    "ranked"     several delegates in the voter's order of preference
 *    "weighted"   several at once, each carrying a share of the voter's wap
 */
export function delegation_mode(): string {
  return environment.delegation.mode || 'simple';
}

export type del_option_spec_t = {type: "+" | "-", oids: Array<string>};
export type del_request_t = {
  option_spec: del_option_spec_t,
  public_key: string,
  // #285: in a poll with ranked delegation, where this delegate stands in the
  // client's order of preference (1 = first choice); in one with weighted
  // delegation, the percentage of trust the client gives them. Both are the
  // client's own statement about their own delegation, so they ride in the
  // client's own request rather than in a poll-wide document.
  rank?: number,
  trust?: number,
  // ... and per option, where the client wants this delegate to carry a
  // different share there than the one they gave them in general. An option
  // with no entry uses `trust`; one where every delegate's entry is 0 is one
  // the client has taken back to rating alone.
  trusts?: {[oid: string]: number},
};
export type del_response_t = {option_spec: del_option_spec_t, status: "agreed" | "declined" | "revoked", "decline_cycle", "decline_self"};
export type del_signed_response_t = string;
export type del_agreement_t = { // by pid, did
  client_vid?: string,
  delegate_vid?: string,
  status?: "pending" | "agreed" | "declined" | "revoked" | "declined_cycle" | "declined_self",
  accepted_oids?: Set<string>, // oids accepted for delegation by delegate
  active_oids?: Set<string> // among those, oids currently activated for delegation by client
};
export type news_t = {
  key: string,
  class: string,
  pid?: string,
  title: string,
  body?: string,
  auto_dismiss: boolean  // whether this is automatically dismissed when seen
}

// TODO: add pid_t, vid_t, oid_t, did_t to prevent confusion! 

// SERVICE:

// attributes of DataService to be stored in storage:
const state_attributes = [
  "user_cache", 
  "_pids", 
  "_pid_oids", 
  "poll_caches", 
  "own_ratings_map_caches", 
  "proxy_ratings_map_caches",
  "effective_ratings_map_caches",
  "max_proxy_ratings_map_caches",
  "argmax_proxy_ratings_map_caches",
  "outgoing_dids_caches",
  "incoming_dids_caches",
  "delegation_agreements_caches",
  "direct_delegation_map_caches",
  "inv_direct_delegation_map_caches",
  "indirect_delegation_map_caches",
  "inv_indirect_delegation_map_caches",
  "effective_delegation_map_caches",
  "inv_effective_delegation_map_caches",
  "tally_caches",
  "news_keys"
];

@Injectable({
  providedIn: 'root'
})
export class DataService implements OnDestroy {

  private G: GlobalService;
  
  private restored_user_cache = false;
  /** whether after_local_only_user_cache_is_filled has run */
  private user_cache_ready = false;
  private restored_poll_caches = false;
  /** resolves once the background Matrix login has finished; the start does
   *  not wait for it, everything that needs the homeserver does (#327) */
  private matrix_ready: Promise<void> = Promise.resolve();
  /** resolves once this device's user data and the user room agree. A POLL
   *  does not wait for this — it needs a logged-in client and nothing else,
   *  and on a magic link the vid and the password come from the link and
   *  this device's own storage, never from the user room. Waiting for it
   *  cost a guest with no polls at all 12.8 s before the poll page began
   *  (#327). */
  private matrix_user_data_ready: Promise<void> = Promise.resolve();

  /** Resolves once this device has read the person's own settings back from
   *  the server, so that the ABSENCE of one means they have none rather than
   *  "not here yet". Whoever would WRITE a default rather than merely show
   *  one waits for this. On the CouchDB backend, and before the Matrix login
   *  has replaced it, this is an already-resolved promise: the user data is
   *  local there, and a caller that gets in ahead of the login is no worse
   *  off than it was before this existed. */
  get user_data_ready(): Promise<void> { return this.matrix_user_data_ready; }

  // current page, used for notifying of changes method:
  page: any;

  /** a magic link was opened on a device without credentials: the joinpoll
   *  page offers to take part as a guest (#193), see
   *  after_local_only_user_cache_is_filled */
  guest_login_pending = false;

  /** the credentials whose data this session uses (after the login, after a
   *  completed move): a change away from them records a pending move (#330) */
  private committed_credentials: credentials_t | null = null;

  /** a guest login (#193) is under way, started by the app rather than the user */
  private guest_login_in_progress = false;

  private loadingElement: HTMLIonLoadingElement;

  can_notify = false;

  // DATA:

  user_cache: {}; // temporary storage of user data
  private local_only_user_DB: PouchDB.Database; // persistent storage of local-only user data
  private _local_synced_user_db: PouchDB.Database = null; // see local_synced_user_db
  /**
   * The persistent local copy of the synced user data — CouchDB's, and made
   * only when something asks for it (#327).
   *
   * On the Matrix backend nothing does, by design: setu() sends everything
   * but the local-only keys (the credentials and the language, which live
   * in local_only_user_DB) to the homeserver instead, and the restore path
   * says so in as many words. Yet it was opened at every start regardless,
   * against a database that on a device of long standing has a CouchDB era
   * behind it. Opening a PouchDB is not free — auto_compaction is on — and
   * this one was pure cost there.
   */
  private get local_synced_user_db(): PouchDB.Database {
    if (!this._local_synced_user_db) {
      this._local_synced_user_db = new PouchDB('local_synced_user', {auto_compaction: true});
      this.G.L.info("DataService opened the local synced user database");
    }
    return this._local_synced_user_db;
  }
  /** assignable, so a test (or a reset) can put its own database there */
  private set local_synced_user_db(db: PouchDB.Database) {
    this._local_synced_user_db = db;
  }

  private remote_user_db: PouchDB.Database; // persistent remote copy of synced user data
  private user_db_sync_handler;

  private _pids: Set<string> = new Set(); // list of pids known to the user
  get pids() { return this._pids; }
  private _pid_oids: Record<string, Set<string>> = {};

  private poll_caches: Record<string, {}>; // temporary storage of poll data
  private local_poll_dbs: Record<string, PouchDB.Database>; // persistent local copies of this user's part of the poll data

  private remote_poll_dbs: Record<string, PouchDB.Database>; // persistent remote copies of complete poll data
  private poll_db_sync_handlers: Record<string, any>;
  // Phase 14: Track Matrix poll event listeners per pid to prevent duplicates
  private _matrixPollListeners: Record<string, any> = {};
  /** the in-flight or finished full load of a poll's contents, per pid (#327) */
  private poll_load_promises: Record<string, Promise<void>> = {};
  // Promise for the async Matrix state-change work (draft→running).
  // Callers (e.g. publish_button_clicked) can await this to ensure all
  // poll data, options, and state have been committed to Matrix before
  // proceeding (e.g. navigating to the share page).
  _matrixStateChangePromises: Record<string, Promise<void>> = {};

  // Caches with redundant information, not stored in database:

  // ratings by pid, oid, vid
  own_ratings_map_caches: Record<string, Map<string, Map<string, number>>>; // redundant storage of ratings data, not stored in database
  proxy_ratings_map_caches: Record<string, Map<string, Map<string, number>>>; // redundant storage of proxy ratings data, not stored in database
  effective_ratings_map_caches: Record<string, Map<string, Map<string, number>>>; // redundant storage of effective ratings data, not stored in database

  // (arg)max (over oids) proxy rating by pid, vid:
  max_proxy_ratings_map_caches: Record<string, Map<string, number>>; // redundant storage of max proxy ratings data, not stored in database 
  argmax_proxy_ratings_map_caches: Record<string, Map<string, Set<string>>>; // redundant storage of oids having max proxy ratings data, not stored in database 

  outgoing_dids_caches: Record<string, Map<string, string>>; // did of delegation requests this voter issues, by pid, oid
  incoming_dids_caches: Record<string, Map<string, [string, string, string]>>; // [from, url, status] of received delegation request links by pid, did 

  delegation_agreements_caches: Record<string, Map<string, del_agreement_t>>; // by pid, did

  direct_delegation_map_caches: Record<string, Map<string, Map<string, string>>>; // redundant storage of direct delegation data, not stored in database
  inv_direct_delegation_map_caches: Record<string, Map<string, Map<string, Set<string>>>>; // redundant storage of inverse direct delegation data, not stored in database
  indirect_delegation_map_caches: Record<string, Map<string, Map<string, Set<string>>>>; // redundant storage of indirect delegation data, not stored in database
  inv_indirect_delegation_map_caches: Record<string, Map<string, Map<string, Set<string>>>>; // redundant storage of inverse indirect delegation data, not stored in database
  effective_delegation_map_caches: Record<string, Map<string, Map<string, string>>>; // redundant storage of effective delegation data, not stored in database
  inv_effective_delegation_map_caches: Record<string, Map<string, Map<string, Set<string>>>>; // redundant storage of inverse effective delegation data, not stored in database

  tally_caches: Record<string, {}>; // temporary storage of tally data, not stored in database

  news_keys: Set<string>;

  // LYFECYCLE:

  private uninitialized_pids: Set<string> = new Set(); // temporary set of pids currently initializing

  private _ready: boolean = false;
  get ready() { return this._ready; }

  private _loading: boolean = false;
  get loading() { return this._loading; }

  private need_poll_db_replication: Record<string, boolean> = {};

  // Consistency hardening state (issue #292):

  // Bootstrap gating: promises that resolve once the respective cache has been
  // fully bootstrapped from the local PouchDB (or restored from storage). Remote
  // syncing may only start after that, since otherwise an incoming remote change
  // could be overwritten by a stale row from a still-running allDocs() bootstrap:
  private user_db_bootstrapped: Promise<void>;
  private mark_user_db_bootstrapped: () => void;
  private reject_user_db_bootstrapped: (reason?: any) => void;
  private poll_db_bootstrapped: Record<string, Promise<void>> = {};
  private mark_poll_db_bootstrapped: Record<string, () => void> = {};
  private reject_poll_db_bootstrapped: Record<string, (reason?: any) => void> = {};

  // Change-event coalescing: incoming db change docs are queued and processed
  // in batches, keeping only the newest change per doc id:
  private change_queue: {pid: string | null, doc: any, deleted: boolean, tally: boolean, confirmed_deletion?: boolean}[] = [];
  private change_queue_scheduled = false;
  private change_queue_timeout: ReturnType<typeof setTimeout> | null = null;
  private change_retry_counts: Record<string, number> = {};
  // pending asynchronous tombstone rechecks (recheck_deleted_doc) whose
  // results still need to be enqueued and applied before a flush is complete:
  private pending_recheck_count = 0;
  private pending_recheck_waiters: (() => void)[] = [];
  // counts tombstone rechecks whose verification failed terminally, so that
  // flushes can report incompleteness while the cache may be stale (#292):
  private recheck_failure_count = 0;

  // draft→running data migrations currently being (re)tried, so that
  // after_changes() retries cannot overlap a still-running migration:
  private draft_migration_in_flight: Record<string, boolean> = {};
  private voter_mutation_generations: Record<string, number> = {};
  private voter_mutation_tails: Record<string, Promise<void>> = {};
  private voter_mutation_failures: Record<string, any> = {};
  private voter_mutation_session = 0;
  // barrier that newly admitted voter mutations must wait for, so that a
  // retired session's already issued writes can no longer interleave with the
  // new session's (#292):
  private voter_mutation_admission: Promise<void> = Promise.resolve();
  private poll_mutation_versions: Record<string, number> = {};

  // Replication watchdog: per-replication progress tracking; keys are
  // 'user' for the user db sync and the pid for poll db syncs:
  private replication_progress: Record<string, number> = {};
  private replication_active: Record<string, boolean> = {};
  replication_stalled: Record<string, boolean> = {}; // exposed for UI/diagnostics

  get replication_is_stalled(): boolean {
    /** whether the watchdog currently considers any replication stalled;
     *  shown as a warning sign in the page headers (#292, #159) */
    return Object.values(this.replication_stalled).some(stalled => !!stalled);
  }

  get sync_pending(): boolean {
    /** whether changes of this device's are still on their way to the
     *  server: the page shows a turning sign while they are (#327). On the
     *  Matrix backend this counts the writes in flight and the ones queued
     *  for another try; the CouchDB backend replicates continuously and has
     *  no such count, so it reports only the stall below. */
    return environment.useMatrixBackend
      && this.matrix_sessions().some(service => service.pendingWriteCount > 0);
  }

  /** every Matrix account this device is signed in as: the person's own and
   *  one per poll (#327). A VOTE is written by the poll's account, so
   *  anything asking "is this device up to date" has to ask them all — the
   *  injected service alone only ever sees the user room, which is why the
   *  header's sign stopped turning for votes when the accounts were split. */
  private matrix_sessions(): MatrixService[] {
    const sessions = Object.values(this.poll_matrix_sessions);
    if (this.matrixService && !sessions.includes(this.matrixService)) {
      sessions.push(this.matrixService);
    }
    return sessions.filter(service => !!service);
  }

  get sync_is_stalled(): boolean {
    /** whether those changes are not merely on their way but stuck. Nothing
     *  is lost either way — vodle keeps retrying, and compares its own votes
     *  against the server once a minute — but the voter gets to see it. */
    return this.replication_is_stalled
      || (environment.useMatrixBackend
          && this.matrix_sessions().some(service => service.syncIsStalled));
  }
  private replication_restart_pending: Record<string, boolean> = {};
  private user_sync_start_pending = false;
  private user_sync_start_generation = 0;
  private poll_sync_start_pending: Record<string, boolean> = {};
  private poll_sync_start_generation: Record<string, number> = {};
  private shutting_down = false;
  private persisted_cache_invalid = false;
  private replication_watchdog_id: any = null;

  constructor(
      private router: Router,
      public loadingController: LoadingController,
      public alertCtrl: AlertController,
      public translate: TranslateService,
      public storage: Storage,
      @Inject(DOCUMENT) private document: Document,
      private matrixService: MatrixService
      ) { 
    // the poll passwords live here (user cache); with them the Matrix backend
    // encrypts each poll's voter data, as every poll document is encrypted on
    // the CouchDB backend (#293):
    if (this.matrixService) {
      this.matrixService.pollPasswordProvider = (pid: string) =>
        this.user_cache?.[get_poll_key_prefix(pid) + 'password'] || null;
      // likewise the user password encrypts the user's own data there:
      this.matrixService.userPasswordProvider = () => this.user_cache?.['password'] || null;
    }
  }

  /**
   * One Matrix account per (poll, voter) — vodle's cross-poll privacy.
   *
   * The CouchDB backend connects to a poll's database as
   * `vodle.poll.<pid>.voter.<myvid>` (see connect_to_remote_poll_db), never
   * as the person, so the server can tie no two polls of one person
   * together. These are the Matrix counterpart: a service of its own per
   * poll, signed in as that poll's vid, which does every poll-room and
   * voter-room operation. The injected service keeps the person's own
   * account and does the user room and nothing else (#327).
   */
  private poll_matrix_promises: {[pid: string]: Promise<MatrixService>} = {};
  /** the same services once they are up, for the callers that cannot wait:
   *  the page asks whether anything is still on its way several times a
   *  second, and a getter may not await a login */
  private poll_matrix_sessions: {[pid: string]: MatrixService} = {};

  /** the service that acts for this device in poll `pid` */
  /* Delegation on the Matrix backend goes to the POLL room, so it goes
   * through the poll's own account like every other poll operation (#327).
   * DelegationService held the injected service — the person's own — which
   * was invisible while delegation was disabled in both environments, and
   * would have written the person's user id into the poll room the moment
   * it was switched on, if the homeserver had taken the write at all. */
  request_delegation(pid: string, did: string, oids: string[]): Promise<string> {
    return this.poll_matrix(pid).then(m => m.requestDelegation(pid, did, oids));
  }

  respond_to_delegation(pid: string, did: string, accept: boolean): Promise<void> {
    return this.poll_matrix(pid).then(m => m.respondToDelegation(pid, did, accept));
  }

  /** Ask the guard bot (EmailService): from the person's own account, or,
   *  with `pid`, from their account in that poll -- the bot takes a poll's
   *  email subscription only from a member of its poll room. The e-mail
   *  requests carry addresses and tokens, so they and their answers are
   *  redacted once answered (MatrixService.askGuardBot). */
  ask_guard_bot(kind: string, params: Record<string, any>, pid?: string): Promise<any> {
    const options = {redact: kind.startsWith('email_')};
    if (pid) {
      return this.poll_matrix(pid).then(m => m.askGuardBot(kind, params, options));
    }
    // the account's own session: not before the login, which runs in the
    // background (the settings page, opened right after a reload, asks
    // while it is still under way)
    return this.matrix_ready.catch(() => { /* the request reports it */ })
      .then(() => this.matrixService.askGuardBot(kind, params, options));
  }

  /** Drop the memoised Matrix session for a poll, so that the next
   *  operation on it signs in again. For a token the homeserver no longer
   *  accepts (#327): everything else is worth retrying with. */
  forget_poll_matrix(pid: string): void {
    delete this.poll_matrix_promises[pid];
    delete this.poll_matrix_sessions[pid];
  }

  poll_matrix(pid: string): Promise<MatrixService> {
    if (!environment.useMatrixBackend) {
      return Promise.resolve(this.matrixService);
    }
    if (!this.poll_matrix_promises[pid]) {
      this.poll_matrix_promises[pid] = this.open_poll_matrix(pid).then(service => {
        this.poll_matrix_sessions[pid] = service;
        return service;
      }).catch(error => {
        // not remembered: a poll whose account could not be signed in must
        // be free to try again on the next operation
        delete this.poll_matrix_promises[pid];
        throw error;
      });
    }
    return this.poll_matrix_promises[pid];
  }
  
  private async open_poll_matrix(pid: string): Promise<MatrixService> {
    const vid = this.getp(pid, 'myvid');
    if (!vid) {
      // previewpoll (creator) and joinpoll (everyone else) both call
      // init_myvid before the poll is ever connected to, exactly as the
      // CouchDB user name needs it
      throw new Error("DataService.poll_matrix: poll " + pid + " has no vid yet");
    }
    const service = MatrixService.forPoll(this.storage, pid, vid);
    service.pollPasswordProvider = this.matrixService.pollPasswordProvider;
    service.userPasswordProvider = this.matrixService.userPasswordProvider;
    service.logger = this.matrixService.logger;
    this.G.L.info("DataService.poll_matrix signing in as this poll's voter", pid);
    await service.signInForPoll(pid, vid, this.user_cache?.['password'] || '');
    // a poll this device took part in BEFORE it had poll accounts has its
    // rooms owned by the person's own account, which must hand them over
    // once or the poll account can write nothing (#327). Costs one local
    // read for every poll that came after.
    await service.takeOverFrom(this.matrixService, pid, vid);
    return service;
  }
  
  /** let go of a poll's account, e.g. when its data is deleted */
  private async close_poll_matrix(pid: string): Promise<void> {
    const pending = this.poll_matrix_promises[pid];
    delete this.poll_matrix_promises[pid];
    delete this.poll_matrix_sessions[pid];
    if (!pending) { return; }
    try {
      const service = await pending;
      if (service !== this.matrixService) { await service.dropSession(); }
    } catch { /* it never came up; nothing to let go of */ }
  }

  /** let go of every poll account this device signed in. Each has a sync
   *  store of its own, so leaving the app has to close them all or the
   *  poll accounts' rooms outlive the session that opened them (#327). */
  private async close_all_poll_matrix(): Promise<void> {
    await Promise.all(Object.keys(this.poll_matrix_promises)
      .map(pid => this.close_poll_matrix(pid)));
  }

  /** log this device out of the person's own account and of every poll
   *  account it signed in */
  private async logout_matrix_everywhere(): Promise<void> {
    await this.close_all_poll_matrix();
    if (this.matrixService?.isLoggedIn()) {
      this.G.L.info("Logging out of Matrix...");
      await this.matrixService.logout();
    }
  }

  /** how long leaving the app waits for the homeserver to be told */
  private static readonly LOGOUT_TIMEOUT_MS = 15000;

  /** Await `what`, but never let leaving the app wait on it for ever: a
   *  poll account whose sign-in is still in flight would otherwise hold the
   *  logout open, and the local side of it — the storage this device holds —
   *  matters more than the server's side and must happen regardless. */
  private static within(ms: number, what: Promise<any>): Promise<void> {
    return Promise.race([
      what.then(() => {}),
      new Promise<void>(resolve => window.setTimeout(resolve, ms)),
    ]);
  }

  ionViewWillLeave() {
    this.save_state();
  }

  ngOnDestroy() {
    console.log("DataService.ngOnDestroy entry");
    this.shutting_down = true;
    this.cancel_voter_mutations();
    this.user_sync_start_generation += 1;
    this.poll_sync_start_generation = {};
    this.stop_replication_watchdog();
    // cancel any pending deferred poll-finalization retry timers (#292):
    if (this.G?.P?.polls) {
      for (const pid in this.G.P.polls) {
        try {
          this.G.P.polls[pid].cancel_end_retry();
        } catch (err) {
          console.warn("DataService.ngOnDestroy could not cancel end retry", pid, err);
        }
      }
    }
    // cancel live replication handlers so no further change events can
    // re-enqueue work after destruction:
    if (this.user_db_sync_handler) {
      try {
        this.user_db_sync_handler.cancel();
      } catch (err) {
        console.warn("DataService.ngOnDestroy could not cancel user data sync", err);
      }
      this.user_db_sync_handler = null;
    }
    if (this.poll_db_sync_handlers) {
      for (const pid in this.poll_db_sync_handlers) {
        if (this.poll_db_sync_handlers[pid]) {
          try {
            this.poll_db_sync_handlers[pid].cancel();
          } catch (err) {
            console.warn("DataService.ngOnDestroy could not cancel poll data sync", pid, err);
          }
        }
      }
      this.poll_db_sync_handlers = {};
    }
    const flushed = this.flush_change_queue();
    if (flushed) {
      this.save_state();
    } else {
      if (this.change_queue_timeout != null) {
        clearTimeout(this.change_queue_timeout);
        this.change_queue_timeout = null;
      }
      this.change_queue_scheduled = false;
      this.change_queue = [];
      this.change_retry_counts = {};
      if (this.storage) {
        this.storage.remove('state');
      }
    }
    console.log("DataService.ngOnDestroy exit");
  }

  save_state(): Promise<any> {
    if (!this.G) {
      // the service was destroyed before init(G) ever ran (e.g. dependency
      // injection teardown), so there is no state worth saving:
      return Promise.resolve();
    }
    this.G.L.entry("DataService.save_state");
    if (this.persisted_cache_invalid) {
      // a replicated change was terminally dropped, so the in-memory caches
      // may be stale. Do not persist them; instead make sure the next startup
      // rebuilds the caches from local PouchDB (#292):
      this.G.L.warn("DataService.save_state not persisting caches since a change was dropped; next startup will bootstrap from local db");
      this.G.L.exit("DataService.save_state");
      if (!this.storage) {
        return null;
      }
      return this.storage.remove('state');
    }
/*
    this.G.L.trace("DataService.save_state _pids", [...this._pids]);
    this.G.L.trace("DataService.save_state _pid_oids", JSON.stringify(this._pid_oids));
    for (const pid in this._pid_oids) {
      this.G.L.trace("DataService.save_state _pid_oids", pid, [...this._pid_oids[pid]]);
    }
*/
    const state = {};
    for (const a of state_attributes) {
      state[a] = this[a];
    }
    this.G.L.exit("DataService.save_state");
    if (!this.storage) {
      return null;
    }
    return this.storage.set('state', state)
  }

  // INITIALIZATION

  /** Initialization process overview
      -------------------------------

    TODO: update this overview comment to the actual process and to renamed method names!

  init()
  `–– try restoring caches from storage 
      init_databases()
      `– asynchronously: 
        process_local_only_user_docs()
        |– if necessary, first redirect to email and password prompt on login page 
        `– email_and_password_exist()
            |
            |– asynchronously: 
            |  local_user_docs2cache()
            |  |– doc2user_cache() for each doc
            |  |  `– for each new poll id:
            |  |     asynchronously:
            |  |     start_poll_initialization()
            |  |     `– local_poll_docs2cache()
            |  |        `– doc2user_cache() for each doc
            |  |
            |  |– once all user docs are processed:
            |  |  for each new poll id:
            |  |  connect_to_remote_poll_db()
            |  |  |– get_remote_connection()
            |  |  `– start_poll_sync()
            |  |     `– handle_poll_db_change() whenever local or remote db has changed
            |  |  
            |  `– once all polls are initialized:
            |     local_docs2cache_finished() 
            |     |– after_changes()
            |     `– notify page that we are ready via <page>.onDataReady()
            |
            `– meanwhile: 
              |– if necessary, first redirect to db credentials prompt on login page 
              `– connect_to_remote_user_db()
                  |– get_remote_connection()
                  `– start_user_sync()
                    `– handle_user_db_change() whenever local or remote db has changed
  */

  init(G: GlobalService) {
    // called by GlobalService
    G.L.entry("DataService.init");
    this.G = G;
    // MatrixService.init was never called from the app: its logger stayed
    // undefined — which is why nothing it logs through this.logger ever
    // reached a console — and, worse, the 'online' listener it registers was
    // never registered, so a queued write waited for the next sync tick
    // instead of going out the moment the connection came back (#326, #327).
    this.matrixService?.init(G.L);
    const mutations_finished = this.cancel_voter_mutations();
    // a deliberate new initialization begins, so clear any teardown state
    // left over from a previous logout/destruction (#292):
    this.shutting_down = false;
    // if necessary, show a loading animation:
    this.show_loading();
    // now start the complicated and partially asynchronous data initialization procedure (see overview in comment below):
    // initialize caches that only live during current session:
    this.user_cache = {};
    this._pids = new Set();
    this._pid_oids = {};
    this.poll_caches = {};
    this.tally_caches = {};
    this.own_ratings_map_caches = {};
    this.outgoing_dids_caches = {};
    this.incoming_dids_caches = {};
    this.delegation_agreements_caches = {};
    this.direct_delegation_map_caches = {};
    this.inv_direct_delegation_map_caches = {};
    this.indirect_delegation_map_caches = {};
    this.inv_indirect_delegation_map_caches = {};
    this.effective_delegation_map_caches = {};
    this.inv_effective_delegation_map_caches = {};
    this.proxy_ratings_map_caches = {};
    this.max_proxy_ratings_map_caches = {};
    this.argmax_proxy_ratings_map_caches = {};
    this.effective_ratings_map_caches = {};
    this.news_keys = new Set();
    // make sure storage exists:
    // Everything below hangs off this read, and a start that sat for
    // eighty seconds before it had even attempted a login had to have sat
    // here — in IndexedDB, through Ionic Storage — with nothing said. So
    // it says something (#327).
    const boot_started_at = Date.now();
    const boot = (stage: string, detail?: any) => {
      console.log("[vodle boot] +" + (Date.now() - boot_started_at) + "ms", stage,
        detail === undefined ? "" : detail);
      // ...and where a waiting page can show it, since a console the owner
      // cannot see is a diagnosis nobody can make (#327):
      MatrixService.noteBootStage(stage);
    };
    (this as any).boot_log = boot;
    boot("data service init");
    // 5.4 MB of crypto WebAssembly the start cannot finish without. Asking
    // for it here rather than after the login lets it download WHILE the
    // login and the registration happen: in the owner's guest start of
    // 2026-09-12 those took 4.9 s and the WASM 8.9 s, one after the other
    // (#327). The fetch is memoised, so the await in initializeWithToken is
    // the same promise, and a failure here is nobody's to handle — crypto
    // reports and degrades on its own path.
    if (environment.useMatrixBackend && environment.matrix.enable_e2ee) {
      MatrixService.fetchCryptoWasm().catch(() => { /* reported where it is awaited */ });
    }
    this.storage.create();
    boot("local storage created");
    // restore state from storage:
    mutations_finished.then(() => this.storage.get('state'))
    .then((state) => {
      boot("restored from local storage", state ? "state found" : "nothing stored yet");
      if (!!state) {
        G.L.debug('DataService got state from storage');
        for (const a of state_attributes) {
          if ((a in state) && (state[a] != undefined)) {
            this[a] = state[a];
            G.L.trace("DataService restored attribute", a, "from storage");
          } else {
            G.L.warn("DataService couldn't find attribute", a, "in storage");
          }
        }
        if ('user_cache' in state) {
          this.restored_user_cache = true;
        }
        if (('_pids' in state) && ('poll_caches' in state)) {
          this.restored_poll_caches = true;
        }
        boot("state restored into the caches",
          this._pids ? this._pids.size + " polls" : "no polls");
        if (environment.show_debug_info) {
          this.G.L.trace("DataService.init _pids", JSON.stringify(this._pids));
          this.G.L.trace("DataService.init _pid_oids", JSON.stringify(this._pid_oids));
          for (const pid in this._pid_oids) {
            this.G.L.trace("DataService.init _pid_oids", pid, [...this._pid_oids[pid]]);
          }
        }
      } else {
        G.L.warn('DataService could not get state from storage (empty)', state);
      }
    }).catch((error) => {
      G.L.warn('DataService could not get state from storage:', error);
    }).finally(() => {
      boot("opening the local databases");
      this.init_databases();
    });
    this.init_notifications(false);

    // test sodium:
    this.test_sodium.bind(this)();

    G.L.exit("DataService.init");
  }
  
  // User data initialization:

  private init_databases() {
    this.G.L.entry("DataService.init_databases");

    // access locally stored data and get some statistics about it:
    this.local_only_user_DB = new PouchDB('local_only_user', {auto_compaction: true});

    /* deactivated for performance:
    this.local_only_user_DB.info()
    .then(doc => { 

      this.G.L.debug("DataService local_only_user_DB info", doc);

    }).catch(err => {

      this.G.L.error("DataService local_only_user_DB error", err);

    });
    */

    // the synced user database is opened by its getter, when something
    // actually wants it — which on the Matrix backend is never (#327)
    this._local_synced_user_db = null;
    (this as any).boot_log?.("local databases open");

    /* deactivated for performance:
    this.local_synced_user_db.info()
    .then(doc => { 

      this.G.L.debug("DataService local_synced_user_DB info", doc);

    }).catch(err => {

      this.G.L.error("DataService local_synced_user_DB error", err);

    });
    */

    this.uninitialized_pids = new Set();
    this.local_poll_dbs = {};
    this.remote_poll_dbs = {};
    this.poll_db_sync_handlers = {};

    // consistency hardening state (issue #292):
    this.user_db_bootstrapped = new Promise<void>((resolve, reject) => {
      this.mark_user_db_bootstrapped = resolve;
      this.reject_user_db_bootstrapped = reject;
    });
    this.poll_db_bootstrapped = {};
    this.mark_poll_db_bootstrapped = {};
    if (!environment.useMatrixBackend) {
      // watch for stalled CouchDB replications (Matrix has its own fallbacks):
      this.start_replication_watchdog();
    }

    if (this.restored_user_cache) {
      // user_cache was restored from storage.

      (this as any).boot_log?.("the user cache came from storage");
      this.after_local_only_user_cache_is_filled();

    } else {
      (this as any).boot_log?.("no stored user cache: reading every local-only document");
      // try restoring from local PouchDB:

      this.user_cache = {};
      // ASYNC:
      // Now start filling the temporary session cache with the persistent local data and syncing with remote data.
      // Because of PouchDB, this must be done asynchronously.
      // First, we fetch all local-only docs:
      this.local_only_user_DB.allDocs({
        include_docs: true
      }).then(

        // process them:
        this.process_local_only_user_docs.bind(this)

      ).catch(err => {

        this.G.L.error(err);

      });
    }

    this.G.L.exit("DataService.init_databases");
  }

  private process_local_only_user_docs(result) {
    this.G.L.entry("DataService.process_local_only_user_docs");
    // copy data from local-only docs to cache:
    for (const row of result.rows) {
      const doc = row.doc, key = doc['_id'], value = doc['value'];
      this.user_cache[key] = value;
      this.G.L.trace("DataService.process_local_only_user_docs filled user cache with key", key, "and value", value);
      if (key=='local_language') {
        // adjust app language:
        const used_lang = (value||'')!=''?value:environment.default_lang;
        this.translate.use(used_lang);
        this.document.documentElement.lang = used_lang; 
      }
    }
    this.after_local_only_user_cache_is_filled();
    this.G.L.exit("DataService.process_local_only_user_docs");
  }

  /** A link that may be opened on a device with no credentials and is then
   *  followed as a guest rather than sent to the login page (#193): an
   *  invitation link, and a delegation link that carries the poll's
   *  password, which is to say the invitation (#341). */
  static route_is_magic_link(route: string): boolean {
    return route.includes('/joinpoll/')
        || (route.includes('/delrespond/') && /[?&]poll_password=/.test(route));
  }

  /**
   * Where the app is, as the BROWSER knows it rather than as the router does.
   *
   * DataService.init runs during the app's bootstrap, and `router.url` is
   * "/" until the router's own initial navigation has finished. The two race,
   * and the loser decides whether a magic link opened on a device with no
   * credentials takes part as a guest or is redirected to the login page — a
   * redirect the in-flight navigation to the join page then cancels, leaving
   * the join page on screen with nothing behind it and no further log line.
   * That is the freeze the owner reported after using the logout button
   * (#193, #327). The location hash is set before any of this app's code
   * runs, so it cannot race with it.
   */
  private current_route(): string {
    const hash = this.location_hash();
    if (hash.startsWith('#')) { return hash.substring(1); }
    return this.router?.url || '';
  }

  /** the browser's URL fragment; its own method so that a spec can say what
   *  the browser shows without navigating the test page */
  private location_hash(): string {
    try {
      return (typeof window !== 'undefined' && window.location && window.location.hash) || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Make sure a magic link on a device without credentials gets its guest.
   *
   * The start normally decides this (below); this is the second line of
   * defence, called by the join page when it has been waiting a few seconds,
   * for the case where the start could not see the route or the page arrived
   * after the decision (#193, #327). Idempotent and cheap: it does nothing
   * unless the user cache is ready, there are no credentials, and no guest
   * is already on its way.
   */
  ensure_guest_for_magic_link(): void {
    if (!this.user_cache_ready || this.guest_login_pending || this.guest_login_in_progress) {
      return;
    }
    if ((this.user_cache['email']||'') != '' && (this.user_cache['password']||'') != '') {
      return;
    }
    this.G.L.warn("DataService.ensure_guest_for_magic_link: the join page is waiting and there are no credentials, creating a guest");
    (this as any).boot_log?.("the join page asked for a guest");
    this.guest_login_pending = true;
    this.login_as_guest();
  }

  private after_local_only_user_cache_is_filled() {
    (this as any).boot_log?.("the user cache is ready");
    this.user_cache_ready = true;
    this.G.L.entry("DataService.after_user_cache_is_filled");
    // check if email and password are set:
    if ((this.user_cache['email']||'')=='' || (this.user_cache['password']||'')=='') {
      this.hide_loading();
      const route = this.current_route();
      (this as any).boot_log?.("no credentials; the page is", route || "(not known yet)");
      if (DataService.route_is_magic_link(route)) {
        // the first visit of a magic link on this device (#193): take part
        // as a guest right away instead of asking for a login first — the
        // visitor votes before registering anything. When the deployment
        // has a privacy statement, the consent question waits on the poll
        // page (consent_pending); a later login moves the guest's data to
        // the account (#330).
        this.G.L.info("DataService found no credentials on a magic link, taking part as a guest");
        this.guest_login_pending = true;
        this.login_as_guest();
      } else {
        this.G.L.info("DataService found empty email or password, redirecting to login page.");
        if (!route.includes('/login')) {
          const current_url = encodeURIComponent(route);
          this.router.navigate([(this.user_cache['local_language']||'')==''?'/login/start/'+current_url:'/login/used_before/'+current_url]);
        }
      }
    } else {
      this.email_and_password_exist();
    }
    this.G.L.exit("DataService.after_user_cache_is_filled");
  }

  private async email_and_password_exist() {
    (this as any).boot_log?.("credentials found, starting the backend");
    this.G.L.entry("DataService.email_and_password_exist: email", 
      this.user_cache['email']);

    // a change of the credentials whose data move did not complete (#330):
    // on the CouchDB backend it is completed before the local user db is
    // read below, since the documents' ids carry the identity
    const pending_move = this.pending_user_data_move();
    if (pending_move && !environment.useMatrixBackend) {
      try {
        await this.perform_user_data_move(pending_move);
      } catch (err) {
        this.G.L.error("DataService: the pending user data move failed, continuing with the new credentials", err);
      }
    }

    /*
    The Matrix login no longer stands between the user and their polls
    (#327).

    Everything the poll list shows — the polls, their titles, their state,
    the ratings and even the computed tallies — was restored from this
    device's own storage a few lines above (state_attributes). The app
    nevertheless waited here for a login, a sync of every room and a read of
    every poll before it would clear the spinner, which is how showing a list
    that was already in hand came to take half a minute.

    So: the login runs, and the rest of the start does not wait for it.
    Anything that needs the homeserver awaits `matrix_ready` instead — see
    connect_to_remote_poll_db and the write paths — and a write made before
    it resolves is queued by the offline queue rather than lost.
    */
    if (environment.useMatrixBackend) {
      const email = this.user_cache['email'];
      const password = this.user_cache['password'];
      if (email && password) {
        this.G.L.info("DataService: logging into the Matrix backend in the background");
        this.matrix_ready = (async () => {
        try {
          if (pending_move) {
            // the move logs in as the new account itself (#330, #193):
            try {
              await this.perform_user_data_move(pending_move);
            } catch (err) {
              this.G.L.error("DataService: the pending user data move failed, logging in without it", err);
            }
          }
          if (!this.matrixService.isLoggedIn()) {
            // The access token this device already holds is enough: a
            // password login on every page load costs a round trip and the
            // key derivation, and leaves a new device on the homeserver each
            // time. It falls back to the password when there is no usable
            // token for this address (#327).
            (this as any).boot_log?.("resuming the stored session");
            if (!await this.matrixService.resumeSession(email)) {
              (this as any).boot_log?.("no stored session, logging in with the password");
              // a guest's credentials were invented a moment ago, so the
              // account cannot exist yet and the login ladder would only
              // produce refusals (#193, #327):
              await this.matrixService.login(email, password, true,
                this.guest_login_in_progress);
            }
          }
          (this as any).boot_log?.("logged in to the homeserver");
          this.committed_credentials = this.credentials_snapshot();
          this.G.L.info("DataService: Matrix login successful, syncing user data");
          // NOT awaited here: the client is usable now, and that is all a
          // poll needs. The sync writes one state event per user-data key
          // and reads the user room back, which for a fresh guest means
          // creating the room first — 12.8 s in the owner's measurement,
          // every millisecond of it in front of a poll page that needed
          // none of it (#327).
          this.matrix_user_data_ready = this.sync_and_settle_user_data();
          this.matrix_user_data_ready.catch(err =>
            this.G.L.error("DataService: the user data sync failed", err));
          // the email subscriptions follow the settings the sync brings
          this.matrix_user_data_ready.then(() => this.G.E?.schedule_sync(), () => {});
          (this as any).boot_log?.("the backend is ready");
          this.G.L.info("DataService: Matrix initialization complete");
        } catch (err: any) {
          this.G.L.error("DataService: Matrix login failed", err?.errcode || err?.message || err);
          
          // Provide specific error messages based on the error type
          let errorMessage = "Failed to connect to Matrix server.";
          if (err?.errcode === 'M_FORBIDDEN' && err?.message?.includes('Registration has been disabled')) {
            errorMessage = 
              "Matrix registration is disabled on the server.\n\n" +
              "To enable registration, add this to matrix-data/homeserver.yaml:\n\n" +
              "enable_registration: true\n\n" +
              "Then restart the Matrix server:\n" +
              "docker-compose -f docker-compose.matrix.yml restart";
          } else if (err?.message?.includes('ECONNREFUSED') || err?.message?.includes('fetch failed')) {
            errorMessage = 
              "Cannot connect to Matrix server at " + environment.matrix.homeserver_url + "\n\n" +
              "Please check that the Matrix homeserver is running:\n" +
              "./start-matrix-server.sh";
          }
          
          this.report_login_failure(errorMessage);
        }
        })();
        // the start does not wait for it, but a failure must not become an
        // unhandled rejection either:
        this.matrix_ready.catch(err =>
          this.G.L.error("DataService: the background Matrix login failed", err));
      }
    }

    if (this.restored_user_cache) {

      if (environment.useMatrixBackend) {
        // In Matrix mode, setu() deliberately skips local_synced_user_db, so
        // reconciling the restored cache against that empty/stale PouchDB
        // would remove Matrix-backed values (e.g. poll.<pid>.myvid and poll
        // passwords) from user_cache. Continue with the restored cache as is:
        this.mark_user_db_bootstrapped();
        this.init_poll_data();
      } else {
        // user_cache was restored from storage. A replication may have committed
        // documents to local PouchDB (and advanced its checkpoint) after that
        // cache was last persisted, so still reconcile the cache (including
        // deletions) from local PouchDB before resolving the user db bootstrap
        // gate (#292):

        // ASYNC:
        let bootstrap_seq = null;
        this.local_synced_user_db.info()
        .then(info => {

          // store last_seq as reference point for the changes feed:
          bootstrap_seq = info.update_seq;
          this.user_cache['user_last_seq'] = info.update_seq;
          this.G.L.trace("DataService recorded user db update_seq at bootstrap", info.update_seq);

          return this.local_synced_user_db.allDocs({
            include_docs:true
          });

        }).then(result => {

          this.reconcile_restored_user_cache.bind(this)(result);
          // replay local writes committed after the allDocs() snapshot was
          // taken, so that they cannot be lost when their later
          // push-direction sync events are ignored (#292):
          return this.local_synced_user_db.changes({
            since: bootstrap_seq,
            include_docs: true
          });

        }).then(changes => {

          this.apply_user_bootstrap_changes(changes);
          this.mark_user_db_bootstrapped();
          // background pass resolving any pre-existing conflicts (#292):
          this.scan_user_db_for_conflicts();

          this.init_poll_data();

        }).catch(err => {

          this.G.L.error("DataService could not reconcile restored user cache with local_synced_user_DB", err);
          this.reject_user_db_bootstrapped(err);

        });
      }

    } else {
      // try restoring from local PouchDB:

      // ASYNC:
      // while remote synchronisation is happening (potentially slow, to be started below), 
      // already fetch all current local versions of synced docs.
      // Record the local db's update_seq *before* taking the allDocs snapshot,
      // then bootstrap the cache. Syncing is gated on this bootstrap having
      // completed (see start_user_sync), so that no incoming change can be
      // overwritten by stale bootstrap data (#292):
      let bootstrap_seq = null;
      this.local_synced_user_db.info()
      .then(info => {

        // store last_seq as reference point for the changes feed:
        bootstrap_seq = info.update_seq;
        this.user_cache['user_last_seq'] = info.update_seq;
        this.G.L.trace("DataService recorded user db update_seq at bootstrap", info.update_seq);

        return this.local_synced_user_db.allDocs({
          include_docs:true
        });

      }).then(result => {

        this.local_user_docs2cache.bind(this)(result);
        // replay local writes committed after the allDocs() snapshot was
        // taken, so that they cannot be lost when their later push-direction
        // sync events are ignored (#292):
        return this.local_synced_user_db.changes({
          since: bootstrap_seq,
          include_docs: true
        });

      }).then(changes => {

        this.apply_user_bootstrap_changes(changes);
        // the user database has been read back, so an absent setting means
        // absent here as well — the same point the Matrix backend reaches
        // when its user-room sync resolves
        this.ensure_user_defaults();
        this.mark_user_db_bootstrapped();
        // background pass resolving any pre-existing conflicts (#292):
        this.scan_user_db_for_conflicts();

      }).catch(err => {

        this.G.L.error("DataService could not read local_synced_user_DB", err);
        this.reject_user_db_bootstrapped(err);

      });
    }

    // Phase 16: When using Matrix backend, skip CouchDB remote connection entirely.
    // Matrix handles all remote data sync via MatrixService.
    // If Matrix login failed, the app can still work with local-only data (drafts).
    if (environment.useMatrixBackend) {
      this.G.L.info("DataService: Matrix backend active, skipping CouchDB connection");
      if (this.router.url.includes('/login')) {
        this.G.remove_spinning_reason("login");
        const then_url = (!!this.page) ? this.page.then_url || '' : '';
        this.router.navigate(['/login/connected/' + then_url]);
      }
      this.G.L.exit("DataService.email_and_password_exist");
      return;
    }

    // check if db credentials are set:
    if (this.has_user_db_credentials()) {

      this.committed_credentials = this.credentials_snapshot();
      // ASYNC:
      // connect to remote and start sync:
      this.connect_to_remote_user_db()
      .then(success => {

        if (this.router.url.includes('/login')) {
          this.G.remove_spinning_reason("login");
          this.router.navigate(['/login/connected/' + ((!!this.page) ? this.page.then_url || '' : '')]);
        } 

      }).catch(err => {

        this.G.L.warn("DataService could not connect to remote user db", err);

      });

    } else {
      this.G.L.warn("DataService found insufficient db credentials, redirecting to login page.");
      this.router.navigate(['/login/db_credentials/missing']);
      // TODO: make that page
    }
    this.G.L.exit("DataService.email_and_password_exist"); 
  }

  /** Signing in, from the user data's point of view: read the user room and
   *  reconcile it with this device, then settle the values that only make
   *  sense once that has happened.
   *
   *  The two belong together and their ORDER is the whole of their meaning —
   *  ensure_user_defaults exists to act on an absence, and before the sync
   *  an absent value only means not-here-yet. They are one method so that a
   *  spec cannot drive them in an order the app never uses, which is how a
   *  language that no device ever showed passed its specs twice (#327).
   */
  private async sync_and_settle_user_data(force = false): Promise<void> {
    await this.syncUserDataWithMatrix(force);
    (this as any).boot_log?.("user data synced");
    this.ensure_user_defaults();
    this.G.L.info("DataService: Matrix user data synced");
  }

  /**
   * Reconcile the local user cache with this account's Matrix user room
   * (#293, #330): local values are pushed where the room's value differs
   * (all of them with `force`, which re-encrypts everything after a
   * password change or an account switch), and keys only the room holds
   * are taken over — the restore of settings and poll memberships (voter
   * ids, poll passwords, drafts) on a second device. Local values win,
   * because they are what the user sees — EXCEPT for the keys the account
   * owns rather than the device (account_wins_user_keys), where a device's
   * copy is at best a stale echo. The device-local keys (credentials,
   * database settings) never leave the device. Until 2026-09-10 the poll
   * membership keys were not synced at all, so a second device knew none
   * of the user's polls.
   */
  private async syncUserDataWithMatrix(force = false): Promise<void> {
    this.G.L.entry("DataService.syncUserDataWithMatrix", force);
    let remote: Record<string, any> = {};
    try {
      remote = await this.matrixService.getAllUserData();
    } catch (err) {
      this.G.L.error("DataService.syncUserDataWithMatrix could not read the user room, pushing everything", err);
      force = true;
    }
    let restored = 0;
    const to_push = Object.keys(this.user_cache).filter(key => {
      if (local_only_user_keys.includes(key) || key == 'user_last_seq') { return false; }
      if (!force && account_wins_user_keys.includes(key)
          && remote[key] !== undefined && remote[key] !== '') {
        // the account has one of its own, and this device's copy is at best
        // a stale echo of it (see account_wins_user_keys)
        return false;
      }
      const value = this.user_cache[key];
      if (value === undefined || value === null || value === '') { return false; }
      return force || remote[key] !== value;
    });
    // One state event per key, and they used to go out one after another: a
    // published poll of fifty writes about thirty-five of them into the user
    // room, which at a round trip each is most of a minute on a slow link
    // (#327). They do not depend on one another, so they go together, as far
    // apart as the client's own write pacing says (matrix.write_burst).
    const pushed = to_push.length;
    await MatrixService.forEachConcurrently(to_push,
      environment.data_service.matrix_user_data_concurrency ?? 8,
      async key => { await this.matrixService.setUserData(key, this.user_cache[key]); });
    for (const [key, value] of Object.entries(remote)) {
      if (local_only_user_keys.includes(key) || value === '') {
        continue;
      }
      const local = this.user_cache[key];
      const account_wins = !force && account_wins_user_keys.includes(key);
      if (local === undefined || local === null || local === '' || account_wins) {
        if (this.user_cache[key] === value) { continue; }
        this.user_cache[key] = value;
        restored++;
        // a poll this device did not know yet:
        this.check_whether_poll_or_option(key, value);
      }
    }
    this.G.L.info("DataService.syncUserDataWithMatrix pushed", pushed, "and restored", restored, "keys");
    this.G.L.exit("DataService.syncUserDataWithMatrix");
  }

  /** the message of the last login failure, for a page that enters later
   *  (the joinpoll page of a guest whose account could not be created, #193) */
  login_failure: string | null = null;

  /** a login failure goes to the page when it can show it, else — for a
   *  login the user asked for — to an alert; a guest login started on the
   *  user's behalf must not block the app with an alert, the joinpoll page
   *  shows the failure when it enters */
  private report_login_failure(message: string) {
    this.login_failure = message;
    if (this.page && this.page.onLoginFailed) {
      this.page.onLoginFailed(message);
    } else if (!this.guest_login_in_progress) {
      alert(message);
    }
  }

  private init_poll_data() {
    // called when user_cache could be restored from storage and email and password exist.
    // checks for existence of all poll caches.
    if (!this.restored_poll_caches) {
      let initializing_polls = false;
      // TODO: go through user cache for pids
      for (const key in this.user_cache) {
        if (this.check_whether_poll_or_option(key, this.user_cache[key])) {
          initializing_polls = true;
        }
      }
    } else if (!environment.useMatrixBackend) {
      // Reconcile restored caches with local winners before conflict cleanup
      // and readiness, just as for a cold bootstrap (#292).
      for (const pid of this._pids) {
        if (!this.pid_is_draft(pid)) {
          this.ensure_local_poll_data(pid);
        }
      }
    } else {
      // Matrix: the restored caches ARE the poll data — there is no local
      // PouchDB to reconcile them against, so the branch above does not run
      // and, until 2026-09-11, nothing else started these polls' lifecycles
      // either. A poll restored from the cache therefore kept the state it
      // had when the app was last closed: one whose deadline had passed in
      // the meantime stayed "running" for ever — listed under Running polls,
      // never tallied, never showing its result — and one that was still
      // running got no timer, so it did not close while the app was open
      // either. start_lifecycle() ends a poll that is past due and sets the
      // timer for one that is not; it is idempotent (#327).
      for (const pid of this._pids) {
        if (this.pid_is_draft(pid)) { continue; }
        const p = this.G.P.polls[pid] || new Poll(this.G, pid, false);
        p.start_lifecycle();
      }
    }
    if (this.uninitialized_pids.size == 0) {
      this.local_docs2cache_finished();
    }
  }

  private has_user_db_credentials() {
    // return whether poll db credentials are nonempty:
    this.G.S.compute_db_credentials();
    return this.getu('db_server_url')!='' && this.getu('db_password')!=''; // && !!this.email_and_pw_hash();
  }

  private local_user_docs2cache(result) {
    // called whenever a connection to a remote user db was established
    this.G.L.entry("DataService.local_user_docs2cache");
    // decrypt and process all synced docs:
    let initializing_polls = false;
    for (const row of result.rows) {
      const [dummy, initializing_poll] = this.doc2user_cache(row.doc);
      initializing_polls = initializing_polls || initializing_poll;
    }
    if (!initializing_polls) {
      this.local_docs2cache_finished();
    } // else that will only be called after poll initialization has finished.
    this.G.L.exit("DataService.local_user_docs2cache");
  }

  private reconcile_restored_user_cache(result) {
    // called when the user_cache was restored from storage, to reconcile it
    // with the current state of local PouchDB before the user db bootstrap
    // gate is resolved (#292):
    this.G.L.entry("DataService.reconcile_restored_user_cache");
    // documents deleted from the local db since the cache was last persisted
    // are not returned by allDocs(), so explicitly apply such deletions:
    const snapshot_ids = new Set<string>();
    for (const row of result.rows) {
      snapshot_ids.add(row.id);
    }
    const prefix = user_doc_id_prefix + this.get_email_and_pw_hash() + "§";
    for (const key of Object.keys(this.user_cache)) {
      if (local_only_user_keys.includes(key) || key == 'user_last_seq') {
        // not backed by a synced user db doc:
        continue;
      }
      if (!snapshot_ids.has(prefix + key)) {
        this.G.L.trace("DataService.reconcile_restored_user_cache dropping cache entry without backing doc", key);
        this.handle_deleted_user_doc({_id: prefix + key});
      }
    }
    // then overlay the snapshot's current document values:
    for (const row of result.rows) {
      this.doc2user_cache(row.doc);
    }
    this.G.L.exit("DataService.reconcile_restored_user_cache");
  }

  private apply_user_bootstrap_changes(changes) {
    /** apply local user db changes committed between the bootstrap's allDocs()
     *  snapshot and the changes() replay, before the bootstrap gate opens (#292). */
    for (const change of changes.results || []) {
      if (change.deleted) {
        this.handle_deleted_user_doc({_id: change.id});
      } else if (change.doc) {
        this.doc2user_cache(change.doc);
      }
    }
    if (changes.last_seq != null) {
      this.user_cache['user_last_seq'] = changes.last_seq;
    }
  }

  private connect_to_remote_user_db(quiet = false) {
    // called at initialization and whenever db credentials were changed
    // (quiet: a failure is only logged, the login page is not shown)
    this.G.L.entry("DataService.connect_to_remote_user_db", quiet);
    const user_password = this.user_cache['password'];
    const user_db_private_username = "vodle.user." + this.get_email_and_pw_hash();

    const promise = new Promise((resolve, reject) => {

      // ASYNC:
      this.get_remote_connection(
        this.getu('db_server_url'), this.getu('db_password'),
        user_db_private_username, user_password
      ).then(db => { 

        this.remote_user_db = db;
        // start synchronisation asynchronously:
        this.start_user_sync();

        // store login month:
        const now = new Date();
        this.setu('last_access', ''+now.getUTCFullYear()+'/'+String(now.getUTCMonth()+1).padStart(2, '0'));

        // RESOLVE:
        resolve(true);

      }).catch(err => {

        this.G.L.warn("DataService.connect_to_remote_user_db failed" + (quiet ? "" : ", redirecting to login page"), err);
        // TODO: if no network, notify and try again when network available. if wrong url or password, ask again for credentials. if wrong permissions, notify to contact db admin. also set 'ready' to false?
        if (!quiet) {
          this.router.navigate(['/login/db_credentials/failed']);
        }
        // TODO: make that page

        // REJECT:
        reject(err);

      });

    });

    this.G.L.exit("DataService.connect_to_remote_user_db");
    return promise;
  }

  // Poll data initialization:

  private get_local_poll_db(pid:string) {
    if (!(pid in this.local_poll_dbs)) {
      this.local_poll_dbs[pid] = new PouchDB('local_poll_'+pid, {auto_compaction: true});
      this.G.L.info("DataService.get_local_poll_db new poll db", pid, this.local_poll_dbs[pid]);
      this.need_poll_db_replication[pid] = true;
    } 
    return this.local_poll_dbs[pid];
  }

  /** What a poll needs on the Matrix backend when it first shows up in the
   *  user cache: to be listed, to have a cache to be filled into, and to
   *  have its lifecycle running, so that one whose deadline passed while
   *  the app was closed is ended rather than left "running" for ever
   *  (the same thing init_poll_data does for restored caches, #327). */
  private ensure_matrix_poll_known(pid: string) {
    this.G.L.entry("DataService.ensure_matrix_poll_known", pid);
    this._pids.add(pid);
    this.ensure_poll_cache(pid);
    const poll = this.G.P.polls[pid] || new Poll(this.G, pid, false);
    for (const oid of this._pid_oids[pid] || []) {
      if (!(oid in poll.options)) { new Option(this.G, poll, oid); }
    }
    poll.start_lifecycle();
    this.G.L.exit("DataService.ensure_matrix_poll_known", pid);
  }

  private ensure_local_poll_data(pid:string) {
    // start fetching poll data from local poll db:
    this.G.L.entry("DataService.ensure_local_poll_data", pid);
    if (this.shutting_down || this.uninitialized_pids.has(pid)) {
      // a local bootstrap for this pid is already in flight and shares this
      // poll's bootstrap gate. Don't launch a second info()/allDocs() load
      // whose stale snapshot could overwrite newer cache values (#292):
      this.G.L.trace("DataService.ensure_local_poll_data bootstrap already in progress", pid);
      this.G.L.exit("DataService.ensure_local_poll_data", pid);
      return;
    }
    this._ready = false;
    this.uninitialized_pids.add(pid);
    this.ensure_poll_cache(pid);
    const lpdb = this.get_local_poll_db(pid);

    const restored_cache = ("state" in this.poll_caches[pid]);
    if (restored_cache) {
      this.G.L.trace("DataService.ensure_local_poll_data reconciling restored cache with local db", pid);
      // poll cache was restored from storage. A replication may have updated
      // local PouchDB after that cache was last persisted, so still reconcile
      // the cache from local PouchDB below before resolving this poll's
      // bootstrap gate (#292):
      this._pids.add(pid);
    }
    // Get this poll's data from local PouchDB.
    // Register a bootstrap promise so that syncing/replication of this poll
    // is deferred until the cache bootstrap has completed (#292):
    this.register_poll_db_bootstrap(pid, true);
    let restored_poll: Poll;
    if (restored_cache) {
      // Derived rating/delegation updates need a Poll, but its closing timers
      // must not run against the stale restored snapshot.
      restored_poll = this.G.P.polls[pid] || new Poll(this.G, pid, false);
      for (const oid of this._pid_oids[pid] || []) {
        new Option(this.G, restored_poll, oid);
      }
    }

    const bootstrap = async (): Promise<void> => {
      for (let attempt = 1; !this.shutting_down; attempt++) {
        try {
          const info = await lpdb.info();
          const result = await lpdb.allDocs({include_docs: true});
          // Read the replay before mutating the cache. A failed read must not
          // expose a partially reconciled snapshot.
          const changes = await lpdb.changes({since: info.update_seq, include_docs: true});
          if (this.shutting_down) { return; }
          this.local_poll_docs2cache(pid, result, restored_cache || attempt > 1);
          this.apply_poll_bootstrap_changes(pid, changes);
          if (restored_poll && this.G.P.polls[pid] === restored_poll) {
            restored_poll.tally_all();
            restored_poll.start_lifecycle();
          }
          this.mark_poll_db_bootstrapped_now(pid);
          this.scan_poll_db_for_conflicts(pid);
          this.uninitialized_pids.delete(pid);
          if (this.uninitialized_pids.size == 0) {
            this.local_docs2cache_finished();
          }
          return;
        } catch (err) {
          this.G.L.error("DataService.ensure_local_poll_data could not fetch all docs", pid, err);
          // All attempts share ONE pending gate. Exhaustion fails closed:
          // leave the poll uninitialized until a fresh startup can reconcile it.
          if (this.shutting_down || attempt >= change_retry_max_attempts) {
            this.invalidate_persisted_cache();
            this.fail_poll_db_bootstrap(pid, err);
            return;
          }
          await new Promise<void>(resolve => window.setTimeout(resolve, environment.db_put_retry_delay_ms));
        }
      }
    };
    bootstrap();
    this.G.L.exit("DataService.ensure_local_poll_data", pid);
  }

  // Bootstrap gating helpers (issue #292):

  private register_poll_db_bootstrap(pid: string, recreate_if_stale: boolean = false) {
    if (
      !(pid in this.poll_db_bootstrapped) ||
      (
        recreate_if_stale &&
        !this.mark_poll_db_bootstrapped[pid] &&
        !this.reject_poll_db_bootstrapped[pid]
      )
    ) {
      this.poll_db_bootstrapped[pid] = new Promise<void>((resolve, reject) => {
        this.mark_poll_db_bootstrapped[pid] = resolve;
        this.reject_poll_db_bootstrapped[pid] = reject;
      });
      // A terminal bootstrap may fail before any sync caller awaits its gate.
      this.poll_db_bootstrapped[pid].catch(() => {});
    }
  }

  private mark_poll_db_bootstrapped_now(pid: string) {
    this.register_poll_db_bootstrap(pid);
    if (this.mark_poll_db_bootstrapped[pid]) {
      this.mark_poll_db_bootstrapped[pid]();
    }
    delete this.mark_poll_db_bootstrapped[pid];
    delete this.reject_poll_db_bootstrapped[pid];
  }

  private fail_poll_db_bootstrap(pid: string, err: any) {
    this.register_poll_db_bootstrap(pid);
    if (this.reject_poll_db_bootstrapped[pid]) {
      this.reject_poll_db_bootstrapped[pid](err);
    }
    delete this.mark_poll_db_bootstrapped[pid];
    delete this.reject_poll_db_bootstrapped[pid];
  }

  private wait_for_poll_db_bootstrap(pid: string): Promise<void> {
    // polls whose cache never needed a local db bootstrap (e.g. freshly joined
    // or newly published ones) may sync right away:
    return this.poll_db_bootstrapped[pid] || Promise.resolve();
  }

  private local_poll_docs2cache(pid: string, result, restored_cache: boolean = false) {
    this.G.L.entry("DataService.local_poll_docs2cache", pid);
    let local_changes = false;
    if (restored_cache) {
      // the cache was restored from storage; documents deleted from the local
      // db since the cache was last persisted are not returned by allDocs(),
      // so explicitly apply such deletions and prune the derived option set
      // before this poll's bootstrap gate is resolved (#292):
      const snapshot_ids = new Set<string>();
      for (const row of result.rows) {
        snapshot_ids.add(row.id);
      }
      const cache = this.ensure_poll_cache(pid);
      for (const key of Object.keys(cache)) {
        if (key == 'last_seq') {
          continue;
        }
        const _id = key.startsWith('voter.')
          ? poll_doc_id_prefix + pid + '.' + key
          : poll_doc_id_prefix + pid + '§' + key;
        if (!snapshot_ids.has(_id)) {
          this.G.L.trace("DataService.local_poll_docs2cache dropping cache entry without backing doc", pid, key);
          local_changes = this.handle_deleted_poll_doc(pid, {_id: _id}) || local_changes;
        }
      }
      for (const oid of this._pid_oids[pid] || []) {
        if (!snapshot_ids.has(poll_doc_id_prefix + pid + '§option.' + oid + '.name')) {
          this._pid_oids[pid].delete(oid);
          const poll = this.G.P.polls[pid];
          if (poll) { poll.remove_option(oid); }
          local_changes = true;
        }
      }
    }
    // decrypt and process all synced docs.
    // Apply the shared '§due' document first: allDocs() orders voter doc ids
    // ('.voter…', '.' = 0x2e) before '§due' ('§' = 0xa7), and doc2poll_cache
    // validates due-carrying rows against the cached due, so a stale restored
    // due would otherwise reject valid voter/state/option rows with no second
    // pass before the poll is tallied (#292):
    const due_id = poll_doc_id_prefix + pid + '§due';
    for (const row of result.rows) {
      if (row.id == due_id && row.doc) {
        local_changes = this.doc2poll_cache(pid, row.doc) || local_changes;
        break;
      }
    }
    for (const row of result.rows) {
      if (row.id == due_id) {
        continue;
      }
      local_changes = this.doc2poll_cache(pid, row.doc) || local_changes;
    }
    this._pids.add(pid);
    if (local_changes) {
      this.save_state();
    }
    this.G.L.exit("DataService.local_poll_docs2cache", pid);
  }

  private apply_poll_bootstrap_changes(pid: string, changes) {
    /** apply local db changes committed between the bootstrap's allDocs()
     *  snapshot and the changes() replay, before the bootstrap gate opens (#292). */
    let local_changes = false;
    for (const change of changes.results || []) {
      if (change.deleted) {
        local_changes = this.handle_deleted_poll_doc(pid, {_id: change.id}) || local_changes;
      } else if (change.doc) {
        local_changes = this.doc2poll_cache(pid, change.doc) || local_changes;
      }
    }
    if (changes.last_seq != null) {
      this.ensure_poll_cache(pid)['last_seq'] = changes.last_seq;
    }
    if (local_changes) {
      this.save_state();
    }
  }

  get_user_doc_selector(email_and_pw_hash: string): any {
    return { 
      "_id": {
        "$gte": user_doc_id_prefix + email_and_pw_hash + "§",
        "$lt": user_doc_id_prefix + email_and_pw_hash + '¨'
      }
    }
  }

  get_poll_doc_selector(pid: string): any {
    return { 
      "$or": [
        {
          "_id": {
            "$gte": poll_doc_id_prefix + pid + "§",
            "$lt": poll_doc_id_prefix + pid + '¨'
          }
        },
        {
          "_id": {
            "$gte": poll_doc_id_prefix + pid + '.voter.',
            "$lt": poll_doc_id_prefix + pid + '.voter/'
          }
        }
      ]
    }
  }

  ensure_poll_loaded(pid: string): Promise<void> {
    /** Everything of a Matrix poll that the poll list does not need: this
     *  device's own voter room, the options, the other voters' ratings and
     *  the delegations.
     *
     *  That is a join and a state read per voter room, so for a 50-voter
     *  poll it is on the order of a hundred round trips — which the app
     *  used to make for every poll the user is in before showing anything.
     *  It happens when a poll is opened instead (#327).
     *
     *  Idempotent: every later call gets the first one's promise. A failed
     *  load is not remembered, so opening the poll again retries it. */
    if (!environment.useMatrixBackend) {
      return Promise.resolve();
    }
    if (!(pid in this.poll_load_promises)) {
      this.poll_load_promises[pid] = this.load_poll_contents(pid).catch(err => {
        delete this.poll_load_promises[pid];
        throw err;
      });
    }
    return this.poll_load_promises[pid];
  }

  private async load_poll_contents(pid: string): Promise<void> {
    this.G.L.entry("DataService.load_poll_contents", pid);
    await this.matrix_ready.catch(() => { /* reported where it failed */ });
    const myVid = this.getp(pid, 'myvid');
    // this device's own voter room is where its ratings are written, so it
    // is needed before the page can be rated on, but not before the poll
    // list can be shown -- and not at all once the poll has ended: nothing
    // is written into a closed poll, and a room made for it now would be
    // announced into a poll room that refuses the announcement (403), whose
    // rejection would end this load before the options were read.
    //
    // The Poll instance is asked as well as the cache. Poll.end() sets its
    // own state to closed and change_poll_state writes that into the user
    // cache, while getp reads a non-draft poll's state out of poll_caches,
    // which still says "running" until the guard bot's closing state event
    // arrives -- and Poll.end() awaits this load in between (#366).
    const closed = this.getp(pid, 'state') == 'closed'
                   || this.G.P.polls[pid]?.state == 'closed';
    if (!closed) {
      await (await this.poll_matrix(pid)).getOrCreateMyVoterRoom(pid, myVid);
    }
    // join and read the other voters' rooms, and keep watching them:
    await (await this.poll_matrix(pid)).startVoterSync(pid);
    // options, ratings and delegations into the service's caches:
    await (await this.poll_matrix(pid)).warmupCache(pid);

    // Load options from timeline events (immutable) and register
    // their oids + data in poll_caches so Option objects get created.
    const options = await (await this.poll_matrix(pid)).getOptions(pid);
    if (!(pid in this._pid_oids)) {
      this._pid_oids[pid] = new Set();
    }
    this.ensure_poll_cache(pid);
    const poll = this.G.P.polls[pid];
    for (const [oid, opt] of options) {
      this._pid_oids[pid].add(oid);
      this.poll_caches[pid]['option.' + oid + '.oid'] = oid;
      this.poll_caches[pid]['option.' + oid + '.name'] = opt.name || '';
      this.poll_caches[pid]['option.' + oid + '.desc'] = opt.description || '';
      this.poll_caches[pid]['option.' + oid + '.url'] = opt.url || '';
      this.poll_caches[pid]['option.' + oid + '.amounts'] = opt.amounts || '';
      // and an Option object on the Poll, which is where Poll.oids reads
      // them and therefore what every tally counts over. Only the live
      // timeline handler (onOptionAdded) made them, so options read back
      // from the room's history -- every option, on a device that arrives
      // after they were added -- reached the caches and nothing else: the
      // poll was tallied over an empty option list (#366)
      if (poll && !(oid in poll.options)) { new Option(this.G, poll, oid); }
    }

    // Bridge other voters' ratings from Matrix into poll_caches and tally system.
    // getRatings() returns cached results (already fetched during warmupCache).
    // Keys are vodle vids when available, or Matrix user IDs as fallback.
    const ratings = await (await this.poll_matrix(pid)).getRatings(pid);
    let bridgedVoters = 0;
    let bridgedRatings = 0;
    for (const [vid, voterRatings] of ratings) {
      if (vid === myVid) continue; // Skip own ratings — already handled locally
      bridgedVoters++;
      for (const [oid, rating] of voterRatings) {
        // Store in poll_caches in the format expected by getv():
        //   voter.<vid>§rating.<oid> = stringified rating
        const pkey = this.get_voter_key_prefix(pid, vid) + 'rating.' + oid;
        this.poll_caches[pid][pkey] = String(rating);
        // Register in the tally system's own_ratings_map so tally_all() finds it
        this.G.P.update_own_rating(pid, vid, oid, rating, false);
        bridgedRatings++;
      }
    }
    this.G.L.info("DataService.load_poll_contents", pid,
                   "options:", options.size,
                   "voters:", bridgedVoters, "ratings:", bridgedRatings);
    this.G.L.exit("DataService.load_poll_contents", pid);
  }

  /** Join a poll this device does not know yet, from what a link carries:
   *  the server (the homeserver the poll room lives on, '_' for this
   *  device's own), the database password (CouchDB only) and the poll
   *  password. Makes the Poll, connects it, and once connected reads its
   *  state and options back, so that it is known to the app and ready for
   *  ensure_poll_loaded. The join page has done this since the beginning;
   *  a delegation link that carries the same information (#341) is the
   *  second place that needs it. */
  join_poll_from_link(pid: string, db_server_url: string, db_password: string, poll_password: string): Promise<Poll> {
    this.G.L.entry("DataService.join_poll_from_link", pid, db_server_url);
    const p = this.G.P.polls[pid] || new Poll(this.G, pid);
    p._state = "running";
    p.allow_voting = true;
    p.password = poll_password;
    p.init_myvid();
    let connected: Promise<any>;
    if (environment.useMatrixBackend) {
      // the link's first segment names the homeserver the poll room lives
      // on ('_' in links from before federation support, meaning this
      // user's own homeserver); the CouchDB db_password is meaningless here
      const origin_server = (db_server_url && db_server_url != '_') ? db_server_url : undefined;
      connected = this.connect_to_remote_poll_db(pid, true, origin_server).then(() => {
        // the state as the caches now hold it, and Option objects for the
        // oids discovered on the way (registered in _pid_oids, no more)
        p._state = (this.getp(pid, 'state') as any) || 'running';
        for (const oid of this._pid_oids[pid] || []) {
          if (!p.oids.includes(oid)) { new Option(this.G, p, oid); }
        }
      });
    } else {
      p.db_server_url = db_server_url;
      p.db_password = db_password;
      connected = this.connect_to_remote_poll_db(pid, true);
    }
    return connected.then(() => {
      p.set_timeouts();
      p.tally_all();
      this.G.L.exit("DataService.join_poll_from_link", pid, p.state);
      return p;
    });
  }

  connect_to_remote_poll_db(pid: string, wait_for_replication=false, origin_server?: string): Promise<any> {
    // called at poll initialization or when joining a poll.
    // origin_server (Matrix backend only): the server_name of the homeserver
    // the poll room lives on, as named in a magic link — needed to join a
    // poll created on another homeserver (#293).
    this.G.L.entry("DataService.connect_to_remote_poll_db", pid, wait_for_replication, origin_server);

    // Phase 12: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      // The start no longer waits for the login (#327), so this does: it is
      // the first thing here that needs a homeserver.
      //
      // Bounded, because a magic link that is being followed shows a
      // spinner until this resolves: a login that never settles left the
      // join page fetching for ever, saying nothing, which is exactly what
      // the owner saw. A ceiling turns that into an error the page can
      // show.
      (this as any).boot_log?.("this poll needs the homeserver; waiting for the login");
      const after_login = MatrixService.within(
        MatrixService.LOGIN_WAIT_TIMEOUT_MS, "the connection to the homeserver",
        () => this.matrix_ready)
        .catch(err => {
          console.warn("[vodle] the poll cannot be opened yet:", err?.message || err);
          if (wait_for_replication) {
            throw err;                  // the join page shows this
          }
          // a poll being reconnected in the background: reported where it
          // failed, and the periodic retry will come back to it
        });
      // For Matrix backend, join the poll room and create voter room instead of PouchDB replication.
      // A poll joined via a magic link is only ever joined, never created
      // here: a link to a poll that cannot be found must fail, not silently
      // produce an empty poll room on this user's homeserver.
      // The origin server named by the link is kept as user data, so that
      // another device of this account (which restores the poll membership
      // from the user room) finds the room too (#330):
      if (origin_server && this.getp(pid, 'origin_server') != origin_server) {
        this.setp(pid, 'origin_server', origin_server);
      }
      origin_server = origin_server || this.getp(pid, 'origin_server') || undefined;
      const room_promise: Promise<string> = after_login.then(() => origin_server
        ? this.poll_matrix(pid)
            .then(m => m.setPollOrigin(pid, origin_server).then(() => m.getPollRoom(pid)))
            .then(roomId => {
              if (!roomId) {
                throw new Error("poll " + pid + " not found on homeserver " + origin_server);
              }
              return roomId;
            })
        : this.poll_matrix(pid).then(m => m.getOrCreatePollRoom(pid, '')));
      return room_promise.then(async (roomId) => {
        this.G.L.info("DataService.connect_to_remote_poll_db poll room", pid, roomId);

        // Only what the poll list needs — title, state, due, type — which
        // the client's own sync usually already holds. The contents of the
        // poll (its options, the other voters' ratings, the delegations)
        // are a room per voter to join and read, and doing that for every
        // poll the user is in is what kept the start waiting; they are
        // fetched when the poll is opened (#327), see ensure_poll_loaded.
        const pollData = await (await this.poll_matrix(pid)).getAllPollData(pid);
        this.ensure_poll_cache(pid);
        for (const [key, value] of Object.entries(pollData)) {
          this.poll_caches[pid][key] = value;
        }
        this.G.L.info("DataService.connect_to_remote_poll_db loaded poll data keys:", Object.keys(pollData));

        // Persist poll state in user_cache so the app remembers this poll after refresh
        if (pollData['state']) {
          const prefix = get_poll_key_prefix(pid);
          this.setu(prefix + 'state', pollData['state']);
        }

        // Phase 14: the poll room's own listeners. The voter rooms are the
        // expensive half and wait for the poll to be opened (#327).
        this.start_poll_sync(pid);

        if (wait_for_replication) {
          // the caller is following a magic link and needs the poll itself,
          // not just its name:
          await this.ensure_poll_loaded(pid);
        }

        this.G.L.exit("DataService.connect_to_remote_poll_db (Matrix)", pid);
      });
    }

    // In order to be able to write our own voter docs, we connect as a voter dbuser (not as a poll dbuser!),
    // who has the same password as the overall user:
    const poll_db_private_username = "vodle.poll." + pid + ".voter." + this.getp(pid, 'myvid');
    var promise: Promise<any>

    if (wait_for_replication) {

      // connect, replicate one, wait for it to finish, then start continuous sync and return:

      promise = new Promise((resolve, reject) => {

        // ASYNC:
        this.get_remote_connection(
          this.getp(pid, 'db_server_url'), this.getp(pid, 'db_password'),
          poll_db_private_username, this.G.S.password
        ).then(db => { 
  
          this.remote_poll_dbs[pid] = db;
  
          // replicate once and wait for it to finish.
          // #292: deferred until this poll's local cache bootstrap has completed:
          return this.wait_for_poll_db_bootstrap(pid).then(() => {

          this.G.L.trace("DataService.connect_to_remote_poll_db about to start one-time replication", pid);
          // see here for possible performance improving options: https://pouchdb.com/api.html#replication
          this.get_local_poll_db(pid).replicate.from(this.remote_poll_dbs[pid], {
              retry: true,
              batch_size: 1000, // see https://docs.couchdb.org/en/stable/api/database/changes.html?highlight=_changes
              include_docs: true,
              selector: this.get_poll_doc_selector(pid)
          })/* on('complete') is never called, so we cannot do it this way but must check for 0 pending inside 'change' (see below):
          .on('complete', function () {

            this.G.L.trace("DataService.connect_to_remote_poll_db completed one-time replication", pid);

            this.need_poll_db_replication[pid] = false;

            // now start synchronisation asynchronously:
            this.start_poll_sync.bind(this)(pid);
    
            // RESOLVE:
            resolve(true);

          })*/
          .on('change', change => {

            this.G.L.trace("DataService.connect_to_remote_poll_db one-time replication received change", change);

            // process incoming docs:
            this.handle_poll_db_change.bind(this)(pid, change, false);

            if (change.pending == 0) {
              // replication completed

              // #292: make sure all queued changes – including deferred
              // tombstone rechecks – are applied to the cache before callers
              // may rely on it:
              this.flush_change_queue_fully().then(flushed => {

                if (!flushed) {
                  reject(new Error("DataService.connect_to_remote_poll_db could not flush queued changes"));
                  return;
                }

                this.G.L.trace("DataService.connect_to_remote_poll_db completed one-time replication", pid, this.poll_caches[pid]['state']);

                this.need_poll_db_replication[pid] = false;

                if (this.poll_caches[pid]['state'] == 'closed') {
                  this.G.L.trace("DataService.connect_to_remote_poll_db no further syncing of closed poll", pid);
                } else {
                  // now start synchronisation asynchronously:
                  this.start_poll_sync.bind(this)(pid);
                }

                // RESOLVE:
                resolve(true);

              });
            }

          }).on('error', err => {

            this.G.L.warn("DataService.connect_to_remote_poll_db failed", pid, err);
  
            // REJECT:
            reject(err);
  
          });
          this.G.L.trace("DataService.connect_to_remote_poll_db started one-time replication", pid);

          });
  
        }).catch(err => {
  
          this.G.L.warn("DataService.connect_to_remote_poll_db failed", pid, err);
  
          // REJECT:
          reject(err);
  
        });
  
      });

    } else {

      // connect, start continuous sync and return:

      promise = new Promise((resolve, reject) => {

        // ASYNC:
        this.get_remote_connection(
          this.getp(pid, 'db_server_url'), this.getp(pid, 'db_password'),
          poll_db_private_username, this.G.S.password
        ).then(db => { 
  
          this.remote_poll_dbs[pid] = db;
  
          if (this.poll_caches[pid]['state'] == 'closed') {
            this.G.L.trace("DataService.connect_to_remote_poll_db no more syncing of closed poll", pid);
          } else {
            // start synchronisation asynchronously:
            this.start_poll_sync(pid);
          }

          // RESOLVE:
          resolve(true);
  
        }).catch(err => {
  
          this.G.L.warn("DataService.connect_to_remote_poll_db failed", pid, err);
  
          // REJECT:
          reject(err);
  
        });
  
      });
  
    }

    this.G.L.exit("DataService.connect_to_remote_poll_db", pid);
    return promise;
  }

  // End of initialization:

  private local_docs2cache_finished() {
    // called whenever content of local docs has fully been copied to cache
    this.G.L.entry("DataService.local_user_docs2cache_finished");
    if (this.shutting_down || this.uninitialized_pids.size > 0) {
      // teardown (logout or destruction) began while a bootstrap was still in
      // flight; do not mark the torn-down service ready or re-enable deferred
      // restart/connect paths (#292):
      this.G.L.exit("DataService.local_user_docs2cache_finished skipped since shutting down");
      return;
    }
    this.after_changes();
    // mark as ready, dismiss loading animation, and notify page:
    this.G.L.info("DataService READY");
    this._ready = true;
    this.hide_loading();
    if (this.page && this.page.onDataReady) this.page.onDataReady();
    this.G.L.exit("DataService.local_user_docs2cache_finished");
  }

  // HOOKS FOR OTHER SERVICES:

  wait_for_user_db(): Promise<any> {
    // TODO: is there a better way for doing this?
    return this.local_synced_user_db.info();
  }

  wait_for_poll_db(pid: string): Promise<any> {
    // Phase 12: For Matrix backend, no PouchDB to wait for
    if (environment.useMatrixBackend) {
      return Promise.resolve(true);
    }
    // TODO: is there a better way for doing this?
    if (pid in this.local_poll_dbs) {
      return this.local_poll_dbs[pid].info();
    } else {
      return new Promise<any>((resolve, reject) => {resolve(true)});
    }
  }

  change_poll_state(p: Poll, new_state: string) {
    this._pids.add(p.pid);

    // called by PollService when changing state
    const pid = p.pid, prefix = get_poll_key_prefix(pid);
    this.G.L.entry("DataService.change_poll_state", pid, new_state);
    const old_state = this.user_cache[prefix + 'state'];

    // Phase 12: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (old_state == 'draft') {
        this.G.L.debug("DataService.change_poll_state (Matrix) old state was draft, creating poll room and moving data", pid, new_state);

        // Create Matrix poll room and move data from user cache to Matrix
        const title = this.getp(pid, 'title');
        this._matrixStateChangePromises[pid] = this.poll_matrix(pid)
          .then(m => m.createPollRoom(pid, title)).then(async () => {
          // Set deadline (if present)
          if (p.due) {
            await (await this.poll_matrix(pid)).setPollDeadline(pid, p.due.toISOString());
          }

          // Move data from user cache to Matrix poll room.
          // Option keys are collected separately and sent as immutable
          // timeline events via addOption() (not as state events).
          const optionData: Record<string, Record<string, string>> = {};
          const allPrefixedKeys = Object.keys(this.user_cache).filter(k => k.startsWith(prefix));
          console.error("OPTION_DEBUG change_poll_state: prefix='" + prefix + "' allPrefixedKeys=" + JSON.stringify(allPrefixedKeys));
          const optionKeys = allPrefixedKeys.filter(k => {
            const key = k.substring(prefix.length);
            const pos2 = (key+'.').indexOf('.');
            const subkey2 = (key+'.').slice(0, pos2);
            return subkey2 === 'option';
          });
          console.error("OPTION_DEBUG change_poll_state: optionKeys=" + JSON.stringify(optionKeys));
          for (const [ukey, value] of Object.entries(this.user_cache)) {
            if (ukey.startsWith(prefix)) {
              const key = ukey.substring(prefix.length),
                    pos = (key+'.').indexOf('.'),
                    subkey = (key+'.').slice(0, pos);
              this.G.L.warn("change_poll_state: processing key", key, "subkey", subkey, "inUserDb", poll_keystarts_in_user_db.includes(subkey));
              console.error("OPTION_DEBUG processing key='" + key + "' subkey='" + subkey + "' inUserDb=" + poll_keystarts_in_user_db.includes(subkey));
              if ((key != 'state') && (key != 'due') && !poll_keystarts_in_user_db.includes(subkey)) {
                if (subkey === 'option') {
                  // Collect option fields: key = "option.<oid>.<field>"
                  const rest = key.slice('option.'.length);  // "<oid>.<field>"
                  const dotPos = rest.indexOf('.');
                  if (dotPos > 0) {
                    const oid = rest.slice(0, dotPos);
                    const field = rest.slice(dotPos + 1);
                    if (!optionData[oid]) optionData[oid] = {};
                    optionData[oid][field] = value as string;
                  }
                  // DON'T delu() here — option data must stay in user_cache
                  // as fallback until addOption() has confirmed the timeline
                  // events exist. The delu() is called below after addOption()
                  // succeeds for each option.
                } else {
                  await (await this.poll_matrix(pid)).setPollData(pid, key, value as string);
                  // Also store in poll_caches so getp() finds it immediately
                  this.ensure_poll_cache(pid);
                  this.poll_caches[pid][key] = value as string;
                  this.delu(ukey);
                }
              }
            }
          }

          // Send options as immutable timeline events
          console.error("OPTION_DEBUG change_poll_state: collected optionData=" + JSON.stringify(optionData));
          this.ensure_poll_cache(pid);
          for (const [oid, fields] of Object.entries(optionData)) {
            console.error("OPTION_DEBUG change_poll_state: sending option oid=" + oid + " fields=" + JSON.stringify(fields));
            if (fields['name']) {
              await (await this.poll_matrix(pid)).addOption(pid, oid, {
                name: fields['name'],
                description: fields['desc'] || '',
                url: fields['url'] || '',
                ...(fields['amounts'] ? {amounts: fields['amounts']} : {})
              });
              this.G.L.warn("change_poll_state: option sent successfully", oid);
              // Populate poll_caches so getp() finds option data immediately
              // (before ensureOptionCache has a chance to fetch from the timeline)
              this.poll_caches[pid]['option.' + oid + '.oid'] = oid;
              this.poll_caches[pid]['option.' + oid + '.name'] = fields['name'];
              this.poll_caches[pid]['option.' + oid + '.desc'] = fields['desc'] || '';
              this.poll_caches[pid]['option.' + oid + '.url'] = fields['url'] || '';
              this.poll_caches[pid]['option.' + oid + '.amounts'] = fields['amounts'] || '';
              if (!(pid in this._pid_oids)) this._pid_oids[pid] = new Set();
              this._pid_oids[pid].add(oid);
              // NOW safe to delete option keys from user_cache since the
              // option is confirmed in the Matrix timeline:
              const optKeyPrefix = prefix + 'option.' + oid + '.';
              for (const ukey of Object.keys(this.user_cache)) {
                if (ukey.startsWith(optKeyPrefix)) {
                  this.delu(ukey);
                }
              }
            }
          }

          // Set poll state to 'running' BEFORE locking metadata,
          // because lockPollMetadata raises m.room.vodle.poll.state to
          // power 100 so the creator (50) can no longer send it.
          if (new_state != 'draft' && new_state != 'closing') {
            await (await this.poll_matrix(pid)).changePollState(pid, new_state);
          }
          
          // Start real-time sync so the creator receives rating updates
          // from other voters via SDK event listeners.
          this.start_poll_sync(pid);
        }).catch(err => {
          this.G.L.error("DataService.change_poll_state failed to create Matrix poll room and move draft data during draft→running transition", pid, err);
        });
      }

      this.setu(prefix + 'state', new_state);
      this.G.L.exit("DataService.change_poll_state");
      return;
    }

    if (old_state == 'draft') {

      this.G.L.debug("DataService.change_poll_state old state was draft, so moving data from user db to poll db and then starting sync", pid, new_state);

      // initialize the poll cache's due synchronously: the draft→running
      // migration confirms the shared state doc with that cached due, so the
      // due must not race the deferred migration callback below, which would
      // otherwise be the first to set it (#292):
      const due_iso = p.due ? p.due.toISOString() : this.user_cache[prefix + 'due'];
      if (due_iso) {
        this.ensure_poll_cache(pid)['due'] = due_iso;
      }

      // now wait for poll db before continuing:
      this.wait_for_poll_db(pid).finally(() => {
        // move data (incl. the due date) from local user db to poll db
        // transactionally; user db copies are only deleted after the
        // corresponding poll db write has been confirmed, and leftover copies
        // make the migration retryable via after_changes(), so a terminal
        // write failure cannot leave the poll running without e.g. its due or
        // shared state document (#292):
        this.move_draft_data_to_poll_db(pid, due_iso, new_state);
        // finally, start synching with remote poll db:
        // check if db credentials are set:
        if (this.poll_has_db_credentials(pid)) {
          this.G.L.trace("DataService.change_poll_state found remote poll db credentials");
          // connect to remote and start sync:
          this.connect_to_remote_poll_db(pid).catch(err => {
            this.G.L.warn("DataService.change_poll_state couldn't start remote poll db syncing for", pid, err);
            // TODO
          });
        } else {
          this.G.L.warn("DataService.change_poll_state couldn't find remote poll db credential for", pid);
          // TODO
        }
      });
    }

    if (new_state != 'draft' && new_state != 'closing') {
      // only "running" and "closed" are stored in poll db. During a
      // draft→running move, do not fire-and-forget that shared state write:
      // move_draft_data_to_poll_db confirms it before the migration may finish
      // deleting the remaining user-db copies (#292). Still update the
      // poll-cache synchronously so local reads see the new state immediately.
      if (old_state == 'draft') {
        this.ensure_poll_cache(pid)['state'] = new_state;
      } else if (environment.useMatrixBackend && new_state == 'closed') {
        // the shared "closed" fact is the guard bot's to write on the Matrix
        // backend (#325): the room's state is locked for participants once
        // the poll runs, so this is only recorded locally
        this.ensure_poll_cache(pid)['state'] = new_state;
      } else {
        this._setp_in_polldb(pid, 'state', new_state);
      }
    }
    this.setu(prefix + 'state', new_state);
    this.G.L.exit("DataService.change_poll_state");
  }

  /**
   * Wait for the guard bot to close the poll on the server (#325). It closes
   * every voter room first and then writes the poll room's closed state, so
   * from that event on the ratings cannot change and every client reads
   * the same ones. Resolves with the closing event's id — or, without a
   * guard bot, with closed=false after closing.matrix_closure_timeout_ms:
   * the poll is then closed by convention, as it was before.
   */
  async wait_for_matrix_poll_closure(pid: string, is_current: () => boolean = () => true):
      Promise<{closed: boolean; event_id: string | null}> {
    const started = Date.now();
    // the same failure every second for two minutes is one fact, not a
    // hundred and twenty of them:
    let reported = false;
    for (;;) {
      if (this.shutting_down || !is_current()) {
        throw new Error("poll finalization cancelled");
      }
      try {
        const closure = await (await this.poll_matrix(pid)).getPollClosure(pid);
        if (closure.closed) {
          this.G.L.info("DataService.wait_for_matrix_poll_closure: the poll is closed on the server", pid, closure.event_id);
          return closure;
        }
      } catch (err) {
        if (MatrixService.isInvalidToken(err)) {
          // this poll account's token is not accepted any more — after an
          // account switch, or a session the homeserver has forgotten.
          // Asking again with it is what put a hundred and forty 401s in
          // the console while the results were determined; signing in
          // again is the only thing that can help (#327).
          this.G.L.warn("DataService.wait_for_matrix_poll_closure: the session is no longer valid, signing in again", pid);
          this.forget_poll_matrix(pid);
        } else if (!reported) {
          reported = true;
          this.G.L.warn("DataService.wait_for_matrix_poll_closure could not read the poll room's state", pid, err);
        }
      }
      if (Date.now() - started >= environment.closing.matrix_closure_timeout_ms) {
        this.G.L.warn("DataService.wait_for_matrix_poll_closure: no guard bot closed the poll within the timeout, closing by convention", pid);
        return {closed: false, event_id: null};
      }
      await new Promise(resolve => window.setTimeout(resolve, environment.closing.matrix_closure_poll_ms));
    }
  }

  /**
   * The final ratings as the server has them, bridged into the caches the
   * tally works from (#325): what the live handlers did while the poll ran,
   * once more and completely, before the final tally.
   */
  async reconcile_matrix_ratings(pid: string): Promise<void> {
    const ratings = await (await this.poll_matrix(pid)).refreshRatings(pid);
    const cache = this.ensure_poll_cache(pid);
    let count = 0;
    for (const [vid, per_option] of ratings) {
      for (const [oid, rating] of per_option) {
        cache[this.get_voter_key_prefix(pid, vid) + 'rating.' + oid] = String(rating);
        this.G.P.update_own_rating(pid, vid, oid, rating, false);
        count++;
      }
    }
    this.G.L.info("DataService.reconcile_matrix_ratings", pid, ratings.size, "voters,", count, "ratings");
  }

  private async confirm_draft_migration_marker(pid: string, state: string): Promise<void> {
    const hash = this.get_email_and_pw_hash(), password = this.user_cache['password'];
    if (!hash || !password || !['running', 'closed', 'closing'].includes(state)) {
      throw new Error("DataService.confirm_draft_migration_marker missing credentials or non-draft state");
    }
    const _id = user_doc_id_prefix + hash + '§' + get_poll_key_prefix(pid) + 'state';
    for (let attempt = 1; ; attempt++) {
      try {
        const doc = await this.local_synced_user_db.get(_id).catch(err => {
          if (err.status == 404) { return {_id}; }
          throw err;
        });
        // A newer non-draft marker is sufficient for restart to open the poll
        // DB. Never regress it while retrying an interrupted migration.
        if (doc.value && ['running', 'closed', 'closing'].includes(decrypt(doc.value, password))) {
          return;
        }
        doc.value = encrypt(state, password);
        await this.local_synced_user_db.put(doc);
        return;
      } catch (err) {
        if (attempt >= confirmed_put_max_attempts) { throw err; }
        await new Promise(resolve => window.setTimeout(resolve, environment.db_put_retry_delay_ms));
      }
    }
  }

  private async move_draft_data_to_poll_db(pid: string, due_iso?: string, state_value?: string): Promise<void> {
    /** Transactionally move a poll's draft data (incl. its due date) from the
     *  user db to the poll db. Each user db copy is only deleted after the
     *  corresponding poll db write has been confirmed, so a failed or
     *  interrupted write cannot lose draft data. Leftover user db copies mark
     *  the migration as still pending (see draft_migration_pending()), and
     *  after_changes() retries it on later change batches and after restarts,
     *  so a terminal write failure cannot permanently leave the poll running
     *  without e.g. its due or shared state document (#292): */
    if (this.draft_migration_in_flight[pid]) {
      return Promise.resolve();
    }
    const prefix = get_poll_key_prefix(pid);
    if (!this.user_cache[prefix + 'password']) {
      // store_poll_data_confirmed would reject every write anyway:
      this.G.L.warn("DataService.move_draft_data_to_poll_db missing poll password, keeping user db copies", pid);
      return Promise.resolve();
    }
    this.draft_migration_in_flight[pid] = true;
    try {
      // setu() only updates the cache synchronously. No fallback may be
      // deleted until restart can discover the poll DB from the user DB.
      await this.confirm_draft_migration_marker(pid, state_value || this.user_cache[prefix + 'state']);
    } catch (err) {
      delete this.draft_migration_in_flight[pid];
      this.G.L.error("DataService.move_draft_data_to_poll_db couldn't confirm migration marker, keeping user db copies", pid, err);
      return;
    }
    // first store due in the poll db and wait until that write has been
    // confirmed and poll_caches[pid].due updated before launching the other
    // writes: a migration retry may find a different, authoritative due in
    // the poll db (store_poll_data_confirmed then updates the cache to that
    // value), and only writes started after that update stamp option/rating
    // docs with the correct due; otherwise doc2poll_cache would reject those
    // docs once the authoritative due is loaded (#292):
    const due = due_iso || this.user_cache[prefix + 'due'],
          state = state_value || this.user_cache[prefix + 'state'];
    const confirm_shared_state = (): Promise<boolean> => {
      if (!state || state == 'draft' || state == 'closing') {
        return Promise.resolve(true);
      }
      this.ensure_poll_cache(pid)['state'] = state;
      return this.store_poll_data_confirmed(pid, 'state', state, true, false)
        .then(() => true)
        .catch(err => {
          this.G.L.error("DataService.move_draft_data_to_poll_db couldn't store shared poll state in poll db yet, will retry", pid, state, err);
          return false;
        });
    };
    let shared_docs_confirmed: Promise<boolean>;
    if (due) {
      this.ensure_poll_cache(pid)['due'] = due;
      shared_docs_confirmed = this.store_poll_data_confirmed(pid, 'due', due, false, false)
        .then(() => {
          return confirm_shared_state().then(state_ok => {
            if (!state_ok) {
              return false;
            }
            // only once both shared docs are confirmed is it safe to remove
            // the user-db due copy; until then it marks the migration as
            // pending so that a missing state doc is also retried (#292).
            // The deletion itself is awaited and durable (only a 404 counts
            // as nonexistent), so a transient failure keeps the migration
            // pending instead of leaving a stale source doc behind:
            return this.delu_confirmed(prefix + 'due')
              .catch(err => {
                this.G.L.warn("DataService.move_draft_data_to_poll_db couldn't confirm deletion of migrated due copy yet, will retry", pid, err);
              })
              .then(() => true);
          });
        })
        .catch(err => {
          this.G.L.error("DataService.move_draft_data_to_poll_db couldn't store due date in poll db yet, will retry", pid, err);
          return false;
        });
    } else {
      shared_docs_confirmed = confirm_shared_state();
    }
    return shared_docs_confirmed.then(shared_docs_ok => {
      if (!shared_docs_ok) {
        // without a confirmed authoritative due, the remaining writes might
        // stamp option/rating docs with a stale draft due that doc2poll_cache
        // would later reject, and without a confirmed shared state doc other
        // replicas might never observe that the poll is running; keep user-db
        // copies so the whole migration is retried on a later change batch or
        // after restart:
        this.G.L.warn("DataService.move_draft_data_to_poll_db could not move all draft data yet, will retry on a later change batch or after restart", pid);
        return;
      }
      return this.move_remaining_draft_data_to_poll_db(pid);
    }).finally(() => {
      delete this.draft_migration_in_flight[pid];
    });
  }

  private move_remaining_draft_data_to_poll_db(pid: string): Promise<void> {
    /** move the non-due draft data items from the user db to the poll db;
     *  must only be called after the poll's authoritative due has been
     *  confirmed and cached (see move_draft_data_to_poll_db, #292): */
    const prefix = get_poll_key_prefix(pid);
    const session = this.voter_mutation_session;
    const session_is_current = () => !this.shutting_down && session === this.voter_mutation_session;
    const move_promises: Promise<void>[] = [];
    let moved_data = false;
    for (const [ukey, value] of Object.entries(this.user_cache)) {
      if (ukey.startsWith(prefix)) {
        // used db entry belongs to this poll.
        const key = ukey.substring(prefix.length),
              pos = (key+'.').indexOf('.'),
              subkey = (key+'.').slice(0, pos);
        if ((key != 'state') && (key != 'due') && !poll_keystarts_in_user_db.includes(subkey)) {
          // draft voter entries are keyed like "voter.<vid>§rating.<oid>",
          // so for them the due requirement is decided by the segment
          // after "§" (as doc2poll_cache does), not by the poll-level
          // subkey "voter" (#292):
          let add_due: boolean;
          if (subkey == 'voter' && key.includes('§')) {
            const vsubkey = key.slice(key.indexOf('§') + 1),
                  vsubkeystart = vsubkey.slice(0, (vsubkey+'.').indexOf('.'));
            add_due = voter_subkeystarts_requiring_due.includes(vsubkeystart);
          } else {
            add_due = poll_keystarts_requiring_due.includes(subkey);
          }
          let source_rev: string;
          let still_wanted = () => session_is_current() && (ukey in this.user_cache);
          const move = async (is_current = session_is_current) => {
            still_wanted = () => is_current() && (ukey in this.user_cache);
            this.assert_voter_mutation_current(still_wanted);
            let source_value = value as string;
            if (subkey === 'voter') {
              const source = await this.read_voter_migration_source(ukey);
              this.assert_voter_mutation_current(still_wanted);
              if (!source) {
                // A failed draft write may have left an optimistic rating in
                // derived maps even though neither DB contains that rating.
                // Reconcile before clearing the source/failure barrier.
                const destination = await this.get_existing_doc(
                  this.get_local_poll_db(pid), poll_doc_id_prefix + pid + '.' + key);
                this.assert_voter_mutation_current(still_wanted);
                if (destination) {
                  this.cache_confirmed_voter_doc(pid, key, destination);
                } else {
                  this.handle_deleted_poll_doc(pid, {_id: poll_doc_id_prefix + pid + '.' + key});
                }
                this.assert_voter_mutation_current(still_wanted);
                delete this.user_cache[ukey];
                moved_data = true;
                return;
              }
              source_rev = source.rev;
              source_value = source.value;
            }
            try {
              await this.store_poll_data_confirmed(pid, key, source_value, add_due, false, still_wanted);
            } catch (err: any) {
              if (!err?.vodle_publication_expired) { throw err; }
              this.assert_voter_mutation_current(still_wanted);
              await this.delu_confirmed(ukey, source_rev, still_wanted);
              moved_data = true;
              return;
            }
            this.assert_voter_mutation_current(still_wanted);
            moved_data = true;
            await this.delu_confirmed(ukey, source_rev, still_wanted);
          };
          const moving = subkey === 'voter'
            ? this.queue_voter_mutation(ukey, move, false)
            : move();
          move_promises.push(
            moving.catch(err => {
              if (err && err.vodle_move_aborted) {
                this.G.L.trace("DataService.move_draft_data_to_poll_db move superseded; the current mutation owns cleanup", pid, ukey);
                return;
              }
              this.G.L.error("DataService.move_draft_data_to_poll_db couldn't move yet, keeping user db copy for retry", pid, ukey, key, err);
            })
          );
        }
      }
    }
    return Promise.all(move_promises).then(() => {
      if (moved_data && session_is_current()) {
        // doc2poll_cache defers tallying to the batch completion hook.
        this.after_changes(true);
        if (this.page && this.page.onDataChange) {
          this.page.onDataChange();
        }
      }
      if (this.draft_migration_pending(pid)) {
        this.G.L.warn("DataService.move_draft_data_to_poll_db could not move all draft data yet, will retry on a later change batch or after restart", pid);
      } else {
        this.G.L.info("DataService.move_draft_data_to_poll_db finished moving draft data to poll db", pid, move_promises.length);
      }
    });
  }

  private async read_voter_migration_source(ukey: string): Promise<{rev: string, value: string} | null> {
    const hash = this.get_email_and_pw_hash(), password = this.user_cache['password'];
    if (!hash || !password) { throw new Error("Cannot read migration source without user credentials"); }
    const doc = await this.get_existing_doc(this.local_synced_user_db, user_doc_id_prefix + hash + '§' + ukey);
    if (!doc) { return null; }
    const value = decrypt(doc.value, password);
    if (!doc._rev || value === null) { throw new Error("Invalid voter migration source " + ukey); }
    return {rev: doc._rev, value};
  }

  private draft_migration_pending(pid: string): boolean {
    /** whether user db copies of poll-db-authoritative data (incl. 'due') are
     *  left over from an incomplete draft→running data migration (#292): */
    const prefix = get_poll_key_prefix(pid);
    for (const ukey of Object.keys(this.user_cache)) {
      if (ukey.startsWith(prefix)) {
        const key = ukey.substring(prefix.length),
              subkey = (key+'.').slice(0, (key+'.').indexOf('.'));
        if ((key != 'state') && !poll_keystarts_in_user_db.includes(subkey)) {
          // covers 'due' as well, whose user db copy is only deleted after
          // its poll db write has been confirmed:
          return true;
        }
      }
    }
    return false;
  }

  async prepare_poll_finalization(pid: string): Promise<number> {
    await this.wait_for_poll_db_bootstrap(pid);
    // Failed optimistic writes are reconciled with the server below, not
    // silently accepted merely because their local promise has settled.
    await this.await_poll_mutations(pid).catch(() => {});
    if (this.shutting_down || this.uninitialized_pids.has(pid) || this.persisted_cache_invalid) {
      throw make_consistency_failure_error("Poll bootstrap is incomplete for " + pid);
    }
    if (this.draft_migration_pending(pid)) {
      await this.move_draft_data_to_poll_db(pid);
    }
    const generation = this.poll_mutation_generation(pid);
    await this.confirm_voter_publications(pid, generation);
    this.assert_poll_consistent(pid);
    return generation;
  }

  private async confirm_voter_publications(pid: string, generation: number): Promise<void> {
    const assert_current = () => {
      if (this.shutting_down || generation !== this.poll_mutation_generation(pid)) {
        throw make_consistency_failure_error("Poll changed during publication confirmation: " + pid);
      }
    };
    assert_current();
    const db = this.get_local_poll_db(pid), remote = this.remote_poll_dbs[pid];
    const vid = this.getp(pid, 'myvid');
    if (!remote || !vid || this.poll_db_sync_handlers?.[pid]) {
      throw make_consistency_failure_error("Cannot verify voter publication without a remote connection and voter ID");
    }
    const simulated = this.getp(pid, 'is_test') === 'true';
    const prefix = poll_doc_id_prefix + pid + '.voter.' + (simulated ? '' : vid + '§');
    const [changes, remote_rows] = await Promise.all([
      db.changes({since: 0, include_docs: true}),
      remote.allDocs({startkey: prefix, endkey: prefix.slice(0, -1) + (simulated ? '/' : '¨'), inclusive_end: false}),
    ]);
    assert_current();
    const ids = new Set<string>([
      ...changes.results.map(change => change.id),
      ...remote_rows.rows.map(row => row.id),
      ...Object.keys(this.ensure_poll_cache(pid)).map(key => poll_doc_id_prefix + pid + '.' + key),
      ...Object.keys(this.voter_mutation_failures).filter(key => key.startsWith(get_poll_key_prefix(pid)))
        .map(key => poll_doc_id_prefix + pid + '.' + key.slice(get_poll_key_prefix(pid).length)),
    ].filter(id => id.startsWith(prefix)
      && voter_subkeystarts_requiring_due.includes(id.slice(id.indexOf('§') + 1).split('.')[0])));
    if (!ids.size) { return; }
    // Include tombstones: a pull alone need not redeliver a remote revision
    // which was already known before an unreplicated local deletion.
    try { await db.replicate.to(remote, {doc_ids: [...ids], retry: false}); }
    catch (err) { this.G.L.trace("DataService final publication push needs reconciliation", pid, err); }
    for (const id of ids) {
      assert_current();
      const key = id.slice((poll_doc_id_prefix + pid + '.').length);
      const local = await this.get_existing_doc(db, id);
      assert_current();
      // Reconcile with the remote winner. Without a remote document, absence
      // is only acceptable when there is no local document either; an
      // unreadable local revision must never be silently dropped.
      const reconcile_from_remote = async (absence_is_acceptable: boolean) => {
        const authoritative = await this.get_existing_doc(remote, id);
        assert_current();
        if (authoritative) {
          await this.restore_expired_poll_payload(pid, key, authoritative, assert_current);
          this.cache_confirmed_voter_doc(pid, key, authoritative);
        } else if (absence_is_acceptable) {
          this.handle_deleted_poll_doc(pid, {_id: id});
        } else {
          throw make_consistency_failure_error("Cannot verify unreadable local publication: " + id);
        }
      };
      // store_poll_data_confirmed normalizes a falsy value to '', so feeding it
      // an undecryptable revision would encrypt an empty payload and push it
      // over a possibly valid remote value. A corrupt local revision is
      // therefore reconciled from the remote winner instead, and finalization
      // is deferred when no authoritative remote value exists (#292).
      const decrypted = local?.value ? decrypt(local.value, this.getp(pid, 'password')) : null;
      if (local && decrypted !== null) {
        try {
          await this.store_poll_data_confirmed(pid, key, decrypted,
            true, false, () => !this.shutting_down && generation === this.poll_mutation_generation(pid));
        } catch (err: any) {
          if (!err?.vodle_publication_expired) { throw err; }
        }
      } else {
        await reconcile_from_remote(!local);
      }
      assert_current();
      delete this.voter_mutation_failures[get_poll_key_prefix(pid) + key];
    }
  }

  private async restore_expired_poll_payload(pid: string, key: string, authoritative, assert_current: () => void) {
    // Repair only permanently rejected deadline-stamped local revisions.
    // Their canonical payload persists across restart but cannot overwrite
    // newer server content: outbound sync is stopped and the due is preserved.
    assert_current();
    const due = authoritative.due, id = poll_doc_id_prefix + pid + '.' + key;
    if (!key.startsWith('voter.') || authoritative._id !== id || !authoritative._rev
        || !due || due !== this.ensure_poll_cache(pid)['due']
        || !Number.isFinite(new Date(due).getTime()) || Date.now() <= new Date(due).getTime() + 1000
        || this.poll_db_sync_handlers?.[pid]
        || decrypt(authoritative.value, this.getp(pid, 'password')) === null) {
      throw make_consistency_failure_error("Cannot safely reconcile rejected local publication: " + id);
    }
    const db = this.get_local_poll_db(pid);
    let local = await this.get_existing_doc(db, id);
    assert_current();
    if (local?.value === authoritative.value && local?.due === due) { return; }
    let revision = local?._rev;
    if (!local) {
      const rows = await db.allDocs({keys: [id]});
      assert_current();
      revision = rows.rows[0]?.value?.rev;
    }
    await db.put({_id: id, ...(revision ? {_rev: revision} : {}), value: authoritative.value, due});
    assert_current();
    local = await db.get(id);
    if (local.value !== authoritative.value || local.due !== due) {
      throw make_consistency_failure_error("Local publication repair did not persist: " + id);
    }
  }

  private cache_confirmed_voter_doc(pid: string, key: string, doc) {
    const cache = this.ensure_poll_cache(pid), had_key = key in cache, previous = cache[key];
    const requires_due = voter_subkeystarts_requiring_due.includes(key.slice(key.indexOf('§') + 1).split('.')[0]);
    if (doc._id !== poll_doc_id_prefix + pid + '.' + key || !doc.value
        || decrypt(doc.value, this.getp(pid, 'password')) === null
        || (requires_due && (!doc.due || doc.due !== cache['due']))) {
      throw make_consistency_failure_error("Cannot apply unverified voter data: " + key);
    }
    delete cache[key];
    try { this.doc2poll_cache(pid, doc); }
    catch (err) {
      if (had_key) { cache[key] = previous; }
      throw err;
    }
  }

  assert_poll_consistent(pid: string) {
    // Draining replication alone does not drain local publication. A retained
    // source can still represent an unaccepted local rating after a restart.
    if (this.shutting_down || this.uninitialized_pids.has(pid) || this.persisted_cache_invalid
        || this.draft_migration_in_flight[pid] || this.draft_migration_pending(pid)
        || this.has_pending_poll_mutations(pid)) {
      throw make_consistency_failure_error("Poll publication is incomplete for " + pid);
    }
  }

  /** The homeserver a poll's room lives on, as a magic link names it, from
   *  what this device knows and without a round trip: the server a link
   *  named when this device joined the poll, else this device's own
   *  homeserver, where it created the poll. get_poll_origin_server is the
   *  same answer with the poll's own session asked. */
  poll_origin_server_known(pid: string): string {
    return this.getp(pid, 'origin_server') || this.matrixService.getHomeserverDomain();
  }

  get_poll_origin_server(pid: string): Promise<string> {
    /** Matrix backend: the server_name of the homeserver this poll's room
     *  lives on, which a magic link must name so that users of OTHER
     *  homeservers can join the poll (#293). */
    return this.poll_matrix(pid).then(m => m.getPollOrigin(pid));
  }

  replicate_once(pid: string): Promise<boolean> {
    this.G.L.entry("DataService.replicate_once", pid);
    
    // Matrix backend has no PouchDB replication
    if (environment.useMatrixBackend) {
      this.G.L.exit("DataService.replicate_once (Matrix noop)", pid);
      return Promise.resolve(true);
    }
    
    return new Promise<boolean>((resolve, reject) => {

      // see here for possible performance improving options: https://pouchdb.com/api.html#replication
      this.get_local_poll_db(pid).replicate.from(this.remote_poll_dbs[pid], {
//          since: this.poll_caches[pid]['last_seq'] || 0,
          // deliberately no infinite retry: this one-shot replication is used
          // as the final pull before Poll.end() tallies, and with retry an
          // unreachable remote would be retried forever instead of emitting
          // the terminal 'error' the caller needs in order to react at all.
          // That error must NOT be answered with a fallback to the local data:
          // Poll.end() catches it, defers finalization and schedules a guarded
          // retry, so results stay unavailable until the final pull and the
          // subsequent consistency checks actually succeed (#292):
          retry: false,
          batch_size: 1000, // see https://docs.couchdb.org/en/stable/api/database/changes.html?highlight=_changes
          include_docs: true,
          selector: this.get_poll_doc_selector(pid)
      }).on('complete', msg => {

        this.G.L.trace("DataService.replicate_once completed", pid, msg);

        // #292: make sure all queued changes – including deferred tombstone
        // rechecks – are applied to the cache before callers may rely on it:
        this.flush_change_queue_fully().then(flushed => {

          if (!flushed) {
            reject(make_consistency_failure_error("DataService.replicate_once could not flush queued changes"));
            return;
          }

          this.need_poll_db_replication[pid] = false;

          // RESOLVE:
          resolve(true);

        });

      }).on('change', change => {

        this.G.L.trace("DataService.replicate_once received change", change);

        // process incoming docs:
        this.handle_poll_db_change.bind(this)(pid, change, false);

        if (change.pending == 0) {
          // replication completed

          // #292: make sure all queued changes – including deferred tombstone
          // rechecks – are applied to the cache before callers may rely on it:
          this.flush_change_queue_fully().then(flushed => {

            if (!flushed) {
              reject(make_consistency_failure_error("DataService.replicate_once could not flush queued changes"));
              return;
            }

            this.G.L.trace("DataService.replicate_once completed", pid);

            this.need_poll_db_replication[pid] = false;

            // RESOLVE:
            resolve(true);

          });
        }

      }).on('error', err => {

        this.G.L.warn("DataService.replicate_once failed", pid, err);

        // REJECT:
        reject(err);

      });
      this.G.L.trace("DataService.replicate_once started one-time replication", pid);
    });
  }

  async ensure_remote_poll_closed(pid: string, is_current = () => true): Promise<void> {
    const remote = this.remote_poll_dbs[pid],
          password = this.user_cache[get_poll_key_prefix(pid) + 'password'],
          _id = poll_doc_id_prefix + pid + '§state';
    if (!remote || !password) {
      throw new Error("cannot confirm remote poll closure without connection and password");
    }
    for (let attempt = 1; ; attempt++) {
      if (this.shutting_down || !is_current()) {
        throw new Error("poll finalization cancelled");
      }
      try {
        const doc = await remote.get(_id).catch(err => {
          if (err.status == 404) { return {_id, due: this.getp(pid, 'due')}; }
          throw err;
        });
        if (this.shutting_down || !is_current()) {
          throw new Error("poll finalization cancelled");
        }
        if (decrypt(doc.value, password) == 'closed') { return; }
        if (!doc.due) { throw new Error("cannot close remote poll without due date"); }
        doc.value = encrypt('closed', password);
        await remote.put(doc);
        return;
      } catch (err: any) {
        // Another closer may have won the write; re-read instead of creating
        // another closed revision. Transport failures use Poll's backoff.
        if (err.status != 409 || attempt >= confirmed_put_max_attempts) { throw err; }
        await new Promise(resolve => window.setTimeout(resolve, environment.db_put_retry_delay_ms));
      }
    }
  }

  get_remote_poll_state_doc(pid: string): Promise<any> {
    // Matrix backend has no PouchDB remote_poll_dbs
    if (environment.useMatrixBackend) {
      return Promise.reject(new Error("no remote poll state doc in Matrix mode"));
    }
    const _id = poll_doc_id_prefix + pid + "§state";
    const remote = this.remote_poll_dbs[pid];
    if (!remote) {
      // Do NOT fall back to the local replica of the state doc: its revision
      // hash depends on randomized encryption, so two offline replicas can
      // produce different revisions even from identical state data, which
      // would make winner selection diverge across devices. Rejecting lets
      // the caller defer finalization until the shared seed from the remote
      // state doc revision is available again (#292):
      return Promise.reject(new Error("no remote poll db connection"));
    }
    return remote.get(_id).then(doc => {
      // A successful pull does not confirm that our earlier closed-state push
      // reached CouchDB. A running revision is not a stable winner seed.
      const password = this.user_cache[get_poll_key_prefix(pid) + 'password'];
      if (!password || !doc._rev || decrypt(doc.value, password) != 'closed') {
        throw new Error("remote poll state is not confirmed closed");
      }
      return doc;
    });
  }

  // HOOKS FOR PAGES:

  login_submitted(as_guest = false) {
    // called by login page when all necessary login information was submitted on the login page
    this.G.L.entry("DataService.login_submitted", as_guest);
    this.show_loading();
    if ((this.user_cache['db']||'')=='') {
      this.G.S.db = 'central';
    }
    this.G.add_spinning_reason("login");
    this.guest_login_pending = false;
    // (the setters are cleared and re-set so that they fire even for
    // unchanged values; the intermediate empty credentials are no identity.
    // When the credentials differ from the ones this device used until now
    // — a guest's (#193), an earlier account's — the first change already
    // recorded those as the origin of a data move, see
    // note_credentials_change; email_and_password_exist performs it, #330)
    // the DISPLAY language, never the account's: clearing `language` here
    // wrote an empty value into the user room of anyone whose client was
    // still signed in — a guest on their way to an account — and emptying
    // the key is how the room forgets it (#327)
    const language = this.G.S.display_language,
          email = this.G.S.email,
          password = this.G.S.password;
    (this as any).boot_log?.("re-setting the credentials");
    this.G.S.display_language = "";
    this.G.S.email = this.G.S.password = "";
    (this as any).boot_log?.("credentials cleared");
    this.G.S.display_language = language;
    (this as any).boot_log?.("language set");
    this.G.S.email = email;
    this.G.S.password = password;
    (this as any).boot_log?.("credentials set");
    this.setu('guest', as_guest ? '1' : '');
    this.email_and_password_exist();
  }

  login_as_guest() {
    /** Take part with a throw-away account whose credentials only this
     *  device knows (#193): from the login page's guest button, and on the
     *  first visit of a magic link. A later login with a real account moves
     *  the guest's data and voter rooms to it (perform_user_data_move).
     *
     *  Logged step by step, because this is where a magic link opened on a
     *  fresh device spent nine seconds with no network traffic at all in the
     *  owner's report, and the code below reads as if none of it could cost
     *  anything: the next log says which line does (#327). */
    this.G.L.entry("DataService.login_as_guest");
    (this as any).boot_log?.("creating a guest for this magic link");
    const {email, password} = DataService.guest_credentials();
    (this as any).boot_log?.("guest credentials generated");
    this.G.S.password = password;
    this.G.S.email = email;
    (this as any).boot_log?.("guest credentials recorded");
    if (environment.privacy_statement_url) {
      // nobody has consented to anything yet: the poll page asks before
      // the first rating is stored (consent_pending)
      this.setu('consent', '0');
    } else {
      this.record_consent();
    }
    (this as any).boot_log?.("consent recorded");
    // the default wap is not seeded here either: ensure_user_defaults gives
    // a guest's empty user room the deployment's default after the sync, and
    // until then SettingsService falls back to the same value anyway
    this.guest_login_in_progress = true;
    this.login_submitted(true);
  }

  static guest_credentials(): {email: string, password: string} {
    /** Random credentials for a guest account (#193): about 115 bits in the
     *  password, 50 in the address. (Until 2026-09-10 a guest was "Guest"
     *  plus a number below a million, used as password AND address, so every
     *  guest account could be enumerated.) The password satisfies the app's
     *  password pattern, and the alphabet leaves out 0/O/1/l/I, since the
     *  credentials may have to be typed on another device. */
    const alphabet = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const random_string = (length: number): string => {
      let result = '';
      while (result.length < length) {
        const bytes = new Uint8Array(2 * length);
        crypto.getRandomValues(bytes);
        for (const byte of bytes) {
          // rejection sampling keeps the letters equally likely:
          if (byte < 4 * alphabet.length && result.length < length) {
            result += alphabet[byte % alphabet.length];
          }
        }
      }
      return result;
    };
    let password = random_string(20);
    while (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(password)) {
      password = random_string(20);
    }
    return {email: 'guest-' + random_string(10).toLowerCase() + '@vodle.it', password};
  }

  record_consent() {
    // store privacy consent in database:
    this.setu('consent', consent_statement);
  }

  /** Whether the consent to the privacy statement is still to be given
   *  (#193): a guest is created silently when a magic link is opened, and
   *  the poll page then shows the consent question and stores no rating
   *  until it is answered. Nothing is pending when the deployment has no
   *  privacy statement; a user who withdrew the consent in the settings is
   *  asked the same way. */
  get consent_pending(): boolean {
    return !!environment.privacy_statement_url && this.getu('consent') == '0';
  }

  change_credentials(changes: {email?: string, password?: string}): Promise<void> {
    /** The settings page's way of changing e-mail address or password
     *  (#330): what the old credentials own moves to the new ones — on the
     *  Matrix backend a new password is set on the homeserver and the user
     *  room re-encrypted, a new address means another account, which takes
     *  over the voter rooms and the data; on CouchDB the user documents are
     *  re-written under the new identity and the user db connected anew. */
    this.G.L.entry("DataService.change_credentials");
    if (changes.email !== undefined) {
      this.G.S.email = changes.email;
    }
    if (changes.password !== undefined) {
      this.G.S.password = changes.password;
    }
    const move = this.pending_user_data_move();   // recorded by note_credentials_change
    if (!move || !this.credentials_snapshot()) {
      return Promise.resolve();
    }
    // a guest who gives an address of their own becomes a regular user:
    this.setu('guest', '');
    return this.perform_user_data_move(move)
      .then(() => {
        if (!environment.useMatrixBackend) {
          return this.reconnect_user_db().then(() => {});
        }
      })
      .catch(err => {
        this.G.L.error("DataService.change_credentials: the move failed, it is retried at the next start", err);
      });
  }

  // REMOTE CONNECTION METHODS:

  private get_remote_connection(server_url:string, user_vodle_password:string,
                                actual_db_username:string, actual_db_user_password:string
                                ): Promise<PouchDB> {
    // TODO: check network reachability!
    /* 
    Get a remote connection to a couchdb for storing user, poll, or voter data.
    For this, first connect as public user 'vodle', 
    check whether private user exist as db user,
    if necessary, generate it in the db, then connect again as this user,
    finally try creating/updating a timestamp file.
    */ 
    this.G.L.entry("DataService.get_remote_connection", server_url, user_vodle_password, actual_db_username, actual_db_user_password);
    // since all this may take some time,
    // make clear we are working:
    this.show_loading();

    // Then return a promise to start the process:
    const promise = new Promise((resolve, reject) => {

      // first connect to database "_users" with public credentials:
      const conn_as_user_vodle = this.get_couchdb(server_url+"/_users", "vodle", user_vodle_password);

      // ASYNC:
      // try to get info to see if credentials are valid:
      this.G.L.debug("DataService.get_remote_connection trying to get info for "+server_url+"/_users as user vodle");
      conn_as_user_vodle.info()
      .then(doc => { 

        this.G.L.debug("DataService logged into "+server_url+"/_users as user 'vodle'. Info:", doc);

        // then connect to database "vodle" with private credentials:
        const conn_as_actual_user = this.get_couchdb(server_url+"/vodle", actual_db_username, actual_db_user_password);

        // ASYNC:
        // try to get info to see if credentials are valid:
        this.G.L.debug("DataService.get_remote_connection trying to get info for "+server_url+"/vodle as actual user "+actual_db_username);
        conn_as_actual_user.info()
        .then(doc => { 

          this.G.L.debug("DataService logged into "+server_url+" as actual user. Info:", doc);

          // ASYNC:
          this.test_remote_connection(conn_as_actual_user, actual_db_username, actual_db_user_password)
          .then(success => {

            // RESOLVE:
            resolve(conn_as_actual_user);

          }).catch(err => {

            // Since we could log in but not write, the db must be configured wrong:
            this.G.L.error("DataService.get_remote_connection could not write in database "+server_url+"/vodle as user "+actual_db_username+ ". Please contact the database server admin!", err);

            // REJECT:
            reject(["write failed", err]);

          }) 

        }).catch(err => {

          this.G.L.debug("DataService.get_remote_connection could not log into "+server_url+"/vodle as actual user:", err);
          this.G.L.info("DataService.get_remote_connection: logging in for the first time as this user? Trying to register user "+actual_db_username+" in database.");

          // ASYNC:
          // try to generate new user:
          conn_as_user_vodle.put({ 
            _id: "org.couchdb.user:"+actual_db_username,
            name: actual_db_username, 
            password: actual_db_user_password,
            type: "user",
            roles: [],
            comment: "user generated by vodle"
          }).then(response => {

            this.G.L.debug("DataService.get_remote_connection generated user "+actual_db_username);

            // connect again with private credentials:
            const conn_as_actual_user = this.get_couchdb(server_url+"/vodle", actual_db_username, actual_db_user_password);
            this.G.L.debug("DataService.get_remote_connection trying to get info for "+server_url+"/vodle as actual user "+actual_db_username);

            // ASYNC:
            // try to get info to see if credentials are valid:
            conn_as_actual_user.info()
            .then(doc => { 

              this.G.L.debug("DataService.get_remote_connection logged into "+server_url+" as new actual user "+actual_db_username+". Info:", doc);

              // ASYNC:
              this.test_remote_connection(conn_as_actual_user, actual_db_username, actual_db_user_password)
              .then(success => {

                this.G.L.trace("DataService.get_remote_connection has write access as "+actual_db_username+". All looks fine.");

                // RESOLVE:
                resolve(conn_as_actual_user);

              }).catch(err => {

                // Since we could log in but not write, the db must be configured wrong, so notify user of this:
                this.G.L.error("DataService could not write in database "+server_url+"/vodle as new user "+actual_db_username+ ". Please contact the database server admin!", err);

                // REJECT:
                reject(["write failed", err]);

              }) 

            }).catch(err => {

              this.G.L.debug("DataService.get_remote_connection could not log into "+server_url+"/vodle as newly generated user:", err);
              reject(["private login failed", err]);

            });
          
          }).catch(err => {

            this.G.L.error("DataService.get_remote_connection could not generate user "+actual_db_username, err);

            // REJECT:
            reject(["generate user failed", err]);

          });

        });

      }).catch(err => {

        this.G.L.error("DataService.get_remote_connection could not log into "+server_url+"/_users as user 'vodle':", err);

        // REJECT:
        reject(["public login failed", err]);

      });

    });

    this.G.L.exit("DataService.get_remote_connection");
    return promise;
  }

  private get_couchdb(url:string, username:string, password:string) {
    return new PouchDB(url, {
      auth: {username: username, password: password},
      skipSetup: true
    });
    // TODO: prevent Browser popup on 401?
  }

  private test_remote_connection(conn:PouchDB, private_username:string, private_password:string): Promise<boolean> {
    // FIXME: sometimes this gives an
    // ERROR Error: Uncaught (in promise): {"status": 409, "name": "conflict", "message": "Document update conflict"}
    return new Promise((resolve, reject) => {

      // testing is currently deactivated to speed up performance, 
      // so we simply:
      resolve(true);

      /*
      // try creating or updating a timestamp document
      const _id = "~"+private_username+"§timestamp", value = encrypt((new Date()).toISOString(), private_password);

      // ASYNC:
      conn.get(_id)
      .then(doc => {

        // doc exists, try updating with current time:
        doc.value = value;
        conn.put(doc)
        .then(response => {

          resolve(true);

        }).catch(err => {

          reject(err);

        });

      }).catch(err => {

        // try generating new doc:
        conn.put({_id:_id, value:value})
        .then(response => {

          resolve(true);

        }).catch(err => {

          reject(err);

        });

      });
      */
    });
  }

  // SYNCHRONISATION:

  private start_user_sync(): boolean {
    // try starting user data local <--> remote syncing:
    this.G.L.entry("DataService.start_user_sync");
    var result: boolean;

    if (this.remote_user_db) { 
      const email_and_pw_hash = this.get_email_and_pw_hash();
      this.G.L.info("DataService starting user data sync");
      if (this.user_db_sync_handler) {
        this.G.L.trace("DataService.start_user_sync already running");
        this.G.L.exit("DataService.start_user_sync", true);
        return true;
      }
      if (this.user_sync_start_pending) {
        this.G.L.trace("DataService.start_user_sync already waiting for bootstrap");
        this.G.L.exit("DataService.start_user_sync", true);
        return true;
      }
      this.user_sync_start_pending = true;
      const sync_start_generation = this.user_sync_start_generation;

      // ASYNC:
      // #292: only start syncing after the local cache bootstrap has completed,
      // so that no incoming change can be overwritten by stale bootstrap data:
      this.user_db_bootstrapped.then(() => {
        if (sync_start_generation !== this.user_sync_start_generation) {
          return;
        }
        if (!this.user_sync_start_pending) {
          this.user_sync_start_pending = false;
          return;
        }
        if (this.shutting_down) {
          this.user_sync_start_pending = false;
          return;
        }
        if (!this._local_synced_user_db) {
          this.user_sync_start_pending = false;
          return;
        }
        if (!this.remote_user_db) {
          this.user_sync_start_pending = false;
          return;
        }
        if (this.user_db_sync_handler) {
          this.user_sync_start_pending = false;
          return;
        }
        try {
          this.set_replication_active('user', true);

          const user_sync_handler = this.local_synced_user_db.sync(this.remote_user_db, {
            // see options here: https://pouchdb.com/api.html#replication
            live: true,
            retry: true,
            batch_size: 1000, // see https://docs.couchdb.org/en/stable/api/database/changes.html?highlight=_changes
            // TODO: if the following works, also use it for poll dbs: 
            style: "main_only", // apparently not used
            seq_interval: 1000, // "  // apparently not used
            revs: false,
            // (until here)
            include_docs: true,
            selector: this.get_user_doc_selector(email_and_pw_hash)
          });
          this.user_db_sync_handler = user_sync_handler;
          user_sync_handler.on('change', change => {
            if (this.user_db_sync_handler !== user_sync_handler) {
              return;
            }
            this.note_replication_progress('user');
            this.handle_user_db_change(change);
          }).on('paused', () => {
            if (this.user_db_sync_handler !== user_sync_handler) {
              return;
            }
            // replication was paused
            this.set_replication_active('user', false);
            this.G.L.info("DataService pausing user data sync");
          }).on('active', () => {
            if (this.user_db_sync_handler !== user_sync_handler) {
              return;
            }
            // replication was resumed
            this.set_replication_active('user', true);
            this.G.L.info("DataService resuming user data sync");
          }).on('denied', err => {
            if (this.user_db_sync_handler !== user_sync_handler) {
              return;
            }
            // a document failed to replicate (e.g. due to permissions)
            this.note_replication_progress('user');
            this.G.L.error("DataService user data sync denied", err);
          }).on('complete', info => {
            if (this.user_db_sync_handler !== user_sync_handler) {
              return;
            }
            // handle complete
            this.set_replication_active('user', false);
            this.G.L.info("DataService completed user data sync", info);
          }).on('error', err => {
            if (this.user_db_sync_handler !== user_sync_handler) {
              return;
            }
            // totally unhandled error (shouldn't happen)
            this.set_replication_active('user', false);
            this.G.L.error("DataService error at user data sync", err);
            this.user_db_sync_handler = null;
            this.user_sync_start_pending = false;
            if (!this.shutting_down && this.remote_user_db) {
              this.replication_restart_pending['user'] = true;
              window.setTimeout(() => {
                if (!this.replication_restart_pending['user']) {
                  return;
                }
                this.replication_restart_pending['user'] = false;
                if (this.shutting_down || !this.remote_user_db || this.user_db_sync_handler || this.user_sync_start_pending) {
                  return;
                }
                this.start_user_sync();
              }, replication_watchdog_interval_ms);
            }
          });
        } catch (err) {
          this.set_replication_active('user', false);
          this.user_db_sync_handler = null;
          throw err;
        } finally {
          this.user_sync_start_pending = false;
        }

      }).catch(err => {
        this.user_sync_start_pending = false;
        this.G.L.error("DataService.start_user_sync could not start because user db bootstrap failed", err);
      });

      result =  true;

    } else {

      result = false;

    }
    this.G.L.exit("DataService.start_user_sync", result);
    return result;
  }

  private start_poll_sync(pid:string): boolean {
    // try starting poll data local <--> remote syncing:
    this.G.L.entry("DataService.start_poll_sync", pid);
    var result: boolean;

    // Phase 14: Use Matrix event handlers for real-time sync
    if (environment.useMatrixBackend) {
      this.G.L.info("DataService starting Matrix poll sync", pid);
      // Prevent duplicate listener registration on repeated calls
      if (!this._matrixPollListeners[pid]) {
        const listener = {
          onRatingUpdate: (pollId: string, vid: string, optionId: string, rating: number) => {
            try {
              this.G.L.info("DataService Matrix onRatingUpdate", pollId, vid, optionId, rating);
              // Bridge the incoming rating into poll_caches so getv() returns it
              if (!this.poll_caches[pollId]) {
                this.poll_caches[pollId] = {};
              }
              const pkey = this.get_voter_key_prefix(pollId, vid) + 'rating.' + optionId;
              this.poll_caches[pollId][pkey] = String(rating);
              const poll = this.G.P.polls[pollId];
              if (poll && !(optionId in poll.options)) {
                // A rating can arrive before this poll's Option objects
                // exist — the live handlers are registered before
                // load_poll_contents has read the options. Tallying it then
                // throws on the missing Option and aborts the whole tally
                // (#327). The value is in poll_caches above, and
                // load_poll_contents bridges every rating into the tally
                // once the options are there, so it is not lost:
                this.G.L.info("DataService Matrix onRatingUpdate before the option exists",
                  pollId, optionId);
                return;
              }
              // Feed the tally system — update_own_rating is idempotent (no-op if
              // the value hasn't changed) and with update_tally=true it calls
              // tally_all() which recomputes scores and updates the UI.
              this.G.P.update_own_rating(pollId, vid, optionId, rating, true);
            } catch (err) {
              this.G.L.error("DataService Matrix onRatingUpdate callback failed", pollId, err);
            }
          },
          onVoterDataChange: (pollId: string, vid: string, key: string, value: any) => {
            // a delegation request or response, which until #327 had no way
            // of reaching this device at all
            try {
              this.G.L.info("DataService Matrix onVoterDataChange", pollId, vid, key);
              this.matrix_voter_data_arrived(pollId, vid, key, value);
            } catch (err) {
              this.G.L.error("DataService Matrix onVoterDataChange callback failed", pollId, key, err);
            }
          },
          onOptionAdded: (pollId: string, oid: string, option: {name: string; description: string; url: string; amounts?: string}) => {
            // an option another participant added while the poll runs
            // (#324): register it like doc2poll_cache does for CouchDB, so
            // the Poll object gets an Option and the page (onDataChange
            // follows) shows it
            try {
              this.G.L.info("DataService Matrix onOptionAdded", pollId, oid);
              const cache = this.ensure_poll_cache(pollId);
              cache['option.' + oid + '.oid'] = oid;
              cache['option.' + oid + '.name'] = option.name || '';
              cache['option.' + oid + '.desc'] = option.description || '';
              cache['option.' + oid + '.url'] = option.url || '';
              cache['option.' + oid + '.amounts'] = option.amounts || '';
              if (!(pollId in this._pid_oids)) {
                this._pid_oids[pollId] = new Set();
              }
              this._pid_oids[pollId].add(oid);
              if (pollId in this.G.P.polls && !(oid in this.G.P.polls[pollId].options)) {
                new Option(this.G, this.G.P.polls[pollId], oid);
              }
            } catch (err) {
              this.G.L.error("DataService Matrix onOptionAdded callback failed", pollId, oid, err);
            }
          },
          onDataChange: () => {
            try {
              if (this.page && this.page.onDataChange) this.page.onDataChange();
            } catch (err) {
              this.G.L.error("DataService Matrix onDataChange callback failed", pid, err);
            }
          },
          onInitialScanComplete: (pollId: string) => {
            // initial restoration of this poll's ratings finished — let the
            // poll page re-sort its options once with the complete data:
            try {
              if (this.page && this.page.pid == pollId && this.page.onInitialScanComplete) {
                this.page.onInitialScanComplete();
              }
            } catch (err) {
              this.G.L.error("DataService Matrix onInitialScanComplete callback failed", pid, err);
            }
          }
        };
        this._matrixPollListeners[pid] = listener;
        this.poll_matrix(pid).then(m => m.addPollEventListener(pid, listener)).catch(err => {
          this.G.L.error("DataService could not register the poll's listeners", pid, err);
        });
      }
      // only the poll room's handlers: discovering and joining this poll's
      // voter rooms waits until the poll is opened (#327, ensure_poll_loaded)
      this.poll_matrix(pid).then(m => m.setupPollRoomHandlers(pid)).catch(err => {
        this.G.L.error("DataService Matrix poll sync setup failed", pid, err);
      });
      result = true;
      this.G.L.exit("DataService.start_poll_sync", pid, result);
      return result;
    }

    if (this.remote_poll_dbs[pid]) { 
      this.G.L.info("DataService starting poll data sync", pid);
      if (this.poll_db_sync_handlers[pid]) {
        this.G.L.trace("DataService.start_poll_sync already running", pid);
        this.G.L.exit("DataService.start_poll_sync", pid, true);
        return true;
      }
      if (this.poll_sync_start_pending[pid]) {
        this.G.L.trace("DataService.start_poll_sync already waiting for bootstrap", pid);
        this.G.L.exit("DataService.start_poll_sync", pid, true);
        return true;
      }
      this.poll_sync_start_pending[pid] = true;
      if (!(pid in this.poll_sync_start_generation)) {
        this.poll_sync_start_generation[pid] = 0;
      }
      const sync_start_generation = this.poll_sync_start_generation[pid] || 0;

      // ASYNC:
      // #292: only start syncing after this poll's local cache bootstrap has
      // completed, so that no incoming change can be overwritten by stale
      // bootstrap data:
      this.wait_for_poll_db_bootstrap(pid).then(() => {
        if (sync_start_generation !== this.poll_sync_start_generation[pid]) {
          return;
        }
        if (!this.poll_sync_start_pending[pid]) {
          this.poll_sync_start_pending[pid] = false;
          return;
        }
        if (this.shutting_down) {
          this.poll_sync_start_pending[pid] = false;
          return;
        }
        if (!(pid in this.local_poll_dbs)) {
          this.poll_sync_start_pending[pid] = false;
          return;
        }
        if (!(pid in this.remote_poll_dbs)) {
          this.poll_sync_start_pending[pid] = false;
          return;
        }
        if (this.poll_db_sync_handlers[pid]) {
          this.poll_sync_start_pending[pid] = false;
          return;
        }
        try {
          this.set_replication_active(pid, true);

          const poll_sync_handler = this.get_local_poll_db(pid).sync(this.remote_poll_dbs[pid], {
            live: true,
            retry: true,
            batch_size: 1000, // see https://docs.couchdb.org/en/stable/api/database/changes.html?highlight=_changes
            include_docs: true,
            selector: this.get_poll_doc_selector(pid)
          });
          this.poll_db_sync_handlers[pid] = poll_sync_handler;
          poll_sync_handler.on('change', change => {
            if (this.poll_db_sync_handlers[pid] !== poll_sync_handler) {
              return;
            }
            this.note_replication_progress(pid);
            this.handle_poll_db_change.bind(this)(pid, change);
          }).on('paused', info => {
            if (this.poll_db_sync_handlers[pid] !== poll_sync_handler) {
              return;
            }
            // replication was paused, usually because of a lost connection
            this.set_replication_active(pid, false);
            this.G.L.info("DataService pausing poll data sync", pid, this.G.P.polls[pid] ? this.G.P.polls[pid]._state : null);
            const _ = window.navigator.onLine;
            if (pid in this.G.P.polls) {
              this.G.P.polls[pid].syncing = false;
            }
            this.G.remove_spinning_reason(pid);
          }).on('active', info => {
            if (this.poll_db_sync_handlers[pid] !== poll_sync_handler) {
              return;
            }
            // replication was resumed
            this.set_replication_active(pid, true);
            this.G.L.info("DataService resuming poll data syncing", pid, info);
            const _ = window.navigator.onLine;
            if (pid in this.G.P.polls) {
              this.G.P.polls[pid].syncing = true;
            }
            this.G.add_spinning_reason(pid);
          }).on('denied', err => {
            if (this.poll_db_sync_handlers[pid] !== poll_sync_handler) {
              return;
            }
            // a document failed to replicate (e.g. due to permissions)
            this.note_replication_progress(pid);
            this.G.L.error("DataService poll data sync denied", pid, err);
          }).on('complete', info => {
            if (this.poll_db_sync_handlers[pid] !== poll_sync_handler) {
              return;
            }
            // handle complete
            this.set_replication_active(pid, false);
            this.G.L.info("DataService completed poll data sync", pid, info);
            const _ = window.navigator.onLine;
            if (pid in this.G.P.polls) {
              this.G.P.polls[pid].syncing = false;
            }
          }).on('error', err => {
            if (this.poll_db_sync_handlers[pid] !== poll_sync_handler) {
              return;
            }
            // totally unhandled error (shouldn't happen)
            this.set_replication_active(pid, false);
            this.G.L.error("DataService error at poll data sync", pid, err);
            delete this.poll_db_sync_handlers[pid];
            if (pid in this.G.P.polls) {
              this.G.P.polls[pid].syncing = false;
            }
            this.G.remove_spinning_reason(pid);
            this.poll_sync_start_pending[pid] = false;
            if (!this.shutting_down && (pid in this.remote_poll_dbs)) {
              this.replication_restart_pending[pid] = true;
              window.setTimeout(() => {
                if (!this.replication_restart_pending[pid]) {
                  return;
                }
                this.replication_restart_pending[pid] = false;
                if (this.shutting_down || !(pid in this.remote_poll_dbs) || this.poll_db_sync_handlers[pid] || this.poll_sync_start_pending[pid]) {
                  return;
                }
                this.start_poll_sync(pid);
              }, replication_watchdog_interval_ms);
            }
          });
        } catch (err) {
          this.set_replication_active(pid, false);
          delete this.poll_db_sync_handlers[pid];
          throw err;
        } finally {
          this.poll_sync_start_pending[pid] = false;
        }

      }).catch(err => {
        this.poll_sync_start_pending[pid] = false;
        this.G.L.error("DataService.start_poll_sync could not start because poll db bootstrap failed", pid, err);
      });

      result =  true;

    } else {

      result = false;

    }
    this.G.L.exit("DataService.start_poll_sync", pid, result);
    return result;
  }

  stop_poll_sync(pid: string) {
    delete this.replication_restart_pending[pid];
    delete this.poll_sync_start_pending[pid];
    this.poll_sync_start_generation[pid] = (this.poll_sync_start_generation[pid] || 0) + 1;
    // Phase 14: Tear down Matrix event handlers
    if (environment.useMatrixBackend) {
      delete this._matrixPollListeners[pid];
      this.poll_matrix_promises[pid]?.then(m => m.teardownPollEventHandlers(pid)).catch(() => {});
      return;
    }
    if (pid in this.G.D.poll_db_sync_handlers && !!this.G.D.poll_db_sync_handlers[pid]) {
      this.G.D.poll_db_sync_handlers[pid].cancel();
      delete this.G.D.poll_db_sync_handlers[pid];
    }
    // stop watching this poll's replication (#292):
    delete this.replication_active[pid];
    delete this.replication_progress[pid];
    delete this.replication_stalled[pid];
  }

  // Replication watchdog (issue #292, targets #159/#171):

  private note_replication_progress(key: string) {
    /** record that the replication identified by 'user' or a pid made progress. */
    this.replication_progress[key] = Date.now();
    this.replication_restart_pending[key] = false;
    if (this.replication_stalled[key]) {
      this.replication_stalled[key] = false;
      this.G.L.info("DataService replication is no longer stalled", key);
    }
  }

  private set_replication_active(key: string, active: boolean) {
    this.replication_active[key] = active;
    if (active) {
      this.note_replication_progress(key);
    }
  }

  get_replication_status(key: string = 'user'): string {
    /** expose sync status for UI/diagnostics: 'stalled', 'active', or 'idle'. */
    return this.replication_stalled[key] ? 'stalled' : this.replication_active[key] ? 'active' : 'idle';
  }

  private start_replication_watchdog() {
    /** periodically check whether some replication stalled (no progress for a
     *  while despite being active), and if so, restart it so that stalled
     *  syncs self-recover (#159 "syncing never ends", #171 incomplete uploads). */
    if (this.replication_watchdog_id != null) {
      return;
    }
    this.replication_watchdog_id = setInterval(() => {
      this.check_for_stalled_replications();
    }, replication_watchdog_interval_ms);
  }

  private stop_replication_watchdog() {
    if (this.replication_watchdog_id != null) {
      clearInterval(this.replication_watchdog_id);
      this.replication_watchdog_id = null;
    }
  }

  private check_for_stalled_replications() {
    const now = Date.now();
    for (const key of Object.keys(this.replication_active)) {
      if (this.shutting_down) {
        continue;
      }
      if (!this.replication_active[key]) {
        continue;
      }
      if (now - (this.replication_progress[key] || 0) <= replication_stall_threshold_ms) {
        continue;
      }
      if (this.replication_restart_pending[key]) {
        continue;
      }
      const can_restart =
        (key == 'user' && !!this.remote_user_db) ||
        (key != 'user' && key in this.remote_poll_dbs);
      if (!can_restart) {
        delete this.replication_active[key];
        delete this.replication_progress[key];
        delete this.replication_stalled[key];
        delete this.replication_restart_pending[key];
        continue;
      }
      // active but no progress for too long: consider it stalled and restart it.
      this.replication_stalled[key] = true;
      this.replication_active[key] = false;
      this.G.L.warn("DataService replication appears stalled, attempting restart", key);
      if (key == 'user') {
        this.replication_restart_pending[key] = true;
        this.restart_user_sync();
      } else {
        this.replication_restart_pending[key] = true;
        this.restart_poll_sync(key);
      }
    }
  }

  private restart_user_sync() {
    if (this.shutting_down) {
      return;
    }
    this.user_sync_start_pending = false;
    this.user_sync_start_generation += 1;
    if (this.user_db_sync_handler) {
      try {
        this.user_db_sync_handler.cancel();
      } catch (err) {
        this.G.L.warn("DataService could not cancel stalled user data sync", err);
      }
      this.user_db_sync_handler = null;
    }
    this.start_user_sync();
  }

  private restart_poll_sync(pid: string) {
    if (this.shutting_down) {
      return;
    }
    this.poll_sync_start_pending[pid] = false;
    this.poll_sync_start_generation[pid] = (this.poll_sync_start_generation[pid] || 0) + 1;
    if (this.poll_db_sync_handlers[pid]) {
      try {
        this.poll_db_sync_handlers[pid].cancel();
      } catch (err) {
        this.G.L.warn("DataService could not cancel stalled poll data sync", pid, err);
      }
      delete this.poll_db_sync_handlers[pid];
    }
    // unstick the syncing spinner so the UI does not spin forever:
    if (pid in this.G.P.polls) {
      this.G.P.polls[pid].syncing = false;
    }
    this.G.remove_spinning_reason(pid);
    this.start_poll_sync(pid);
  }

  // PUBLIC DATA ACCESS METHODS:

  getu(key:string): string {
    // get user data item
    
    // Phase 2: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      // For Matrix backend, we need to handle this synchronously but Matrix is async
      // Store value in cache for now - actual Matrix sync happens via login/init
      // This is a temporary bridge until full Matrix integration in later phases
      return this.user_cache[key] || '';
    }
    
    let value = this.user_cache[key] || '';
    if (!value && key=='language') {
      value = this.getu('local_language');
    }
    return value;
  }

  setu(key:string, value:string): boolean {
    if (this.getu(key) == value) {
      return true;
    }
    // set user data item
    value = value || '';
    if (key=='language') {
      this.setu('local_language', value);
    } else if (key=='local_language') {
        const used_lang = value!=''?value:environment.default_lang;
        this.translate.use(used_lang);
        this.document.documentElement.lang = used_lang; 
    }
    const old_values = {};
    if (keys_triggering_data_move.includes(key)) {
      // remember old credentials:
      for (const k of keys_triggering_data_move) {
        old_values[k] = this.user_cache[k];
      }
    }
    this.user_cache[key] = value;
    this.G.L.trace("DataService.setu", key, value);
    if (key == 'email' || key == 'password') {
      this.note_credentials_change();
    }
    
    // Phase 2: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (local_only_user_keys.includes(key)) {
        // credentials and database settings never leave the device
        // (syncUserDataWithMatrix and its restore skip them alike):
        this.store_user_data(key, this.user_cache, key);
      } else if (this.matrixService.isLoggedIn()) {
        // Sync to Matrix immediately
        this.G.L.info("DataService.setu syncing to Matrix:", key);
        this.matrixService.setUserData(key, value).catch(err => {
          this.G.L.error("DataService.setu Matrix sync failed", key, err);
        });
      } else {
        // before the login (the consent, the guest flag): pushed by
        // syncUserDataWithMatrix right after it
        this.G.L.trace("DataService.setu Matrix not logged in yet, synced after login:", key);
      }
      
      // Skip CouchDB storage when using Matrix backend
      if (keys_triggering_data_move.includes(key)) {
        this.move_user_data(old_values);
      }
      return true;
    }
    
    if (keys_triggering_data_move.includes(key)) {
      this.move_user_data(old_values);
    }
    return this.store_user_data(key, this.user_cache, key);
  }

  delu(key:string) {
    // delete a user data item
    if (!(key in this.user_cache)) {
      this.G.L.trace("DataService.delu cannot delete unknown key", key);
      return;
    }
    delete this.user_cache[key];
    
    // Phase 2: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (this.matrixService.isLoggedIn()) {
        // Asynchronously delete from Matrix backend (skip CouchDB entirely)
        this.matrixService.deleteUserData(key).catch(err => {
          this.G.L.warn("DataService.delu Matrix delete failed", key, err);
        });
      }
      // Skip CouchDB delete when using Matrix backend
      return;
    }
    
    this.delete_user_data(key);
  }

  private pid_is_draft(pid): boolean {
    return this.user_cache[get_poll_key_prefix(pid) + 'state'] == 'draft';
  } 

  private poll_data_authority(pid: string, key: string): 'user' | 'poll' {
    /** Central rule deciding which database is authoritative for a poll data
     *  item (#292 item 6): draft polls' data and certain personal keys live in
     *  the user db, everything else in the poll's own db. All getp/setp/delp
     *  routing must go through this. */
    const pos = (key+'.').indexOf('.'),
          subkey = (key+'.').slice(0, pos);
    return (this.pid_is_draft(pid) || poll_keystarts_in_user_db.includes(subkey)) ? 'user' : 'poll';
  }

  getp(pid:string, key:string): string {
    // get poll data item

    // Phase 10: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      // For Matrix backend, draft polls and user-db keys stay in user_cache.
      // Non-draft poll data comes from poll_caches (populated from Matrix).
      if (this.poll_data_authority(pid, key) == 'user') {
        const ukey = get_poll_key_prefix(pid) + key;
        return this.user_cache[ukey] || '';
      }
      this.ensure_poll_cache(pid);
      if (key in this.poll_caches[pid]) {
        return this.poll_caches[pid][key] || '';
      }
      // Fall back to user_cache for keys not yet moved to Matrix
      // (e.g. when Matrix room creation failed during draft→running)
      const ukey = get_poll_key_prefix(pid) + key;
      return this.user_cache[ukey] || '';
    }

    let value = null;
    if (this.poll_data_authority(pid, key) == 'user') {
      // draft polls' data is stored in user's database:
      const ukey = get_poll_key_prefix(pid) + key;
      value = this.user_cache[ukey] || '';
    } else {
      // other polls' data is stored in poll's own database:
      this.ensure_poll_cache(pid);
      if (key in this.poll_caches[pid]) {
        // test key presence, not the value: an authoritative empty string
        // (e.g. an empty description or url) must not be overridden by a
        // possibly stale user db copy (#292):
        value = this.poll_caches[pid][key] || '';
      } else {
        // fall back to a possibly remaining user db copy, e.g. when a
        // draft→running move write has not been confirmed yet (#292):
        const ukey = get_poll_key_prefix(pid) + key;
        value = this.user_cache[ukey] || '';
      }
    }
    return value;
  }

  setp(pid:string, key:string, value:string): boolean {
    // set poll data item
    // register pid, oid if necessary:
    this._pids.add(pid);
    if (this.getp(pid, key) == value) {
      return true;
    }
    if (key.startsWith('option.')) {
      this.G.L.trace("DataService.setp option key", pid, key, value);
      if (!(pid in this._pid_oids)) {
        this._pid_oids[pid] = new Set();
      }
      const keyend = key.slice('option.'.length), oid = keyend.slice(0, keyend.indexOf('.'));
      this._pid_oids[pid].add(oid);
      this.G.L.trace("DataService.setp option pid oid", pid, oid, this._pid_oids[pid].size, [...this._pid_oids[pid]]);
    }
    // decide where to store data:
    const authority = this.poll_data_authority(pid, key);

    // Phase 10: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (authority == 'user') {
        return this._setp_in_userdb(pid, key, value);
      }
      // Non-draft, non-userdb key: store in local cache and sync to Matrix
      this.ensure_poll_cache(pid);
      this.poll_caches[pid][key] = value;
      if (key.startsWith('option.')) {
        // an option added while the poll runs. Not a state event: the room's
        // state is locked once the poll runs (lockPollMetadata), and the
        // other clients read options from the timeline only. One
        // m.room.vodle.poll.option timeline event carries all of the
        // option's fields, sent once the Option constructor has set them
        // all (#324, the Matrix side of #163):
        this.schedule_matrix_option_event(pid, key);
        return true;
      }
      this.poll_matrix(pid).then(m => m.setPollData(pid, key, value)).catch(err => {
        this.G.L.error("DataService.setp Matrix sync failed", pid, key, err);
      });
      return true;
    }

    if (authority == 'user') {
      return this._setp_in_userdb(pid, key, value);
    } else if (key.startsWith('option.')) {
      if (!(key in this.poll_caches[pid])) {
        return this._setp_in_polldb(pid, key, value);
      } else {
        this.G.L.error("DataService.setp change option attempted for existing entry", pid, key, value);
      }
    } else {
      this.G.L.error("DataService.setp non-local attempted for non-draft poll", pid, key, value);
    }
  }

  /** timers of options whose fields are still being set, by "<pid>:<oid>" */
  private pending_matrix_options: Record<string, ReturnType<typeof setTimeout>> = {};

  /**
   * Send an option added to a running poll to Matrix as ONE timeline event
   * (MatrixService.addOption) once the current task has set all of its
   * fields: the Option constructor calls setp for oid, name, desc and url in
   * a row, and the event is immutable, so it must carry all of them (#324).
   */
  private schedule_matrix_option_event(pid: string, key: string) {
    const keyend = key.slice('option.'.length), oid = keyend.slice(0, keyend.indexOf('.')),
          mkey = pid + ':' + oid;
    if (!oid || mkey in this.pending_matrix_options) {
      return;
    }
    this.pending_matrix_options[mkey] = setTimeout(() => {
      delete this.pending_matrix_options[mkey];
      const cache = this.poll_caches[pid] || {}, name = cache['option.' + oid + '.name'];
      if (!name) {
        this.G.L.warn("DataService: option without a name not sent to Matrix", pid, oid);
        return;
      }
      this.poll_matrix(pid).then(m => m.addOption(pid, oid, {
        name,
        description: cache['option.' + oid + '.desc'] || '',
        url: cache['option.' + oid + '.url'] || '',
        // the amounts a budget poll's option may receive (#201):
        ...(cache['option.' + oid + '.amounts'] ? {amounts: cache['option.' + oid + '.amounts']} : {})
      })).catch(err => {
        this.G.L.error("DataService: sending the new option to Matrix failed", pid, oid, err);
      });
    }, 0);
  }

  delp(pid:string, key:string) {
    // delete a poll data item
    // deregister pid, oid if necessary:
    if (key == "title") {
      this._pids.delete(pid);
      delete this._pid_oids[pid];
    }
    if (key.startsWith('option.') && key.endsWith('name') && (pid in this._pid_oids)) {
      this.G.L.trace("DataService.delp option key", pid, key);
      const keyend = key.slice('option.'.length), oid = keyend.slice(0, keyend.indexOf('.'));
      this._pid_oids[pid].delete(oid);
      this.G.L.trace("DataService.delp option pid oid", pid, oid, this._pid_oids[pid].size, [...this._pid_oids[pid]]);
    }
    const authority = this.poll_data_authority(pid, key);

    // Phase 10: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (authority == 'user') {
        const ukey = get_poll_key_prefix(pid) + key;
        this.delu(ukey);
      } else {
        this.ensure_poll_cache(pid);
        delete this.poll_caches[pid][key];
        this.poll_matrix(pid).then(m => m.deletePollData(pid, key)).catch(err => {
          this.G.L.warn("DataService.delp Matrix delete failed", pid, key, err);
        });
      }
      return;
    }

    if (authority == 'user') {
      // construct key for user db:
      const ukey = get_poll_key_prefix(pid) + key;
      this.delu(ukey);
    } else {
      if (!(pid in this.poll_caches) || !(key in this.poll_caches[pid])) {
        this.G.L.trace("DataService.delp cannot delete unknown combination", pid, key);
        return;
      }
      delete this.poll_caches[pid][key];
      this.delete_poll_data(pid, key);
    }      
  }

  getv(pid: string, key: string, vid?: string): string {
    // get own voter data item

    // Phase 11: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (this.pid_is_draft(pid)) {
        const ukey = get_poll_key_prefix(pid) + this.get_voter_key_prefix(pid, vid) + key;
        return this.user_cache[ukey] || '';
      }
      const pkey = this.get_voter_key_prefix(pid, vid) + key;
      this.ensure_poll_cache(pid);
      return this.poll_caches[pid][pkey] || '';
    }

    let value = null;
    if (this.pid_is_draft(pid)) {
      // draft polls' data is stored in user's database.
      // construct key for user db:
      const ukey = get_poll_key_prefix(pid) + this.get_voter_key_prefix(pid, vid) + key;
      value = this.user_cache[ukey] || '';
    } else {
      // other polls' data is stored in poll's own database.
      // construct key for poll db:
      const pkey = this.get_voter_key_prefix(pid, vid) + key;
    //  this.G.L.trace("getv", pid, key, vid, pkey)
      this.ensure_poll_cache(pid);
      value = (pkey in this.poll_caches[pid])
        ? this.poll_caches[pid][pkey] || ''
        : this.user_cache[get_poll_key_prefix(pid) + pkey] || '';
    }
    return value;
  }

  private voter_source_parts(key: string): {pid: string, pkey: string} | null {
    const match = /^poll\.([^.]+)\.(voter\.[^§]+§.+)$/.exec(key);
    return match ? {pid: match[1], pkey: match[2]} : null;
  }

  private assert_voter_mutation_current(is_current: () => boolean) {
    if (!is_current()) {
      const err: any = new Error("Voter mutation was superseded or cancelled");
      err.vodle_move_aborted = true;
      throw err;
    }
  }

  private queue_voter_mutation(
      ukey: string,
      work: (is_current: () => boolean, is_active: () => boolean) => Promise<void>,
      supersedes = true): Promise<void> {
    const pid = this.voter_source_parts(ukey).pid;
    this.poll_mutation_versions[pid] = (this.poll_mutation_versions[pid] || 0) + 1;
    const generation = this.voter_mutation_generations[ukey] =
      (this.voter_mutation_generations[ukey] || 0) + (supersedes ? 1 : 0);
    const session = this.voter_mutation_session;
    const is_active = () => !this.shutting_down && session === this.voter_mutation_session;
    const is_current = () => is_active() && generation === this.voter_mutation_generations[ukey];
    const previous = this.voter_mutation_tails[ukey] || this.voter_mutation_admission;
    const pending = previous.catch(() => {}).then(() => {
      this.assert_voter_mutation_current(is_active);
      return work(is_current, is_active);
    });
    this.voter_mutation_tails[ukey] = pending;
    pending.then(() => {
      if (is_active()) { delete this.voter_mutation_failures[ukey]; }
    }, err => {
      if (is_active() && !err?.vodle_move_aborted) {
        this.voter_mutation_failures[ukey] = err;
      }
    }).then(() => {
      if (is_active()) {
        this.poll_mutation_versions[pid] = (this.poll_mutation_versions[pid] || 0) + 1;
      }
      if (this.voter_mutation_tails[ukey] === pending) {
        delete this.voter_mutation_tails[ukey];
      }
    });
    return pending;
  }

  has_pending_poll_mutations(pid: string): boolean {
    const prefix = get_poll_key_prefix(pid);
    return Object.keys(this.voter_mutation_tails).some(key => key.startsWith(prefix))
      || Object.keys(this.voter_mutation_failures).some(key => key.startsWith(prefix));
  }

  poll_mutation_generation(pid: string): number {
    return this.poll_mutation_versions[pid] || 0;
  }

  async await_poll_mutations(pid: string): Promise<void> {
    const prefix = get_poll_key_prefix(pid);
    while (true) {
      const pending = Object.entries(this.voter_mutation_tails)
        .filter(([key]) => key.startsWith(prefix)).map(([, promise]) => promise);
      if (!pending.length) { break; }
      await Promise.all(pending.map(promise => promise.catch(() => {})));
    }
    if (Object.keys(this.voter_mutation_failures).some(key => key.startsWith(prefix))) {
      throw make_consistency_failure_error("Voter mutations are incomplete for " + pid);
    }
  }

  private cancel_voter_mutations(): Promise<void> {
    for (const pid of Object.keys(this.poll_mutation_versions)) {
      this.poll_mutation_versions[pid] += 1;
    }
    // callers that need the retired writes to be drained (or reconciled)
    // before new ones are admitted install their own barrier below:
    this.voter_mutation_admission = Promise.resolve();
    const pending = Object.values(this.voter_mutation_tails);
    this.voter_mutation_session += 1;
    this.voter_mutation_generations = {};
    this.voter_mutation_tails = {};
    this.voter_mutation_failures = {};
    return Promise.all(pending.map(promise => promise.catch(() => {}))).then(() => {});
  }

  private async retry_voter_mutation(work: () => Promise<void>, is_current: () => boolean): Promise<void> {
    for (let attempt = 1; ; attempt++) {
      this.assert_voter_mutation_current(is_current);
      try {
        await work();
        this.assert_voter_mutation_current(is_current);
        return;
      } catch (err) {
        this.assert_voter_mutation_current(is_current);
        if (attempt >= confirmed_put_max_attempts) { throw err; }
        await new Promise(resolve => window.setTimeout(resolve, environment.db_put_retry_delay_ms));
      }
    }
  }

  private async get_existing_doc(db, id: string): Promise<any> {
    try {
      return await db.get(id);
    } catch (err: any) {
      if (err?.status === 404) { return null; }
      throw err;
    }
  }

  setv(pid: string, key: string, value: string): boolean {
    /** Set a voter data item.
     * If necessary, mark the database entry with poll's due date
     * to allow couchdb validating that due date is not passed.
     */
    const ukey = get_poll_key_prefix(pid) + this.get_voter_key_prefix(pid) + key;
    if (!this.voter_mutation_tails[ukey] && !this.voter_mutation_failures[ukey]
        && this.getv(pid, key) == value) {
      return true;
    }
    // set voter data item
    if (this.pid_is_draft(pid)) {
      return this._setv_in_userdb(pid, key, value);
    } else {
      return this.setv_in_polldb(pid, key, value);
    }
  }

  async delv(pid: string, key: string): Promise<void> {
    // delete a voter data item

    // Phase 11: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      if (this.pid_is_draft(pid)) {
        const ukey = get_poll_key_prefix(pid) + this.get_voter_key_prefix(pid) + key;
        if (this.matrixService.isLoggedIn()) {
          await this.matrixService.deleteUserData(ukey);
        }
        delete this.user_cache[ukey];
      } else {
        const pkey = this.get_voter_key_prefix(pid) + key;
        this.ensure_poll_cache(pid);
        const vid = this.getp(pid, 'myvid');
        await (await this.poll_matrix(pid)).deleteVoterData(pid, vid, key);
        delete this.poll_caches[pid][pkey];
      }
      return;
    }

    const pkey = this.get_voter_key_prefix(pid) + key;
    const ukey = get_poll_key_prefix(pid) + pkey;
    return this.queue_voter_mutation(ukey, async (is_current, is_active) => {
      // A newer set waits behind this entire deletion. Only its cache updates
      // supersede ours; submitted writes cannot be cancelled retroactively.
      await this.delete_user_data_confirmed(ukey, undefined, is_active);
      this.assert_voter_mutation_current(is_active);
      if (is_current()) { delete this.user_cache[ukey]; }
      if (!this.pid_is_draft(pid)) {
        await this.delete_voter_data_confirmed(pid, pkey, is_active);
        if (is_current()) {
          this.handle_deleted_poll_doc(pid, {_id: poll_doc_id_prefix + pid + '.' + pkey});
        }
      }
    });
  }

  // #285 kept the delegation graph in two poll-wide documents that every
  // voter had to be able to rewrite. Both are derivable from what each
  // client already holds, so they are views now, not storage: see
  // DelegationService.get_direct_delegations and .get_inverse_delegations.

  // key: voter id, value: JSON list of the voter ids that have effectively
  // delegated to them, directly or through a chain.
  get_inverse_indirect_map(pid: string, option_map?: string): Map<string, string> {
    return this.G.Del.get_inverse_delegations(pid, option_map);
  }

  // key: voter id, value: their delegations as [did, rank or trust, status],
  // most preferred first.
  get_direct_delegation_map(pid: string, option_map?: string): Map<string, Array<[string, string, string]>> {
    return this.G.Del.get_direct_delegations(pid, option_map);
  }

  // #285 kept every voter's own and effective waps in one poll-wide
  // document as well. The blend they were for is computed by
  // Poll.update_weighted_proxy_ratings out of what each client already
  // holds, so that document is gone too, and with it the last reason for
  // a voter to be able to write a shared poll document.

  /** Which delegation this deployment offers (see delegation_mode above).
   *  The pid these still take is for the callers, which have one to hand and
   *  need not know that the answer no longer depends on it. */
  get_ranked_delegation_allowed(_pid?: string): boolean {
    return delegation_mode() == 'ranked';
  }

  get_different_delegation_allowed(_pid?: string): boolean {
    return delegation_mode() == 'different';
  }

  get_weighted_delegation_allowed(_pid?: string): boolean {
    return delegation_mode() == 'weighted';
  }

  // TODO: delv!

  get_example_docs(): Promise<any> {
    if (environment.useMatrixBackend) {
      // For Matrix backend, fetch example polls from static JSON assets
      return firstValueFrom(this.G.http.get<any[]>('assets/examples/index.json'))
        .then(async (index: any[]) => {
          const rows = [];
          for (const entry of index) {
            try {
              const doc = await firstValueFrom(this.G.http.get<any>('assets/examples/' + entry.file));
              doc._id = 'examples§' + entry.file.replace('.json', '');
              rows.push({ doc });
            } catch (e) {
              this.G.L.warn("DataService.get_example_docs: failed to load", entry.file, e);
            }
          }
          return { rows };
        });
    }
    const promise = this.remote_user_db.allDocs({
      include_docs: true,
      startkey: 'examples§',
      endkey: 'examplet',
      inclusive_end: false,
      limit: 50
    });
    return promise;
  }

  // OTHER METHODS:

  private _setp_in_userdb(pid: string, key: string, value: string): boolean {
    // set poll data item in user db:
    value = value || '';
    // construct key for user db:
    const ukey = get_poll_key_prefix(pid) + key;
    this.G.L.trace("DataService._setp_in_userdb", pid, key, value);
    this.user_cache[ukey] = value;
    if (environment.useMatrixBackend) {
      this.schedule_matrix_user_data_write(ukey);
    }
    return this.store_user_data(ukey, this.user_cache, ukey);
  }

  private _setp_in_polldb(pid: string, key: string, value: string): boolean {
    /** Set poll data item in poll db.
     * If necessary, mark the database entry with poll's due date
     * to allow couchdb validating that due date is not passed.
     */ 
     value = value || '';
    this.ensure_poll_cache(pid);
    this.G.L.trace("DataService._setp_in_polldb", pid, key, value);
    this.poll_caches[pid][key] = value;

    // Phase 10: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      this.poll_matrix(pid).then(m => m.setPollData(pid, key, value)).catch(err => {
        this.G.L.error("DataService._setp_in_polldb Matrix sync failed", pid, key, err);
      });
      return true;
    }

    const keystart = key.slice(0, (key+'.').indexOf('.'));
    return this.store_poll_data(pid, key, this.poll_caches[pid], key, 
                                poll_keystarts_requiring_due.includes(keystart));
  }

  private _setv_in_userdb(pid: string, key: string, value: string): boolean {
    // set voter data item in user db:
    value = value || '';
    // construct key for user db:
    const ukey = get_poll_key_prefix(pid) + this.get_voter_key_prefix(pid) + key;
    this.G.L.trace("DataService._setv_in_userdb", pid, key, value);
    this.user_cache[ukey] = value;
    if (environment.useMatrixBackend) {
      this.schedule_matrix_user_data_write(ukey);
    }
    return this.store_user_data(ukey, this.user_cache, ukey);
  }

  setv_global_for_poll(pid: string, key: string, value: string): boolean {
    // set global voter data item in poll db:
    value = value || '';
    // construct key for poll db:
    // return 'voter.' + (vid ? vid : this.getp(pid, 'myvid')) + "§";
    const pkey = "§";
    this.ensure_poll_cache(pid);
    this.G.L.trace("DataService.setv_global_for_poll", pid, key, value);
    this.poll_caches[pid][pkey] = value;
    return this.store_poll_data(pid, pkey, this.poll_caches[pid], pkey, false);
  }

  setv_in_polldb(pid: string, key: string, value: string, vid?: string): boolean {
    /** Set voter data item in poll db.
     * If necessary, mark the database entry with poll's due date
     * to allow couchdb validating that due date is not passed.
     */ 
    value = value || '';
    // construct key for poll db:
    const pkey = this.get_voter_key_prefix(pid, vid) + key;
    this.ensure_poll_cache(pid);
    this.G.L.trace("DataService.setv_in_polldb", pid, key, value);
    this.poll_caches[pid][pkey] = value;

    // Phase 11: Delegate to Matrix if flag is set
    if (environment.useMatrixBackend) {
      const effectiveVid = vid || this.getp(pid, 'myvid');
      this.poll_matrix(pid).then(m => m.setVoterData(pid, effectiveVid, key, value)).catch(err => {
        this.G.L.error("DataService.setv_in_polldb Matrix sync failed", pid, key, err);
      });
      return true;
    }

    const subkeystart = key.slice(0, (key+'.').indexOf('.'));
    return this.store_poll_data(pid, pkey, this.poll_caches[pid], pkey, 
                                voter_subkeystarts_requiring_due.includes(subkeystart));
  }

  private async show_loading() {
    this.G.L.entry("DataService.show_loading");
    this._loading = true;
    // start showing a loading animation which will be dismissed when initialization is finished
    this.loadingElement = await this.loadingController.create({
      spinner: 'crescent'
    });
    // since the previous operation might take some time,
    // only actually present the animation if data is not yet ready:
    if (this._loading && !this._ready) {
      // FIXME: why is the loadingElement not always dismissed?
      // await this.loadingElement.present();     
    }
    if (!this._loading) this.hide_loading();
    this.G.L.exit("DataService.show_loading");
  }
  private hide_loading() {
    if (this.loadingElement) this.loadingElement.dismiss();
    this._loading = false;
  }
  
  fix_url(url:string): string {
    // make sure urls start with http:// or https://
    if (!url) return null;
    return (url.startsWith("http://")||url.startsWith("https://")) ? url : "http://" + url;
  }

  // DBs --> caches:

  private handle_user_db_change(change) {
    // called by PouchDB sync and replicate
//    change = JSON.parse(JSON.stringify(change));
    this.G.L.entry("DataService.handle_user_db_change");
    if (change.deleted){
      this.enqueue_db_change(null, change.doc, true, true);
    } else if (!change.direction || change.direction == 'pull') {
      // sometimes the actual change doc is one level deeper:
      if (change.change) {
        change = change.change;
      }
      for (const doc of change.docs) {
        this.enqueue_db_change(null, doc, !!doc._deleted, true);
      }
      if (change.last_seq) {
        // store last_seq in local storage as reference point for next session's "since" value:
        this.user_cache['user_last_seq'] = change.last_seq;
        this.G.L.trace("DataService.handle_user_db_change stored last_seq", change.last_seq);
      }
    }
    this.G.L.exit("DataService.handle_user_db_change");
  }

  pending_changes = 0; // used for debugging

  private handle_poll_db_change(pid, change, tally=true) {
    // called by PouchDB sync and replicate
    this.G.L.entry("DataService.handle_poll_db_change", pid, this.pending_changes, change);
    if (change.deleted){
      this.G.L.trace("DataService.handle_poll_db_change handling deleted");
      this.enqueue_db_change(pid, change.doc, true, tally);
    } else if (!change.direction || change.direction == 'pull') {
      this.G.L.trace("DataService.handle_poll_db_change handling incoming");
      if (change.change) {
        change = change.change;
      }
      this.G.L.trace("DataService.handle_poll_db_change n_docs, change:", change.docs.length, change);
      for (const doc of change.docs) {
        this.enqueue_db_change(pid, doc, !!doc._deleted, tally);
      }
      if (change.last_seq) {
        // store last_seq in local storage as reference point for next session's "since" value:
        if (this.poll_caches[pid] || (this.uninitialized_pids && this.uninitialized_pids.has(pid))) {
          this.ensure_poll_cache(pid)['last_seq'] = change.last_seq;
          this.G.L.trace("DataService.handle_poll_db_change stored last_seq", change.last_seq);
        }
      }
    }
    this.G.L.exit("DataService.handle_poll_db_change", pid, this.pending_changes);
  }

  // Change-event coalescing (issue #292):

  private enqueue_db_change(pid: string | null, doc: any, deleted: boolean, tally: boolean, confirmed_deletion = false) {
    /** queue an incoming db change doc (pid == null means user db)
     *  for coalesced batch processing. */
    if (this.shutting_down) {
      // teardown has begun; ignore late change events, e.g. from one-shot
      // replications that are not retained and cancelled, so that no timer is
      // recreated and no cache/UI work happens during shutdown (#292):
      this.G.L.trace("DataService.enqueue_db_change ignoring change during shutdown", pid, doc?._id);
      return;
    }
    this.change_queue.push({pid: pid, doc: doc, deleted: deleted, tally: tally, confirmed_deletion: confirmed_deletion});
    if (!this.change_queue_scheduled) {
      this.change_queue_scheduled = true;
      // process on the next tick, so that changes arriving in a burst are
      // coalesced. Deliberately setTimeout and not requestIdleCallback: the
      // latter is not patched by zone.js 0.11, so Angular change detection
      // would no longer be triggered for pages whose onDataChange does not
      // call detectChanges() itself:
      this.change_queue_timeout = setTimeout(() => {
        this.process_change_queue();
      }, 0);
    }
  }

  flush_change_queue(): boolean {
    /** apply all queued changes immediately, e.g. before resolving one-shot
     *  replications whose callers expect an up-to-date cache. */
    if (this.change_queue_timeout != null) {
      clearTimeout(this.change_queue_timeout);
      this.change_queue_timeout = null;
      this.change_queue_scheduled = false;
    }
    let applied_without_drop = true;
    // exhaust the bounded per-document retries synchronously: a conversion
    // failure keeps its entry in change_queue, and process_change_queue(false)
    // deliberately schedules no retry timer, so without this loop such an
    // entry would never reach its remaining attempts or terminal
    // invalidation and a later save_state() could persist the stale
    // cache (#292). Each pass increments the failing entries' retry counts,
    // so at most change_retry_max_attempts passes are needed:
    for (let pass = 0;
         this.change_queue.length > 0 && pass < change_retry_max_attempts;
         pass++) {
      applied_without_drop = this.process_change_queue(false) && applied_without_drop;
    }
    // pending tombstone rechecks mean their resulting changes have not been
    // enqueued yet, so the cache cannot be guaranteed up to date; a recheck
    // whose verification failed terminally leaves a possibly stale cached
    // value behind for the rest of the session (only a restart rebuilds the
    // caches from the local db), so every later flush must keep reporting
    // incompleteness, not only ones overlapping the failure (#292):
    return applied_without_drop && this.change_queue.length == 0
           && this.pending_recheck_count == 0
           && this.recheck_failure_count == 0
           && !this.persisted_cache_invalid;
  }

  flush_change_queue_fully(): Promise<boolean> {
    /** like flush_change_queue(), but additionally waits for pending
     *  asynchronous tombstone rechecks (recheck_deleted_doc) and applies the
     *  changes they enqueue, so that one-shot replications only resolve after
     *  every replicated change – including verified deletions or surviving
     *  winners – has reached the cache (#292): */
    let applied_without_drop = true;
    const step = (): Promise<boolean> => {
      if (this.change_queue_timeout != null) {
        clearTimeout(this.change_queue_timeout);
        this.change_queue_timeout = null;
        this.change_queue_scheduled = false;
      }
      for (let pass = 0;
           this.change_queue.length > 0 && pass < change_retry_max_attempts;
           pass++) {
        applied_without_drop = this.process_change_queue(false) && applied_without_drop;
      }
      if (this.pending_recheck_count > 0) {
        return new Promise<void>(resolve => {
          this.pending_recheck_waiters.push(resolve);
        }).then(step);
      }
      // a terminally failed recheck – whether during this flush or earlier –
      // or any other dropped replicated change means a possibly stale value is
      // still cached for the rest of the session, so a full flush must remain
      // unsuccessful (#292):
      return Promise.resolve(applied_without_drop && this.change_queue.length == 0
                             && this.recheck_failure_count == 0
                             && !this.persisted_cache_invalid);
    };
    return step();
  }

  private recheck_finished() {
    /** a pending tombstone recheck has enqueued its result (or was skipped);
     *  wake up any flush_change_queue_fully() waiting for it: */
    this.pending_recheck_count -= 1;
    if (this.pending_recheck_count == 0) {
      const waiters = this.pending_recheck_waiters;
      this.pending_recheck_waiters = [];
      for (const waiter of waiters) {
        waiter();
      }
    }
  }

  private invalidate_persisted_cache() {
    /** a replicated change was terminally dropped, so the in-memory caches
     *  can be permanently stale while the local PouchDB and the replication
     *  checkpoint already contain the update. Invalidate the persisted state
     *  so the next startup rebuilds the caches from local PouchDB instead of
     *  restoring the stale caches (#292): */
    if (this.persisted_cache_invalid) {
      return;
    }
    this.persisted_cache_invalid = true;
    this.G.L.warn("DataService invalidating persisted caches after a dropped change; next startup will bootstrap from local db");
    if (this.storage) {
      this.storage.remove('state');
    }
  }

  private process_change_queue(schedule_retries: boolean = true): boolean {
    /** process queued db changes, keeping only the newest change per doc id,
     *  then perform the after-changes work (tallying, UI notification) only
     *  once per batch instead of once per change event. */
    this.G.L.entry("DataService.process_change_queue", this.change_queue.length);
    // keep only the latest change for each doc id:
    const latest: {pid: string | null, doc: any, deleted: boolean, tally: boolean, confirmed_deletion?: boolean}[] = [];
    const latest_by_key: Record<string, {pid: string | null, doc: any, deleted: boolean, tally: boolean, confirmed_deletion?: boolean}> = {};
    const seen = new Set<string>();
    for (let i = this.change_queue.length - 1; i >= 0; i--) {
      const entry = this.change_queue[i];
      const key = (entry.pid || '') + '|' + entry.doc._id;
      if (!seen.has(key)) {
        seen.add(key);
        const kept = {pid: entry.pid, doc: entry.doc, deleted: entry.deleted, tally: entry.tally, confirmed_deletion: entry.confirmed_deletion};
        latest.push(kept);
        latest_by_key[key] = kept;
      } else {
        latest_by_key[key].tally = latest_by_key[key].tally || entry.tally;
      }
    }
    latest.reverse();
    this.change_queue = [];
    this.change_queue_scheduled = false;
    this.change_queue_timeout = null;
    const retry_queue: {pid: string | null, doc: any, deleted: boolean, tally: boolean, confirmed_deletion?: boolean}[] = [];
    let dropped_changes = false;
    // collect ids of applied replicated docs for a batched conflict check (#292):
    const conflict_check_ids: Record<string, Set<string>> = {};

    let local_changes = false, tally = false;
    for (let i = 0; i < latest.length; i++) {
      const entry = latest[i];
      const key = (entry.pid || '') + '|' + entry.doc._id;
      if (entry.deleted && !entry.confirmed_deletion && !environment.useMatrixBackend) {
        // A replicated tombstone may just be a deleted losing conflict
        // revision of a doc whose winning revision still exists, so don't
        // drop the cached value yet. Instead re-fetch the id from the local
        // db: if a surviving winner is found it is applied, and only a 404
        // confirms the deletion (#292):
        this.recheck_deleted_doc(entry);
        delete this.change_retry_counts[key];
        continue;
      }
      try {
        if (entry.pid == null) {
          // user db change:
          if (entry.deleted) {
            local_changes = this.handle_deleted_user_doc(entry.doc) || local_changes;
          } else {
            const [value_changed, dummy] = this.doc2user_cache(entry.doc);
            local_changes = value_changed || local_changes;
          }
          tally = true;
        } else {
          // poll db change:
          if (entry.deleted) {
            local_changes = this.handle_deleted_poll_doc(entry.pid, entry.doc) || local_changes;
          } else {
            this.pending_changes += 1;
            try {
              local_changes = this.doc2poll_cache(entry.pid, entry.doc) || local_changes;
            } finally {
              this.pending_changes -= 1;
            }
          }
          tally = tally || entry.tally;
        }
        if (!entry.deleted) {
          const scope = entry.pid || '';
          (conflict_check_ids[scope] = conflict_check_ids[scope] || new Set()).add(entry.doc._id);
        }
        delete this.change_retry_counts[key];
      } catch (err) {
        const attempts = (this.change_retry_counts[key] || 0) + 1;
        if (attempts < change_retry_max_attempts) {
          this.change_retry_counts[key] = attempts;
          retry_queue.push(entry);
          this.G.L.warn("DataService.process_change_queue failed for one change, will retry", attempts, key, err);
        } else {
          dropped_changes = true;
          delete this.change_retry_counts[key];
          this.G.L.error("DataService.process_change_queue dropped repeatedly failing change", key, err);
          this.invalidate_persisted_cache();
        }
      }
    }
    if (retry_queue.length > 0) {
      this.change_queue = this.change_queue.concat(retry_queue);
      if (schedule_retries && !this.change_queue_scheduled) {
        this.change_queue_scheduled = true;
        this.change_queue_timeout = setTimeout(() => {
          this.process_change_queue();
        }, 0);
      }
    }
    if (local_changes) {
      this.after_changes(tally);
      if (this.page && this.page.onDataChange) {
        this.page.onDataChange();
      }
    }
    // background: resolve any conflicts the applied replicated docs may carry (#292):
    this.schedule_conflict_checks(conflict_check_ids);
    this.G.L.exit("DataService.process_change_queue");
    return !dropped_changes;
  }

  private recheck_deleted_doc(entry: {pid: string | null, doc: any, deleted: boolean, tally: boolean}) {
    /** A tombstone arrived via replication. It may only be the deletion of a
     *  losing conflict revision performed by another replica's conflict
     *  cleanup, in which case a winning revision of the same id still exists
     *  and the cached value must not disappear. So re-fetch the id and apply
     *  the surviving winner; treat it as a real deletion only when the get
     *  returns 404 (#292): */
    const db = entry.pid == null ? this.local_synced_user_db : this.local_poll_dbs[entry.pid];
    if (!db || typeof db.get != 'function') {
      // no local db to verify against, so apply the deletion as-is:
      this.enqueue_db_change(entry.pid, entry.doc, true, entry.tally, true);
      return;
    }
    // track this recheck so flush_change_queue_fully() can wait for the
    // change it will enqueue before one-shot replications resolve (#292):
    this.pending_recheck_count += 1;
    const attempt_get = (attempt: number) => {
      db.get(entry.doc._id)
      .then(doc => {
        this.G.L.trace("DataService.recheck_deleted_doc winning revision survives tombstone, applying it", entry.pid, entry.doc._id);
        this.enqueue_db_change(entry.pid, doc, !!doc._deleted, entry.tally, true);
        this.recheck_finished();
      })
      .catch(err => {
        if (err && err.status == 404) {
          // the doc is really gone, apply the deletion:
          this.enqueue_db_change(entry.pid, entry.doc, true, entry.tally, true);
          this.recheck_finished();
        } else if (attempt < change_retry_max_attempts) {
          // a transient read failure is no proof of deletion – a winning
          // revision may still exist – so retry the read instead of
          // removing possibly valid cached data (#292):
          this.G.L.warn("DataService.recheck_deleted_doc could not verify tombstone, will retry", entry.pid, entry.doc._id, attempt, err);
          window.setTimeout(() => attempt_get(attempt + 1), environment.db_put_retry_delay_ms);
        } else {
          // verification is exhausted. Keep the cached value rather than
          // applying an unverified deletion, mark the current flush as
          // incomplete, and invalidate the persisted cache so the next
          // startup rebuilds the caches from the local db (#292):
          this.G.L.error("DataService.recheck_deleted_doc could not verify tombstone, keeping cached value", entry.pid, entry.doc._id, err);
          this.recheck_failure_count += 1;
          this.invalidate_persisted_cache();
          this.recheck_finished();
        }
      });
    };
    attempt_get(1);
  }

  private handle_deleted_user_doc(doc): boolean {
    const _id = doc._id;
    if (_id.includes("§")) {
      const key = _id.slice(_id.indexOf("§") + 1);
      if (key in this.user_cache) {
        this.G.L.trace("DataService.handle_user_db_change deleting", key);
        delete this.user_cache[key];
        // also remove derived state indexed under this key, so that e.g.
        // absent del_incoming.* docs no longer appear as unanswered requests
        // and deleted news/poll metadata are no longer indexed (#292):
        if (key.startsWith("news.")) {
          this.news_keys.delete(key);
        } else if (key.startsWith("del_incoming.")) {
          delete this.incoming_dids_caches[key.slice("del_incoming.".length)];
        } else if (key.startsWith("poll.")) {
          // deregister poll/option metadata analogously to delp():
          const rest = key.slice("poll.".length),
                pid = rest.slice(0, rest.indexOf('.')),
                pkey = rest.slice(pid.length + 1);
          if (pkey == "title") {
            this._pids.delete(pid);
            delete this._pid_oids[pid];
          } else if (pkey.startsWith('option.') && pkey.endsWith('.name') && (pid in this._pid_oids)) {
            const keyend = pkey.slice('option.'.length),
                  oid = keyend.slice(0, keyend.indexOf('.'));
            this._pid_oids[pid].delete(oid);
          }
        }
        return true;    
      }  
    }
    return false;
  }

  private handle_deleted_poll_doc(pid: string, doc): boolean {
    this.G.L.entry("DataService.handle_deleted_poll_doc", pid, doc);
    if (!(pid in this.poll_caches)) {
      return false;
    }
    const _id = doc._id;
    if (_id.includes(pid)) {
      const key = _id.slice(_id.indexOf(pid) + pid.length + 1);
      if (key.startsWith('voter.') && key.includes('§del_request.')) {
        const keyfromvid = key.slice('voter.'.length),
              vid = keyfromvid.slice(0, keyfromvid.indexOf("§")),
              subkey = keyfromvid.slice(vid.length + 1);
        const did = subkey.slice("del_request.".length);
        this.G.Del.process_deleted_request_from_db(pid, did, vid);
      } else if (key.startsWith('voter.') && key.includes('§rating.')) {
        const keyfromvid = key.slice('voter.'.length),
              vid = keyfromvid.slice(0, keyfromvid.indexOf("§")),
              subkey = keyfromvid.slice(vid.length + 1);
        const oid = subkey.slice("rating.".length);
        this.G.P.update_own_rating(pid, vid, oid, 0, false);
      }  
      if (key in this.poll_caches[pid]) {
        this.G.L.trace("DataService.handle_poll_db_change deleting", key);
        delete this.poll_caches[pid][key];
        return true;    
      }  
    }
    return false;
  }

  // Conflict detection & deterministic resolution (issue #292):

  private may_cleanup_conflicts(pid: string | null, _id: string): boolean {
    /** Whether this client's replication credentials are authorized to delete
     *  losing conflict revisions of the doc also in the *remote* db. The
     *  server-side validator rejects any update or deletion of existing
     *  shared poll docs (it cannot verify that a revision is a losing branch,
     *  see validate_doc_update.js), so deleting such losers only locally
     *  would diverge from the remote db and the conflict would reappear on
     *  the next pull. Shared poll docs and other voters' docs are therefore
     *  skipped; that is safe because CouchDB's winner selection is
     *  deterministic, so all replicas read the same winning revision even
     *  while the conflict lingers (#292). */
    if (pid == null) {
      // user db: all docs are owned by this user's credentials:
      return true;
    }
    // poll db: only this client's own voter docs may be deleted remotely:
    return _id.startsWith("~vodle.poll." + pid + ".voter." + this.getp(pid, 'myvid') + "§");
  }

  private resolve_doc_conflicts(db, doc, pid: string | null = null): Promise<boolean> {
    /** Deterministically resolve a conflicted document by deleting all losing
     *  revisions. CouchDB/PouchDB pick the winning revision deterministically,
     *  so removing the losers makes every replica converge on the same
     *  winning revision (#292). */
    if (!doc || !doc._conflicts || doc._conflicts.length == 0) {
      return Promise.resolve(false);
    }
    if (!this.may_cleanup_conflicts(pid, doc._id)) {
      this.G.L.trace("DataService.resolve_doc_conflicts skipping doc not deletable with our credentials", doc._id);
      return Promise.resolve(false);
    }
    this.G.L.warn("DataService.resolve_doc_conflicts resolving conflicted doc", doc._id, doc._conflicts);
    const removals = doc._conflicts.map(rev =>
      db.remove(doc._id, rev)
      .catch(err => {
        if (err && (err.status == 404 || err.status == 409)) {
          // another client may have resolved this revision concurrently:
          this.G.L.trace("DataService.resolve_doc_conflicts couldn't remove losing revision", doc._id, rev, err);
        } else {
          this.G.L.warn("DataService.resolve_doc_conflicts unexpected error removing losing revision", doc._id, rev, err);
        }
      })
    );
    return Promise.all(removals).then(() => true);
  }

  private check_docs_for_conflicts(db, label: string, ids?: string[], pid: string | null = null): Promise<number> {
    /** Fetch docs (all of them, or only the given ids) with conflict metadata
     *  and resolve any conflicts found. Used as a background scan at startup
     *  and as a batched check for docs that arrived via replication (#292).
     *  Never rejects. */
    if (!db) {
      return Promise.resolve(0);
    }
    const options: any = {include_docs: true, conflicts: true};
    if (ids) {
      if (ids.length == 0) {
        return Promise.resolve(0);
      }
      options.keys = ids;
    }
    return db.allDocs(options)
    .then(result => {
      const conflicted = (result.rows || []).filter(row => 
        row.doc && row.doc._conflicts && row.doc._conflicts.length > 0);
      if (conflicted.length == 0) {
        return 0;
      }
      this.G.L.info("DataService.check_docs_for_conflicts found conflicted docs", label, conflicted.length);
      return Promise.all(conflicted.map(row => this.resolve_doc_conflicts(db, row.doc, pid)))
      .then(() => conflicted.length);
    })
    .catch(err => {
      this.G.L.warn("DataService.check_docs_for_conflicts failed", label, err);
      return 0;
    });
  }

  private scan_user_db_for_conflicts() {
    /** background startup pass over the whole local user db (#292): */
    if (environment.useMatrixBackend || this.shutting_down) {
      return;
    }
    this.check_docs_for_conflicts(this.local_synced_user_db, 'user');
  }

  private scan_poll_db_for_conflicts(pid: string) {
    /** background startup pass over a whole local poll db (#292): */
    if (environment.useMatrixBackend || this.shutting_down) {
      return;
    }
    this.check_docs_for_conflicts(this.get_local_poll_db(pid), 'poll ' + pid, undefined, pid);
  }

  private schedule_conflict_checks(ids_by_scope: Record<string, Set<string>>) {
    /** batched follow-up conflict checks for docs that were just applied from
     *  replication change events ('' scope means the user db) (#292): */
    if (environment.useMatrixBackend || this.shutting_down) {
      return;
    }
    for (const scope of Object.keys(ids_by_scope)) {
      const ids = [...ids_by_scope[scope]];
      if (scope == '') {
        this.check_docs_for_conflicts(this.local_synced_user_db, 'user', ids);
      } else if (scope in this.local_poll_dbs) {
        this.check_docs_for_conflicts(this.local_poll_dbs[scope], 'poll ' + scope, ids, scope);
      }
    }
  }

  private after_changes(tally=true) {
    this.G.L.entry("DataService.after_changes");
    const lang = this.getu('language'),
          used_lang = lang!=''?lang:environment.default_lang;
    this.translate.use(used_lang);
    this.document.documentElement.lang = used_lang; 

    // process all known pids and, if necessary, generate Poll objects and connect to remote poll dbs,
    // or if due is long over, delete:
    for (const pid of new Set(this._pids)) {
      if (this.uninitialized_pids.has(pid)) { continue; }
      this.G.L.info("DataService.after_changes processing poll", pid);
      // get due:
      const due_str = this.G.D.getp(pid, 'due'),
            deletion_date = (due_str == '') ? null : 
              new Date((new Date(due_str)).getTime() + environment.polls.delete_after_days*24*60*60*1000);
      // archived polls are kept (issue #83):
      if (!!deletion_date && (new Date()) >= deletion_date && this.getp(pid, 'is_archived') != 'true') {
        // poll data shall be deleted locally
        this.G.L.debug("DataService.after_changes deleting old poll data", pid, due_str);
        this.stop_poll_sync(pid);
        if (environment.useMatrixBackend) {
          // and its rooms on the homeserver are left (#331):
          this.poll_matrix(pid).then(m => m.leavePollRooms(pid))
            .then(() => this.close_poll_matrix(pid)).catch(err => {
            this.G.L.warn("DataService.after_changes could not leave the poll's rooms", pid, err);
          });
        }
        const expired_poll = this.G.P.polls[pid];
        if (!!expired_poll) {
          // cancel any pending deferred-finalization retry timer and remove
          // the Poll object, so no later end() retry can run against the
          // destroyed local db (#292):
          expired_poll.cancel_end_retry();
          delete this.G.P.polls[pid];
        }
        const lpdb = this.get_local_poll_db(pid);
        if (!!lpdb) {
          lpdb.destroy();
        }
        delete this.local_poll_dbs[pid];
        if (pid in this.remote_poll_dbs) {
          delete this.remote_poll_dbs[pid];
        }
        if (pid in this._pid_oids) {
          delete this._pid_oids[pid];
        }
        for (const key of poll_keystarts_in_user_db) {
          this.delp(pid, key);
        }
        this._pids.delete(pid);
      } else {
        // poll data shall not be deleted.
        if (!(pid in this.G.P.polls)) {
          // poll object does not exist yet, so create it:
          this.G.L.debug("DataService.after_changes creating poll object", pid);
          const p = new Poll(this.G, pid);
        }
        if (!environment.useMatrixBackend && !this.pid_is_draft(pid)
            && this.draft_migration_pending(pid)) {
          // an earlier draft→running data migration was interrupted or some
          // of its confirmed writes (e.g. the due document) terminally
          // failed; retry moving the leftover user db copies now (#292):
          this.G.L.debug("DataService.after_changes retrying incomplete draft→running data migration", pid);
          this.move_draft_data_to_poll_db(pid);
        }
        if (!this.pid_is_draft(pid) && !(pid in this.remote_poll_dbs) && !(environment.useMatrixBackend && this._matrixPollListeners[pid])) {
          // try syncing with remote db:
          // check if db credentials are set:
          if (this.poll_has_db_credentials(pid)) {
            this.G.L.trace("DataService.after_changes found remote poll db credentials");
  
            // ASYNC:
            // connect to remote and start sync:
            this.connect_to_remote_poll_db(pid, this.need_poll_db_replication[pid] || false)
            .catch(err => {
  
              this.G.L.warn("DataService.after_changes couldn't start poll db syncing", pid, err);
              // TODO: react somehow?
  
            });
  
          } else {
  
            this.G.L.warn("DataService.after_changes couldn't find remote poll db credentials", pid);
            // TODO: react somehow?
  
          }
        }  
      }
    }

    // process all known oids and, if necessary, generate Option objects:
    for (const pid in this._pid_oids) {
      if (this.uninitialized_pids.has(pid)) { continue; }
      const oids = this._pid_oids[pid];
      for (const oid of oids) {
        if (pid in this.G.P.polls) {
          const p = this.G.P.polls[pid];
          if (!p.oids.includes(oid)) {
            // option object does not exist yet, so create it:
            this.G.L.trace("DataService.after_changes creating Option object", oid);
            const o = new Option(this.G, p, oid);
          }  
        } else {
          this.G.L.error("DataService.after_changes found an option for an unknown poll", pid, oid);
        }
      }
    }

    // notifty all running polls that they might need to tally:
    for (const [pid, p] of Object.entries(this.G.P.polls)) {
      if (this.uninitialized_pids.has(pid)) { continue; }
      this.G.L.trace("DataService.after_changes telling poll to tally", pid);
      p.ratings_have_changed = true;
      p.after_incoming_changes(tally);
    }

    // a poll that started or was joined gets its email subscription
    this.G.E?.schedule_sync();

    this.save_state();
    this.G.L.exit("DataService.after_changes");
  }

  private poll_has_db_credentials(pid:string) {
    // Phase 12: Matrix backend handles auth differently — always has "credentials"
    if (environment.useMatrixBackend) {
      return true;
    }
    // return whether poll db credentials are nonempty:
    return this.getp(pid, 'db_server_url')!='' && this.getp(pid, 'db_password')!='' && this.getp(pid, 'myvid')!='';
  }

  private ensure_poll_cache(pid:string) {
    let cache = this.poll_caches[pid];
    if (!cache) {
      cache = this.poll_caches[pid] = {};
    }
    return cache;
  }

  private doc2user_cache(doc): [boolean, boolean] {
    // populate user cache with key, value from doc
    const _id = doc._id, prefix = user_doc_id_prefix + this.get_email_and_pw_hash() + "§";
    if (_id.includes('§timestamp') || _id === '_design/vodle') {
      return [false, false];
    }

    if (_id.startsWith(prefix)) {

      const key = _id.slice(prefix.length, _id.length);
      let value_changed = false, initializing_poll = false;
      const cyphertext = doc['value'];
      this.G.L.trace("DataService.doc2user_cache cyphertext is", cyphertext);

      if (cyphertext) {

        // extract value and store in cache if changed:
        const value = user_keys_unencrypted.includes(key) ? cyphertext : decrypt(cyphertext, this.user_cache['password']);
        if (this.user_cache[key] != value) {
          const had_key = key in this.user_cache, old_value = this.user_cache[key];
          this.user_cache[key] = value;

          try {
            if (key.startsWith("news.")) {
              this.G.L.trace("DataService.doc2user_cache news", key);
              this.news_keys.add(key);
            } else if (key.startsWith("del_incoming.")) {
              this.G.L.trace("DataService.doc2user_cache incoming did", key);
              this.incoming_dids_caches[key.slice("del_incoming.".length)] = JSON.parse(value); 
            }
          } catch (err) {
            // roll back the partial cache mutation so that a retry of this
            // change reruns the failed side effect (#292):
            if (had_key) {
              this.user_cache[key] = old_value;
            } else {
              delete this.user_cache[key];
            }
            throw err;
          }
          value_changed = true;

        }
        this.G.L.trace("DataService.doc2user_cache key, value", key, value);

        if (this.check_whether_poll_or_option(key, value)) {
          initializing_poll = true;
        }

      } else {

        this.G.L.debug("DataService.doc2user_cache got corrupt doc", JSON.stringify(doc));

      }

      // RETURN whether the value actually changed.
      return [value_changed, initializing_poll];

    } else {

      this.G.L.error("DataService.doc2user_cache got corrupt doc _id", _id);
      // RETURN:
      return [false, false];

    }
  }

  private check_whether_poll_or_option(key:string, value:string): boolean {
    let initializing_poll = false;

    if (key.startsWith('poll.') && key.endsWith('.state')) {

      // it's a poll's state entry, so check whether we know this poll:
      const pid = key.slice('poll.'.length, key.indexOf('.state')), state = value;
      if (!this._pids.has(pid)) {
        this.G.L.trace("DataService.check_whether_poll_or_option found new poll", pid);
        if (state == 'draft') {
          this._pids.add(pid);
        } else if (environment.useMatrixBackend) {
          // no local PouchDB on this backend: the poll's data lives in its
          // Matrix room and the cache is filled from there. Going through
          // ensure_local_poll_data would create a `local_poll_<pid>`
          // database per poll, read it three times (info, allDocs, changes)
          // and hold `_ready` false until those reads of an always-empty
          // database came back — every start, for every poll the user room
          // names (#327).
          this.ensure_matrix_poll_known(pid);
        } else {
          this.ensure_local_poll_data(pid);
          initializing_poll = true;
        }
      }

    } else if (key.startsWith('poll.') && key.includes('.option.') && key.endsWith('.name')) {

      // it's an option's oid entry, so check whether we know this option:
      const pid = key.slice('poll.'.length, key.indexOf('.option.')), 
          oid = key.slice(key.indexOf('.option.') + '.option.'.length, key.indexOf('.name'));
      if (!(pid in this._pid_oids)) {
        this._pid_oids[pid] = new Set();
      }
      if (!this._pid_oids[pid].has(oid)) {
        this.G.L.trace("DataService found new option", pid, oid);
        this._pid_oids[pid].add(oid);
        if (pid in this.G.P.polls) {
          const o = new Option(this.G, this.G.P.polls[pid], oid);              
        }
      }

    }
    return initializing_poll;
  }

  private doc2poll_cache(pid: string, doc): boolean {
    /** Copy data from an incoming JSON doc to a poll cache. */

    this.G.L.entry("DataService.doc2poll_cache", pid, doc._id);

    const _id = doc._id;
    if (_id.includes('§timestamp') || _id === '_design/vodle') {
      return false;
    }

    const poll_doc_prefix = poll_doc_id_prefix + pid + "§",
          voter_doc_prefix = poll_doc_id_prefix + pid + '.',
          cache = this.ensure_poll_cache(pid);
    var key, value_changed;

    // check if doc contains a claimed due date:
    const doc_due = doc['due'];
    if (doc_due) {
      // check if it is correct:
      if (!!cache['due'] && !(doc_due == cache['due'])) {
        this.G.L.warn("DataService.doc2poll_cache received doc with wrong due", doc, this.poll_caches[pid]['due']);

        // RETURN:
        return false;
      }
    }
    value_changed = false;
    const cyphertext = doc['value'];
    this.G.L.trace("DataService.doc2poll_cache cyphertext is", cyphertext);

    if (cyphertext) {

      // extract value:
      const value = _id.endsWith('§due') ? cyphertext : decrypt(cyphertext, this.user_cache[get_poll_key_prefix(pid) + 'password']);

      // extract key depending on doc type:
      if (_id.startsWith(poll_doc_prefix)) {

        // it's a non-voter poll doc.
        key = _id.slice(poll_doc_prefix.length, _id.length);

        // check if doc should contain a claimed due data for validation:
        const keystart = key.slice(0, (key+'.').indexOf('.'));
        if (poll_keystarts_requiring_due.includes(keystart) && !doc_due) {
          this.G.L.warn("DataService.doc2poll_cache received doc missing necessary due date", doc);

          // RETURN:
          return false;  
        }

        if (key == 'state') {
          // also set state in user db:
          this.setu(get_poll_key_prefix(pid) + 'state', value);
          if (pid in this.G.P.polls) {
            // also update poll's internal state cache:
            this.G.P.polls[pid]._state = value;
          }
        }

        if (key.startsWith('option.') && key.endsWith('.name')) {

          // it's an option's oid entry, so check whether we know this option:
          const keyend = key.slice('option.'.length), 
              oid = keyend.slice(0, keyend.indexOf('.'));
          if (!(pid in this._pid_oids)) {
            this._pid_oids[pid] = new Set();
          }
          if (!this._pid_oids[pid].has(oid)) {
            this.G.L.trace("DataService.doc2poll_cache found new option", pid, oid);
            this._pid_oids[pid].add(oid);
            if (pid in this.G.P.polls) {
              const o = new Option(this.G, this.G.P.polls[pid], oid);              
            }
          }

        }

        // store in cache if changed:
        if (cache[key] != value) {
          cache[key] = value;
          value_changed = true;
        }  

      } else if (_id.startsWith(voter_doc_prefix)) {

        // it's a voter doc.
        key = _id.slice(voter_doc_prefix.length, _id.length);

        const keyfromvid = key.slice('voter.'.length),
              vid = keyfromvid.slice(0, keyfromvid.indexOf("§")),
              subkey = keyfromvid.slice(vid.length + 1);

        // check if doc should contain a claimed due data for validation:
        const subkeystart = subkey.slice(0, (subkey+'.').indexOf('.'));
        if (voter_subkeystarts_requiring_due.includes(subkeystart) && !doc_due) {
          this.G.L.warn("DataService.doc2poll_cache received doc missing necessary due date", doc);

          // RETURN:
          return false;  
        }

        this.G.L.trace("DataService.doc2poll_cache voter data item", pid, vid, subkey, value);

        // if changed, store in cache and postprocess:
        if (cache[key] != value) {
          const had_key = key in cache, old_value = cache[key];
          cache[key] = value;

          try {
            if (subkey.startsWith("rating.")) {
              const oid = subkey.slice("rating.".length), r = Number.parseInt(value);
              this.G.P.update_own_rating(pid, vid, oid, r, false);
            } else if (subkey.startsWith("del_request.")) {
              const did = subkey.slice("del_request.".length);
              this.G.Del.process_request_from_db(pid, did, vid);
            } else if (subkey.startsWith("del_response.")) {
              const did = subkey.slice("del_response.".length);
              this.G.Del.process_signed_response_from_db(pid, did, vid);
            }
          } catch (err) {
            // roll back the partial cache mutation so that a retry of this
            // change reruns the failed side effect (#292):
            if (had_key) {
              cache[key] = old_value;
            } else {
              delete cache[key];
            }
            throw err;
          }
          value_changed = true;
        }  

      } else {

        // it's neither.
        this.G.L.error("DataService.doc2poll_cache got corrupt doc _id", pid, _id);
        this.G.L.exit("DataService.doc2poll_cache false");

        // RETURN:
        return false;

      }
      this.G.L.trace("DataService.doc2poll_cache key, value", pid, key, value);

    } else {

      this.G.L.warn("DataService.doc2poll_cache got corrupt doc", pid, JSON.stringify(doc));

    }

    // returns whether the value actually changed.
    this.G.L.exit("DataService.doc2poll_cache value_changed", pid, value_changed);

    // RETURN:
    return value_changed;
  }

  // caches --> DBs:

  private store_all_userdata() {
    // stores user_cache in suitable DBs. 
    for (const [ukey, value] of Object.entries(this.user_cache)) {
      this.store_user_data(ukey, this.user_cache, ukey);
    }
  }

  private store_all_polldata(pid:string) {
    // stores poll_cache[pid] in suitable DBs. 
    for (const [key, value] of Object.entries(this.poll_caches[pid])) {
      this.store_poll_data(pid, key, this.poll_caches[pid], key);
    }
  }

  private store_user_data(key:string, dict, dict_key:string, enforce=false): boolean {
    // stores key and value = dict[dict_key] in user database. 
    this.G.L.trace("DataService.store_user_data", key, dict[dict_key]);
    var doc;

    if (!this.G.S.consent) return false;
    const voter_source = this.voter_source_parts(key);
    if (!environment.useMatrixBackend && voter_source) {
      // Only newly admitted draft writes may create a source. Replayed stale
      // cache rows and bulk saves must not recreate a retired running-poll copy.
      if (!this.pid_is_draft(voter_source.pid)) { return false; }
      const hash = this.get_email_and_pw_hash(), password = this.user_cache['password'];
      if (!hash || !password || !(dict_key in dict) || this.shutting_down) { return false; }
      const db = this.local_synced_user_db;
      const id = user_doc_id_prefix + hash + '§' + key;
      const value = dict[dict_key] || '';
      this.queue_voter_mutation(key, async is_current => {
        await this.retry_voter_mutation(async () => {
          const doc = await this.get_existing_doc(db, id);
          this.assert_voter_mutation_current(is_current);
          if (doc && decrypt(doc.value, password) === value) { return; }
          await db.put({...doc, _id: id, value: encrypt(value, password)});
        }, is_current);
      }).catch(err => {
        if (!err?.vodle_move_aborted) {
          this.G.L.error("DataService.store_user_data voter write failed", key, err);
        }
      });
      return true;
    }

    if (local_only_user_keys.includes(key)) {

      // ASYNC:
      // simply use key as doc id and don't encrypt:
      this.local_only_user_DB.get(key)
      .then(doc => {
        // key existed in db, so update:

        const value = dict[dict_key];
        if (enforce || doc.value != value) {
          doc.value = value;
          this.local_only_user_DB.put(doc)
          .then(() => {
            this.G.L.trace("DataService.store_user_data local-only update", key, value);
          })
          .catch(err => {
            this.G.L.warn("DataService.store_user_data couldn't local-only update, will try again soon", key, value, doc, err);
            window.setTimeout(this.store_user_data.bind(this), environment.db_put_retry_delay_ms, key, dict, dict_key, enforce=true);
          });
        } else {
          this.G.L.trace("DataService.store_user_data local-only no need to update", key, value);
        }

      }).catch(err => {

        // key did not exist in db, so add:
        const value = dict[dict_key];
        doc = {_id: key, value: value};
        this.local_only_user_DB.put(doc)
        .then(response => {
          this.G.L.trace("DataService.store_user_data local-only new", key, value);
        })
        .catch(err => {
          this.G.L.warn("DataService.store_user_data couldn't local-only new", key, value, doc, err);
          this.local_only_user_DB.get(key)
          .then(doc => {
            this.G.L.warn("DataService.store_user_data will try again soon", key, value, doc, err);
            window.setTimeout(this.store_user_data.bind(this), environment.db_put_retry_delay_ms, key, dict, dict_key, enforce=true);
          });
        });

      });

    } else {

      // store encrypted with suitable owner prefix in doc id:
      const email_and_pw_hash = this.get_email_and_pw_hash();
      if (!email_and_pw_hash) {
        this.G.L.warn("DataService.store_user_data couldn't set "+key+" since email or password are missing!");

        // RETURN:
        return false;
      }
      const _id = user_doc_id_prefix + email_and_pw_hash + "§" + key, 
          user_pw = this.user_cache['password'];

      // ASYNC:
      this.local_synced_user_db.get(_id)
      .then(doc => {

        // key existed in db, so update:
        const value = dict[dict_key], 
              enc_value = user_keys_unencrypted.includes(key) ? value : encrypt(value, user_pw),
              old_value = user_keys_unencrypted.includes(key) ? doc.value : decrypt(doc.value, user_pw);
        if (old_value != value) {
          doc.value = enc_value;
          this.local_synced_user_db.put(doc)
          .then(response => {
            this.G.L.trace("DataService.store_user_data synced update", key, value);
          })
          .catch(err => {
            this.G.L.warn("DataService.store_user_data couldn't synced update, will try again soon", key, value, err);
            window.setTimeout(this.store_user_data.bind(this), environment.db_put_retry_delay_ms, key, dict, dict_key);
          });
        } else {
          this.G.L.trace("DataService.store_user_data synced no need to update", key, value, old_value);
        }

      }).catch(err => {

        // key did not exist in db, so add:
        const value = dict[dict_key], 
              enc_value = user_keys_unencrypted.includes(key) ? value : encrypt(value, user_pw);
        doc = {
          '_id': _id, 
          'value': enc_value,
        };
        this.local_synced_user_db.put(doc)
        .then(response => {
          this.G.L.trace("DataService.store_user_data synced new", key, value);
        })
        .catch(err => {
          this.G.L.warn("DataService.store_user_data couldn't synced new, will try again soon", key, value, err);
          window.setTimeout(this.store_user_data.bind(this), environment.db_put_retry_delay_ms, key, dict, dict_key);
        });  

      });
    }

    // RETURN:
    return true;
  }

  private store_poll_data(pid:string, key:string, dict, dict_key:string, add_due=false, enforce=false): boolean {
    // stores key and value in poll database. 
    this.G.L.trace("DataService.store_poll_data", pid, key, dict[dict_key]);
    var doc;

    if (!this.G.S.consent) return false;

    // see what type of entry it is:
    if (key.indexOf("§") == -1) {

      // it's a non-voter data item.

      // store encrypted and with correct prefix:
      const _id = poll_doc_id_prefix + pid + "§" + key,
            poll_pw = this.user_cache[get_poll_key_prefix(pid) + 'password'];
      if ((poll_pw=='') || (!poll_pw)) {
        this.G.L.warn("DataService.store_poll_data couldn't set "+key+" in local_poll_DB since poll password is missing!");

        // RETURN:
        return false;
      }
      const db = this.get_local_poll_db(pid);

      // ASYNC:
      db.get(_id)
      .then(doc => {

        // key existed in poll db, check whether update is allowed.
        const value = dict[dict_key];
        const enc_value = encrypt(value, poll_pw);
        if ((key != 'due') && (key != 'state') && (decrypt(doc.value, poll_pw) != value)) {
          // this is not allowed for poll docs!
          this.G.L.error("DataService.store_poll_data tried changing an existing poll data item", pid, key, value);
        } else if ((key == 'due') && (doc.due != value)) {
          // this is not allowed for poll docs!
          this.G.L.error("DataService.store_poll_data tried changing due time", pid, key, value);
        } // TODO: also check state change against due time!

        // now update:
        if (enforce || decrypt(doc['value'], poll_pw) != value) {
          // only due value is stored unencrypted:
          doc['value'] = (key == 'due') ? value : enc_value;
          if (add_due) {
            doc['due'] = this.poll_caches[pid]['due'];
          }
          db.put(doc)
          .then(response => {
            this.G.L.trace("DataService.store_poll_data update", pid, key, value, doc);
          })
          .catch(err => {
            this.G.L.warn("DataService.store_poll_data couldn't update", pid, key, value, doc, err);
          });
        } else {
          this.G.L.trace("DataService.store_poll_data no need to update", pid, key, value);
        }
        
      }).catch(err => {

        doc = {
          '_id': _id,
        };
        const value = dict[dict_key];
        const enc_value = encrypt(value, poll_pw);
        doc['value'] = (key == 'due') ? value : enc_value;
        if (add_due) {
          doc['due'] = this.poll_caches[pid]['due'];
        }
        db.put(doc)
        .then(response => {
          this.G.L.trace("DataService.store_poll_data new", pid, key, value, doc);
        })
        .catch(err => {
          this.G.L.warn("DataService.store_poll_data couldn't new", pid, key, value, doc, err);
          // if doc exists, try again:
          db.get(_id)
          .then(doc => {
            window.setTimeout(this.store_poll_data.bind(this), environment.db_put_retry_delay_ms, pid, key, dict, dict_key, add_due, true);
            this.G.L.trace("DataService.store_poll_data scheduled new try", pid, key, value, doc, err);
          });
        });  
      });

      // RETURN:
      return true;

    } else {

      // it's a voter data item.

      // check which voter's data this is:
      const vid_prefix = key.slice(0, key.indexOf("§")),
            vid = this.user_cache[get_poll_key_prefix(pid) + 'myvid'];
      if (vid_prefix != 'voter.' + vid && this.poll_caches[pid]['is_test']!='true') {
          // it is not allowed to alter other voters' data!
          this.G.L.error("DataService.store_poll_data tried changing another voter's data item", pid, key);

          // RETURN: 
          return false;
      }

      const _id = poll_doc_id_prefix + pid + '.' + key,
          poll_pw = this.user_cache[get_poll_key_prefix(pid) + 'password'];
      if ((poll_pw=='')||(!poll_pw) || (vid=='')||(!vid)) {
        this.G.L.warn("DataService.store_poll_data couldn't set voter data "+key+" in local_poll_DB since poll password is missing!");

        // RETURN:
        return false;
      }

      const value = dict[dict_key];
      const db = this.get_local_poll_db(pid);
      if (!(dict_key in dict) || this.shutting_down) { return false; }
      const due = this.poll_caches[pid]['due'];
      this.queue_voter_mutation(get_poll_key_prefix(pid) + key, async is_current => {
        await this.retry_voter_mutation(async () => {
          const existing = await this.get_existing_doc(db, _id);
          this.assert_voter_mutation_current(is_current);
          if (!enforce && existing && decrypt(existing.value, poll_pw) === value
              && (!add_due || existing.due === due)) { return; }
          const updated = {...existing, _id, value: encrypt(value, poll_pw)};
          if (add_due) { updated.due = due; }
          await db.put(updated);
        }, is_current);
      }).catch(err => {
        if (!err?.vodle_move_aborted) {
          this.G.L.error("DataService.store_poll_data voter write failed", pid, key, err);
        }
      });
      return true;
    }
  }

  private store_poll_data_confirmed(pid: string, key: string, value: string, add_due = false,
                                    overwrite = true, still_wanted: () => boolean = () => true): Promise<void> {
    /** Store a poll data item in the local poll db and resolve only once the
     *  write is confirmed, retrying a bounded number of times. Used for
     *  transactional draft→running data moves, where the user db copy may
     *  only be deleted after the poll db write has been confirmed (#292).
     *  With overwrite disabled, an existing doc with a different value counts
     *  as confirmed too, so that a retried migration cannot clobber a newer
     *  write that happened after the poll started running.
     *  For deadline-stamped docs (add_due, i.e. options and voter data),
     *  confirmation additionally requires the destination doc to be
     *  acknowledged by the remote poll db, because the server validator
     *  permanently rejects such docs once the poll's due date has passed
     *  (validate_doc_update.js): a merely local write could otherwise let the
     *  fallback be deleted although the doc can never replicate. If the due
     *  date has passed and the doc verifiably never reached the server, the
     *  unpublishable local copy is withdrawn and the promise rejects with a
     *  vodle_publication_expired flag so the caller can drop the fallback.
     *  still_wanted is consulted before each write, so a move can be aborted
     *  (vodle_move_aborted) when the item was deleted while in flight. */
    value = value || '';
    const is_voter_key = key.indexOf("§") >= 0,
          _id = poll_doc_id_prefix + pid + (is_voter_key ? '.' : '§') + key,
          poll_pw = this.user_cache[get_poll_key_prefix(pid) + 'password'];
    if ((poll_pw == '') || (!poll_pw)) {
      return Promise.reject(new Error("DataService.store_poll_data_confirmed missing poll password for " + pid));
    }
    const db = this.get_local_poll_db(pid);
    const assert_wanted = () => {
      if (this.shutting_down || !still_wanted()) {
        const aborted: any = new Error("DataService.store_poll_data_confirmed aborting move of " + _id);
        aborted.vodle_move_aborted = true;
        throw aborted;
      }
    };
    const publish_confirmed = (doc) => {
      assert_wanted();
      if (!overwrite) {
        if (is_voter_key) {
          // Use the same rating/delegation processing as incoming documents;
          // a local migration write will only produce ignored push events.
          this.cache_confirmed_voter_doc(pid, key, doc);
        } else {
          this.ensure_poll_cache(pid)[key] = key == 'due' ? doc.value : decrypt(doc.value, poll_pw);
        }
      }
    };
    // the state doc also carries a due field but is exempt from the server's
    // deadline validation, so only option/voter docs need a remote ack:
    const deadline_constrained = add_due && key != 'state' && !environment.useMatrixBackend;
    const remote_ack = async (doc): Promise<any> => {
      assert_wanted();
      if (!deadline_constrained) {
        return doc;
      }
      const remote = this.remote_poll_dbs[pid];
      if (!remote) {
        throw new Error("DataService.store_poll_data_confirmed cannot acknowledge " + _id + " without a remote poll db connection");
      }
      const adopt_authoritative = async (authoritative, local_rev?: string): Promise<any> => {
        assert_wanted();
        if (authoritative._id != _id || !authoritative._rev || !authoritative.value
            || authoritative.due != this.poll_caches[pid]['due']
            || decrypt(authoritative.value, poll_pw) === null) {
          throw make_consistency_failure_error("Invalid remote publication acknowledgment for " + _id);
        }
        // A successful push (or a remotely existing ID) is not proof that our
        // revision won. Pull the actual winner and verify durable local agreement
        // before publishing it or allowing the migration source to be removed.
        if (local_rev != authoritative._rev) {
          await db.replicate.from(remote, {doc_ids: [_id], retry: false});
          assert_wanted();
        }
        const local = await this.get_existing_doc(db, _id);
        assert_wanted();
        if (local?._rev != authoritative._rev) {
          await this.restore_expired_poll_payload(pid, key, authoritative, assert_wanted);
        }
        return authoritative;
      };
      // push just this doc; the push may fail (or be redundant) although the
      // doc already reached the remote db earlier, e.g. via live sync, so its
      // remote presence is verified either way:
      try {
        await db.replicate.to(remote, {doc_ids: [_id], retry: false});
      } catch (err) {
        this.G.L.trace("DataService.store_poll_data_confirmed push failed, still checking remote presence", pid, key, err);
      }
      assert_wanted();
      let authoritative;
      try {
        authoritative = await remote.get(_id);
      } catch (err: any) {
        assert_wanted();
        if (err && err.status == 404) {
          const due = this.poll_caches[pid]['due'];
          // use the same one second grace period as validate_doc_update.js:
          if (due && (new Date()).getTime() > (new Date(due)).getTime() + 1000) {
            // Only a durable withdrawal permits dropping an expired source.
            await db.get(_id)
              .then(stale => {
                assert_wanted();
                return db.remove(stale);
              })
              .catch(remove_err => {
                if (!remove_err || remove_err.status != 404) { throw remove_err; }
              });
            // Removing a winning revision can reveal another conflict leaf.
            // Do not discard the source while any local value can reappear.
            const remaining = await db.get(_id).catch(read_err => {
              if (read_err && read_err.status == 404) { return null; }
              throw read_err;
            });
            if (remaining) {
              throw make_consistency_failure_error("Unpublished local revisions remain for " + _id);
            }
            assert_wanted();
            // The first remote read is not proof of permanent absence: another
            // session may have published this document between that read and
            // the local withdrawal above (clock skew around the deadline makes
            // this reachable). Only a second 404 shows the value was never
            // accepted; otherwise adopt the authoritative remote revision
            // instead of dropping a remotely accepted vote (#292).
            const republished = await this.get_existing_doc(remote, _id);
            assert_wanted();
            if (republished) {
              return await adopt_authoritative(republished);
            }
            this.handle_deleted_poll_doc(pid, {_id: _id});
            const expired: any = new Error("DataService.store_poll_data_confirmed cannot publish " + _id + " anymore since the poll's due date has passed");
            expired.vodle_publication_expired = true;
            throw expired;
          }
        }
        throw err;
      }
      return await adopt_authoritative(authoritative, doc._rev);
    };
    const attempt_put = (attempt: number): Promise<void> =>
      db.get(_id)
      .catch(err => {
        if (err && err.status == 404) { return null; }
        throw err;
      })
      .then(doc => {
        assert_wanted();
        // only the due value is stored unencrypted:
        const enc_value = (key == 'due') ? value : encrypt(value, poll_pw);
        if (doc) {
          const stored = (key == 'due') ? doc.value : decrypt(doc.value, poll_pw),
                // a non-overwriting migration retry must not clobber a newer
                // value written after the poll started running, so any
                // existing value counts as confirmed — except for the poll
                // state, which advances monotonically: an existing 'running'
                // must still be updated when this retry requests 'closed',
                // while an existing 'closed' is preserved (#292):
                value_confirmed = (stored == value)
                  || (!overwrite
                      && (key != 'state' || poll_state_is_at_least(stored, value)));
          if (value_confirmed
              && (!add_due || doc.due == this.poll_caches[pid]['due'])) {
            // already stored locally (or a newer value that a non-overwriting
            // migration retry must not clobber), incl. any required due
            // field; the write is confirmed once remotely acknowledged:
            return remote_ack(doc).then(publish_confirmed);
          }
          if (!is_voter_key && key != 'state') {
            // CouchDB rejects all updates to existing shared metadata. Keep
            // the fallback until a valid authoritative document is available,
            // rather than confirming a local repair that cannot replicate.
            throw new Error("DataService.store_poll_data_confirmed cannot repair immutable shared document " + _id);
          }
          if (!value_confirmed) {
            doc.value = enc_value;
          }
          if (add_due) {
            doc.due = this.poll_caches[pid]['due'];
          }
          return db.put(doc).then(result => remote_ack({...doc, _rev: result.rev})).then(publish_confirmed);
        }
        const new_doc: any = {_id: _id, value: enc_value};
        if (add_due) {
          new_doc.due = this.poll_caches[pid]['due'];
        }
        return db.put(new_doc).then(result => remote_ack({...new_doc, _rev: result.rev})).then(publish_confirmed);
      })
      .catch(err => {
        if (err && (err.vodle_publication_expired || err.vodle_move_aborted)) {
          // terminal by design, retrying cannot change the outcome:
          throw err;
        }
        if (attempt >= confirmed_put_max_attempts) {
          this.G.L.error("DataService.store_poll_data_confirmed giving up", pid, key, attempt, err);
          throw err;
        }
        this.G.L.warn("DataService.store_poll_data_confirmed will retry", pid, key, attempt, err);
        return new Promise<void>((resolve, reject) => {
          window.setTimeout(
            () => attempt_put(attempt + 1).then(resolve, reject),
            environment.db_put_retry_delay_ms);
        });
      });
    return attempt_put(1);
  }

  private async delete_user_data_confirmed(
      key: string, expected_rev?: string, is_current = () => !this.shutting_down): Promise<void> {
    /** Delete a user db doc and resolve only once the deletion is confirmed,
     *  retrying a bounded number of times. Unlike delete_user_data(), only a
     *  404 counts as "already deleted"; transient read/remove failures are
     *  retried and finally rejected, so callers can keep depending state
     *  consistent instead of silently losing the deletion (#292). */
    const local_only = local_only_user_keys.includes(key);
    const db = local_only ? this.local_only_user_DB : this.local_synced_user_db;
    let _id = key;
    if (!local_only) {
      const email_and_pw_hash = this.get_email_and_pw_hash();
      if (!email_and_pw_hash) {
        throw new Error("Cannot confirm user data deletion without user credentials");
      }
      _id = user_doc_id_prefix + email_and_pw_hash + "§" + key;
    }
    for (let attempt = 1; ; attempt++) {
      this.assert_voter_mutation_current(is_current);
      try {
        let doc = null;
        try {
          doc = await db.get(_id);
          this.assert_voter_mutation_current(is_current);
        } catch (err: any) {
          if (err && err.status == 404) {
            // really nonexistent (not just a transient read failure):
            this.assert_voter_mutation_current(is_current);
            return;
          }
          throw err;
        }
        if (expected_rev && doc._rev !== expected_rev) {
          const changed: any = new Error("Migration source changed before deletion: " + key);
          changed.vodle_source_changed = true;
          throw changed;
        }
        await db.remove(doc);
        this.assert_voter_mutation_current(is_current);
        const remaining = await this.get_existing_doc(db, _id);
        this.assert_voter_mutation_current(is_current);
        if (!remaining) { return; }
        const changed: any = new Error("User source still has a live revision: " + key);
        changed.vodle_source_changed = !!expected_rev;
        throw changed;
      } catch (err: any) {
        this.assert_voter_mutation_current(is_current);
        if (err?.vodle_source_changed) { throw err; }
        if (attempt >= confirmed_put_max_attempts) {
          this.G.L.error("DataService.delete_user_data_confirmed giving up", key, attempt, err);
          throw err;
        }
        this.G.L.warn("DataService.delete_user_data_confirmed will retry", key, attempt, err);
        await new Promise(resolve => window.setTimeout(resolve, environment.db_put_retry_delay_ms));
      }
    }
  }

  private delu_confirmed(key: string, expected_rev?: string, is_current = () => !this.shutting_down): Promise<void> {
    /** Durable, awaited variant of delu() for draft→running migration source
     *  copies (#292): the cache entry is only removed once the user db
     *  deletion has been confirmed (treating only a 404 as nonexistent), so a
     *  transient failure cannot leave a stale source doc behind that would be
     *  mistaken for an unfinished move after a restart. */
    return this.delete_user_data_confirmed(key, expected_rev, is_current).then(() => {
      this.assert_voter_mutation_current(is_current);
      delete this.user_cache[key];
    });
  }

  private async delete_voter_data_confirmed(pid: string, key: string, is_current: () => boolean): Promise<void> {
    const vid = this.user_cache[get_poll_key_prefix(pid) + 'myvid'];
    if (!vid || !key.startsWith('voter.' + vid + '§')
        || !this.user_cache[get_poll_key_prefix(pid) + 'password']) {
      throw new Error("Cannot delete voter data without its owner's credentials");
    }
    const db = this.get_local_poll_db(pid);
    const id = poll_doc_id_prefix + pid + '.' + key;
    await this.retry_voter_mutation(async () => {
      const doc = await this.get_existing_doc(db, id);
      this.assert_voter_mutation_current(is_current);
      if (!doc) { return; }
      await db.remove(doc);
      this.assert_voter_mutation_current(is_current);
      if (await this.get_existing_doc(db, id)) {
        throw new Error("Voter destination still has a live revision: " + key);
      }
    }, is_current);
  }

  private delete_user_data(key:string): boolean {
    // deletes a key from the user database. 
    this.G.L.trace("DataService.delete_user_data", key);
    var db, _id;

    if (local_only_user_keys.includes(key)) {
      db = this.local_only_user_DB;
      // simply use key as doc id:
      _id = key;
    } else {
      db = this.local_synced_user_db;
      // compose id:
      const email_and_pw_hash = this.get_email_and_pw_hash();
      if (!email_and_pw_hash) {
        this.G.L.warn("DataService.delete_user_data couldn't delete "+key+" since email or password are missing!");

        // RETURN:
        return false;
      }
      _id = user_doc_id_prefix + email_and_pw_hash + "§" + key;
    }

    // ASYNC:
    db.get(_id)
    .then(doc => {
      // key existed in db, so delete:

      db.remove(doc)
      .then(() => {
        this.G.L.trace("DataService.delete_user_data delete", key);
      })
      .catch(err => {
        this.G.L.warn("DataService.delete_user_data couldn't delete, will try again soon", key, doc, err);
        window.setTimeout(this.delete_user_data.bind(this), environment.db_put_retry_delay_ms, key);
      });

    }).catch(err => {

      // key did not exist in db:
      this.G.L.trace("DataService.delete_user_data no need to delete nonexistent key", key, err);

    });

    // RETURN:
    return true;
  }

  private delete_poll_data(pid:string, key:string): boolean {
    // deletes a key from a poll database. 
    this.G.L.trace("DataService.delete_poll_data", pid, key);

    const poll_pw = this.user_cache[get_poll_key_prefix(pid) + 'password'];
    var _id;

    // see what type of entry it is:
    if (key.indexOf("§") == -1) {

      // it's a non-voter data item.

      // use correct prefix:
      _id = poll_doc_id_prefix + pid + "§" + key;
      if ((poll_pw=='')||(!poll_pw)) {
        this.G.L.warn("DataService.delete_poll_data couldn't delete "+key+" from local_poll_DB since poll password or voter id are missing!");

        // RETURN:
        return false;
      }

    } else {

      // it's a voter data item.

      // check which voter's data this is:
      const vid_prefix = key.slice(0, key.indexOf("§")),
          vid = this.user_cache[get_poll_key_prefix(pid) + 'myvid'];
      if (vid_prefix != 'voter.' + vid) {
          // it is not allowed to alter other voters' data!
          this.G.L.error("DataService.delete_poll_data tried deleting another voter's data item", key);

          // RETURN: 
          return false;
      }

      _id = poll_doc_id_prefix + pid + '.' + key;
      if ((poll_pw=='')||(!poll_pw) || (vid=='')||(!vid)) {
        this.G.L.warn("DataService.delete_poll_data couldn't delete "+key+" from local_poll_DB since poll password or voter id are missing!");

        // RETURN:
        return false;
      }
    }

    const db = this.get_local_poll_db(pid);

    // ASYNC:
    db.get(_id)
    .then(doc => {
      // key existed in db, so delete:

      db.remove(doc)
      .then(() => {
        this.G.L.trace("DataService.delete_poll_data local-only delete", pid, key);
      })
      .catch(err => {
        this.G.L.warn("DataService.delete_poll_data couldn't delete, will try again soon", pid, key, doc, err);
        window.setTimeout(this.delete_poll_data.bind(this), environment.db_put_retry_delay_ms, pid, key);
      });

    }).catch(err => {

      // key did not exist in db:
      this.G.L.warn("DataService.delete_poll_data no need to delete nonexistent key", pid, key, err);

    });

    // RETURN:
    return true;

  }

  private get_email_and_pw_hash(email = this.user_cache['email'],
                                pw = this.user_cache['password']): string {
    // the parameters allow computing the hash of *retired* credentials, whose
    // documents must still be reachable right after a credential change (#292)
    if ((email=='')||(!email) || (pw=='')||(!pw)) { return null; }
    const hash = myhash(email + "§" + pw);
//    this.G.L.trace("email_and_pw_hash:", email, pw, hash);
    return hash;
  }


  // DBs --> DBs:

  private move_user_data(old_values) {
    this.G.L.entry("DataService.move_user_data");
    // Cancelling the mutation session only stops work that has not started
    // yet. A voter-source db.put() already issued under the old credentials
    // can still complete afterwards, and its post-write generation check only
    // reports the cancellation — it cannot undo the durable document, which
    // carries the old email/password hash in its _id and is therefore
    // invisible to the new session. So keep the admission of new voter
    // mutations behind the drain returned by cancel_voter_mutations(), and
    // reconcile whatever the retired session completed before allowing any new
    // write for the same keys (#292):
    const retired_keys = Object.keys(this.voter_mutation_tails)
      .filter(key => !!this.voter_source_parts(key));
    const old_hash = old_values
      ? this.get_email_and_pw_hash(old_values['email'], old_values['password']) : null;
    const drained = this.cancel_voter_mutations();
    const admission = drained
      .then(() => this.migrate_retired_voter_sources(retired_keys, old_hash))
      .catch(err => {
        this.G.L.error("DataService.move_user_data could not reconcile retired voter sources", err);
      });
    this.voter_mutation_admission = admission;
    // The user data itself moves when new credentials are COMMITTED — by
    // login_submitted() and change_credentials() (#330) — not on every
    // keystroke in a credentials field, which also arrives here. A change
    // of the database settings alone keeps the identity, so the existing
    // documents stay valid; the user db is connected anew (CouchDB):
    if (!environment.useMatrixBackend && this.credentials_snapshot() && !!this.remote_user_db
        && old_values['email'] == this.user_cache['email'] && old_values['password'] == this.user_cache['password']) {
      this.schedule_user_db_reconnection();
    }
    this.G.L.exit("DataService.move_user_data");
  }

  // Credential changes (#330). The credentials a device used until a change
  // are recorded as a pending move (a device-local key) until the move has
  // completed, so that an interrupted move — a closed browser, an
  // unreachable server — is resumed at the next start; every step of a
  // move is idempotent.

  private credentials_snapshot(): credentials_t | null {
    const email = this.user_cache['email'] || '', password = this.user_cache['password'] || '';
    if (!email || !password) {
      return null;
    }
    return {email, password, guest: this.user_cache['guest'] == '1'};
  }

  private note_credentials_change() {
    /** The credentials differ from the ones in use: remember the latter as
     *  the origin of a data move — once, at the first change, so that
     *  typing new credentials into the login or settings page records the
     *  credentials the data is owned by, not an intermediate value. */
    const committed = this.committed_credentials;
    if (!committed || this.pending_user_data_move()) {
      return;
    }
    const now = this.credentials_snapshot();
    if (now && (now.email != committed.email || now.password != committed.password)) {
      this.G.L.info("DataService.note_credentials_change: the credentials change, recording a data move");
      this.record_pending_user_data_move(committed);
    }
  }

  private record_pending_user_data_move(previous: credentials_t) {
    this.user_cache['pending_user_data_move'] = JSON.stringify({...previous, attempts: 0});
    this.store_user_data('pending_user_data_move', this.user_cache, 'pending_user_data_move');
    this.save_state();
  }

  pending_user_data_move(): credentials_t | null {
    const stored = this.user_cache['pending_user_data_move'];
    if (!stored) {
      return null;
    }
    try {
      const move = JSON.parse(stored);
      return (move && move.email && move.password) ? move : null;
    } catch (err) {
      return null;
    }
  }

  private clear_pending_user_data_move() {
    this.user_cache['pending_user_data_move'] = '';
    this.store_user_data('pending_user_data_move', this.user_cache, 'pending_user_data_move');
    delete this.user_cache['pending_user_data_move'];
    this.save_state();
  }

  async perform_user_data_move(from: credentials_t): Promise<void> {
    /** Move the user's data from the credentials `from` to the current ones
     *  (#330). Matrix: a changed password is changed on the homeserver and
     *  the user room re-encrypted; a changed e-mail address names another
     *  Matrix account, so the voter rooms are handed over to it and the
     *  user data written to its user room (which is also how a guest's data
     *  reaches the account the guest logs in with later, #193). CouchDB:
     *  the user documents are re-written under the new identity. The
     *  pending record is cleared only once the move has completed; a move
     *  that keeps failing is given up after three attempts. */
    const to = this.credentials_snapshot();
    if (!to) {
      // logged out meanwhile; the record stays for the next login
      return;
    }
    if (from.email == to.email && from.password == to.password) {
      this.clear_pending_user_data_move();
      this.committed_credentials = to;
      return;
    }
    const attempts = (from.attempts || 0) + 1;
    if (attempts > 3) {
      this.G.L.error("DataService.perform_user_data_move giving up after", attempts - 1, "failed attempts");
      this.clear_pending_user_data_move();
      return;
    }
    this.user_cache['pending_user_data_move'] = JSON.stringify({...from, attempts});
    this.store_user_data('pending_user_data_move', this.user_cache, 'pending_user_data_move');
    this.G.L.info("DataService.perform_user_data_move attempt", attempts, from.guest ? "(from a guest account)" : "");
    this.G.add_spinning_reason("user-data-move");
    try {
      if (environment.useMatrixBackend) {
        await this.move_matrix_user_data(from, to);
      } else {
        await this.move_couchdb_user_data(from, to);
      }
      this.clear_pending_user_data_move();
      this.committed_credentials = to;
      this.G.L.info("DataService.perform_user_data_move completed");
    } finally {
      this.G.remove_spinning_reason("user-data-move");
    }
  }

  /** every poll account's password follows the user's own (#327) */
  private async change_poll_account_passwords(
      matrix: MatrixService, from_password: string, to_password: string): Promise<void> {
    for (const pid of this._pids) {
      const vid = this.user_cache[get_poll_key_prefix(pid) + 'myvid'];
      if (!vid) { continue; }
      try {
        await matrix.changePollAccountPassword(pid, vid, from_password, to_password);
      } catch (error) {
        // one poll that cannot be reached must not strand the whole move;
        // the account keeps the old password and says so in the log
        this.G.L.error("DataService: a poll account's password did not change", pid, error);
      }
    }
  }
  
  private async move_matrix_user_data(from: credentials_t, to: credentials_t): Promise<void> {
    const matrix = this.matrixService;
    // First, before anything can leave the old password behind: every poll
    // account's password is derived from the user's (pollAccountPassword),
    // so a password change locks the user out of every poll they take part
    // in unless the accounts follow. The accounts themselves do not change
    // — they are named after poll and vid, not the e-mail — so the rooms,
    // the ratings and the tally are untouched, and an e-mail change alone
    // needs nothing here at all (#327).
    if (from.password !== to.password) {
      await this.change_poll_account_passwords(matrix, from.password, to.password);
    }
    if (hashEmail(from.email) == hashEmail(to.email)) {
      // the same account, only the password changed — from which the
      // homeserver password is derived and under which the user room's
      // data is encrypted:
      if (!matrix.isLoggedIn()) {
        // resuming after a restart: the homeserver has the new password
        // when the interrupted attempt got that far, else still the old one
        let has_new_password = true;
        try {
          await matrix.login(to.email, to.password, false);
        } catch (err) {
          has_new_password = false;
          await matrix.login(to.email, from.password, false);
        }
        if (!has_new_password) {
          await matrix.changePassword(to.email, from.password, to.password);
        }
      } else {
        await matrix.changePassword(to.email, from.password, to.password);
      }
      await this.syncUserDataWithMatrix(true);
      return;
    }
    // another account, named by the new address: it takes over the voter
    // rooms (same voter ids, so the tally does not change) and the data;
    // a guest account is deactivated afterwards
    const old_session = await matrix.sessionFor(from.email, from.password);
    await matrix.dropSession();
    await matrix.login(to.email, to.password);
    await matrix.takeOverVoterRooms(old_session, this.my_voter_room_entries());
    await this.syncUserDataWithMatrix(true);
    if (from.guest) {
      await matrix.retireSession(old_session, from.email, from.password, true);
    }
    await this.reconnect_matrix_polls();
  }

  private my_voter_room_entries(): Array<{pollId: string, vid: string}> {
    /** the (poll, own voter id) pairs of the running polls this user takes
     *  part in, whose voter rooms an account switch hands over */
    const entries: Array<{pollId: string, vid: string}> = [];
    for (const key of Object.keys(this.user_cache)) {
      const match = /^poll\.([^.]+)\.myvid$/.exec(key);
      if (!match) {
        continue;
      }
      const pid = match[1], vid = this.user_cache[key];
      if (!vid || this.pid_is_draft(pid) || this.getp(pid, 'state') == 'closed') {
        continue;
      }
      entries.push({pollId: pid, vid});
    }
    return entries;
  }

  private async reconnect_matrix_polls(): Promise<void> {
    /** after an account switch mid-session: the polls' Matrix wiring
     *  belonged to the old session, so every connected poll is joined and
     *  wired anew */
    for (const pid of Array.from(this._pids)) {
      if (!this._matrixPollListeners[pid]) {
        continue;
      }
      this.stop_poll_sync(pid);
      try {
        await this.connect_to_remote_poll_db(pid, false);
      } catch (err) {
        this.G.L.error("DataService.reconnect_matrix_polls could not reconnect", pid, err);
      }
    }
  }

  private async move_couchdb_user_data(from: credentials_t, to: credentials_t): Promise<void> {
    /** The user documents' ids carry the hash of e-mail address and
     *  password and their values are encrypted with the password, so they
     *  are re-written under the new identity: from the cache, which holds
     *  the decrypted data, or from the old documents where the cache lacks
     *  a key. The copies under the old identity leave this device; the old
     *  remote database keeps its copies, which the old credentials still
     *  open on another device. Documents of voter sources with mutations
     *  in flight are left to migrate_retired_voter_sources (#292). */
    const old_hash = this.get_email_and_pw_hash(from.email, from.password),
          new_hash = this.get_email_and_pw_hash(to.email, to.password);
    if (!old_hash || !new_hash || old_hash == new_hash) {
      return;
    }
    // nothing written below may still reach the old remote db:
    this.user_sync_start_pending = false;
    this.user_sync_start_generation += 1;
    if (this.user_db_sync_handler) {
      try {
        this.user_db_sync_handler.cancel();
      } catch (err) {
        this.G.L.warn("DataService.move_couchdb_user_data could not cancel the user data sync", err);
      }
      this.user_db_sync_handler = null;
    }
    this.remote_user_db = null;
    const db = this.local_synced_user_db;
    const old_prefix = user_doc_id_prefix + old_hash + '§', new_prefix = user_doc_id_prefix + new_hash + '§';
    const old_docs = await db.allDocs({include_docs: true, startkey: old_prefix, endkey: user_doc_id_prefix + old_hash + '¨'});
    const values: Record<string, string> = {};
    for (const row of old_docs.rows) {
      const key = row.id.slice(old_prefix.length);
      try {
        values[key] = user_keys_unencrypted.includes(key) ? row.doc.value : decrypt(row.doc.value, from.password);
      } catch (err) {
        this.G.L.warn("DataService.move_couchdb_user_data could not decrypt an old document", key);
      }
    }
    const in_flight = new Set(Object.keys(this.voter_mutation_tails));
    for (const key of Object.keys(this.user_cache)) {
      if (!local_only_user_keys.includes(key) && key != 'user_last_seq' && (this.user_cache[key] ?? '') !== '') {
        values[key] = this.user_cache[key];
      }
    }
    const keys = Object.keys(values).filter(key => !in_flight.has(key) && (values[key] ?? '') !== '');
    const existing = keys.length ? await db.allDocs({keys: keys.map(key => new_prefix + key)}) : {rows: []};
    const revs: Record<string, string> = {};
    for (const row of existing.rows) {
      if (row.value && row.value.rev && !row.value.deleted) {
        revs[row.id] = row.value.rev;
      }
    }
    const docs = keys.map(key => ({
      _id: new_prefix + key,
      ...(revs[new_prefix + key] ? {_rev: revs[new_prefix + key]} : {}),
      value: user_keys_unencrypted.includes(key) ? values[key] : encrypt(values[key], to.password),
    }));
    if (docs.length) {
      await db.bulkDocs(docs);
    }
    const deletions = old_docs.rows
      .filter(row => !in_flight.has(row.id.slice(old_prefix.length)))
      .map(row => ({_id: row.id, _rev: row.value.rev, _deleted: true}));
    if (deletions.length) {
      await db.bulkDocs(deletions);
    }
    this.G.L.info("DataService.move_couchdb_user_data re-wrote", docs.length, "documents, removed", deletions.length);
  }

  private user_db_reconnection_timer: any = null;

  private schedule_user_db_reconnection() {
    if (this.user_db_reconnection_timer) {
      clearTimeout(this.user_db_reconnection_timer);
    }
    this.user_db_reconnection_timer = setTimeout(() => {
      this.user_db_reconnection_timer = null;
      this.reconnect_user_db().catch(err => {
        this.G.L.warn("DataService.schedule_user_db_reconnection failed", err);
      });
    }, 1000);
  }

  private reconnect_user_db(): Promise<any> {
    /** CouchDB: connect the user db anew after the credentials or the
     *  database settings changed (#330). Quiet: a failure is logged, the
     *  next start asks for credentials as usual. */
    if (environment.useMatrixBackend || this.shutting_down) {
      return Promise.resolve(false);
    }
    this.user_sync_start_pending = false;
    this.user_sync_start_generation += 1;
    if (this.user_db_sync_handler) {
      try {
        this.user_db_sync_handler.cancel();
      } catch (err) {
        this.G.L.warn("DataService.reconnect_user_db could not cancel the user data sync", err);
      }
      this.user_db_sync_handler = null;
    }
    this.remote_user_db = null;
    if (!this.has_user_db_credentials()) {
      return Promise.resolve(false);
    }
    return this.connect_to_remote_user_db(true);
  }

  // Matrix: the poll membership keys (voter id, poll password, drafts…)
  // are written to the user room like the settings, coalesced so that
  // editing a draft does not send a state event per keystroke (#330):

  private pending_matrix_user_data: Set<string> = new Set();
  private matrix_user_data_timer: any = null;

  private schedule_matrix_user_data_write(key: string) {
    this.pending_matrix_user_data.add(key);
    if (this.matrix_user_data_timer) {
      return;
    }
    this.matrix_user_data_timer = setTimeout(() => {
      this.matrix_user_data_timer = null;
      this.flush_matrix_user_data().catch(err => {
        this.G.L.warn("DataService.flush_matrix_user_data failed", err);
      });
    }, environment.data_service?.matrix_user_data_delay_ms ?? 1000);
  }

  private async flush_matrix_user_data(): Promise<void> {
    if (this.shutting_down || !this.matrixService?.isLoggedIn()) {
      // pushed by syncUserDataWithMatrix at the next login
      return;
    }
    const keys = Array.from(this.pending_matrix_user_data);
    this.pending_matrix_user_data.clear();
    for (const key of keys) {
      const value = this.user_cache[key];
      try {
        if (value === undefined || value === null || value === '') {
          await this.matrixService.deleteUserData(key);
        } else {
          await this.matrixService.setUserData(key, value);
        }
      } catch (err) {
        this.G.L.warn("DataService.flush_matrix_user_data could not write", key, err);
      }
    }
  }

  private async migrate_retired_voter_sources(keys: string[], old_hash: string | null): Promise<void> {
    /** Reconcile the voter-source documents that writes from a retired
     *  mutation session may have completed under the old credentials. Their
     *  doc ids embed the old email/password hash, so the new session can
     *  neither read nor supersede them, and the filtered user sync no longer
     *  replicates them. Each such document is therefore re-written under the
     *  new identity from the still cached value and only then removed, so a
     *  completed old-session write is neither lost nor left behind as an
     *  unaccounted durable copy of voter data (#292). A document that cannot
     *  be migrated is kept, since it becomes valid again once the previous
     *  credentials are restored. */
    const new_hash = this.get_email_and_pw_hash(), password = this.user_cache['password'];
    if (!keys.length || !old_hash || this.shutting_down || old_hash === new_hash) {
      // nothing was in flight, or the credentials themselves did not change
      // (e.g. only a server url did), so the existing ids stay valid:
      return;
    }
    const db = this.local_synced_user_db;
    if (!db) { return; }
    for (const key of keys) {
      const old_id = user_doc_id_prefix + old_hash + "§" + key;
      try {
        const retired = await this.get_existing_doc(db, old_id);
        if (!retired) { continue; }
        if (key in this.user_cache) {
          if (!new_hash || !password) {
            // without a usable new identity the retired document is the only
            // durable copy, so keep it rather than losing the value:
            this.G.L.warn("DataService.migrate_retired_voter_sources keeping unmigratable source", old_id);
            continue;
          }
          const new_id = user_doc_id_prefix + new_hash + "§" + key;
          const existing = await this.get_existing_doc(db, new_id);
          await db.put({...existing, _id: new_id,
                        value: encrypt(this.user_cache[key] || '', password)});
        }
        // only now that the value is either migrated or gone from the cache
        // may the old-credential document be withdrawn:
        await db.remove(retired);
      } catch (err) {
        // keep the retired document and make the unreconciled state visible to
        // the consistency checks instead of finalizing over it:
        this.voter_mutation_failures[key] = err;
        this.G.L.error("DataService.migrate_retired_voter_sources failed", old_id, err);
      }
    }
  }

  // OTHER:

  clear_all_local(): Promise<any> {
    // called at logout.
    this.G.L.entry("DataService.clear_all_local");
    // TODO: disable user interaction
    this._ready = false;
    this.shutting_down = true;
    const mutations_finished = this.cancel_voter_mutations();
    return new Promise((resolve, reject) => {
      // Log out of Matrix — the person's own account and every poll account
      // this device signed in (#327). It is AWAITED below, before the local
      // storage is cleared and dropped: dropSession writes the same storage
      // that clear_all_local is about to empty and set to null, and a
      // fire-and-forget logout raced it, which could leave a credential
      // behind the clear or throw on a storage that was no longer there.
      const matrix_logged_out = environment.useMatrixBackend
        ? DataService.within(DataService.LOGOUT_TIMEOUT_MS,
            this.logout_matrix_everywhere().catch(err => {
              this.G.L.warn("Matrix logout failed (continuing anyway)", err);
            }))
        : Promise.resolve();

      // stop all syncs:
      this.G.L.info("Stopping database synchronisation...");
      // cancel any pending deferred poll-finalization retry timers, so no
      // end() retry can fire against the local dbs destroyed below (#292):
      for (const pid in this.G.P.polls) {
        this.G.P.polls[pid].cancel_end_retry();
      }
      this.stop_replication_watchdog();
      this.user_sync_start_pending = false;
      this.user_sync_start_generation += 1;
      this.poll_sync_start_pending = {};
      this.poll_sync_start_generation = {};
      if (this.change_queue_timeout != null) {
        clearTimeout(this.change_queue_timeout);
        this.change_queue_timeout = null;
      }
      this.change_queue_scheduled = false;
      this.change_queue = [];
      this.change_retry_counts = {};
      if (!!this.user_db_sync_handler) {
        this.user_db_sync_handler.cancel();
        this.user_db_sync_handler = null;
      }
      this.replication_active = {};
      this.replication_progress = {};
      this.replication_stalled = {};
      this.replication_restart_pending = {};
      for (const pid in this.poll_db_sync_handlers) {
        this.stop_poll_sync(pid);
      }
      // TODO: wait for all syncs to finish
      // delete all local dbs:
      this.G.L.info("Deleting local databases...");
      // only if it was ever opened: asking for it would open it to destroy it
      mutations_finished
      .then(() => matrix_logged_out)
      .then(() => this._local_synced_user_db
          ? this._local_synced_user_db.destroy() : Promise.resolve())
      .then(() => {
        this.local_only_user_DB.destroy()
        .then(() => {
          for (let pid in this.local_poll_dbs) {
            if (!!this.local_poll_dbs[pid]) {
              this.local_poll_dbs[pid].destroy();
            }
          }
          // delete ionic local storage:
          this.G.L.info("Deleting local storage...");
          if (!this.storage) {
              // DONE. 
              resolve(true);
          } else {
            this.storage.clear()
            .then(() => {
              this.storage = null;
              this.G.L.info("...done!");
              // DONE. 
              resolve(true);
            }).catch(reject);  
          }
        }).catch(reject);
      }).catch(reject);
    });
  }

  /**
   * The settings a fresh account gets, for an account whose settings turn
   * out to be genuinely absent — after the sync, so that "absent" means
   * absent and not merely "not here yet".
   *
   * Both paths that CREATE an account set the default wap to 10: the login
   * page's new-password step and the guest. The path for someone who says
   * they have used vodle before deliberately does not, because the value is
   * supposed to arrive from their user room. After "delete my data" and a
   * registration with the same address there is no user room left to bring
   * it, so nothing set it and SettingsService fell back to its `||'0'` —
   * a wap of zero for every option nobody has rated, which is not what a
   * new account gets (#327).
   *
   * A deliberate zero is untouched: it is stored as '0', not as nothing.
   */
  private ensure_user_defaults(): void {
    if (!this.user_cache) { return; }
    // The LANGUAGE, which is two keys: 'language' is the person's stored
    // preference and travels with the account, 'local_language' is what this
    // device shows and is local-only. The sync's restore writes the cache
    // directly rather than through setu(), and setu() is what actually
    // applies a language, so a preference that came back from the server has
    // to be applied here. And an account that has none takes this device's,
    // which is what the login page's question established.
    const preferred = this.user_cache['language'] || '', showing = this.getu('local_language');
    if (preferred && preferred !== showing) {
      this.G.L.info("DataService: showing the language this account prefers", preferred);
      this.setu('local_language', preferred);
    }
    // and NOTHING when the account has none. This used to make this device's
    // language the account's, which reads as helpful and is not: that value
    // is a guess (the browser's language, taken without asking, #193), and
    // storing it gave every other device of the person a language they never
    // chose — and, once stored, it was pushed over the one they later chose
    // in the settings. The account gets a language when the person picks one
    // on the settings page, and not before (#327).
    // The DEFAULT WAP is not settled here at all any more, and nothing else
    // is either. Storing the deployment's setting for an account that has
    // none looks like the same kindness as storing a guessed language, and
    // costs the same: it is a value the person did not choose, so it can be
    // pushed over one they did, and it freezes the deployment's default at
    // the moment of their first login. SettingsService.default_wap falls
    // back to environment.default_wap for display, and the one place that
    // WRITES a wap into a poll waits for user_data_ready first, so nothing
    // needs it in the account until the person picks a wap of their own
    // (#327).
  }

  /** What deleting all of a person's data is doing right now, for the page
   *  to show: a translation key and its parameters, or null when nothing is
   *  being deleted. It takes tens of seconds — a poll's every voter room is
   *  left and forgotten, two round trips each — and the page was blank for
   *  all of it (#327). */
  deletion_progress: {key: string, params?: any} | null = null;

  /** What courtesy to the other participants could not be managed before
   *  the data went: the declined requests, the withdrawn ones, the zeroed
   *  waps. Read by the page, which tells the person what is left of them. */
  deletion_left_undone: string[] = [];

  /** A step of the deletion that is owed to OTHER people -- declining a
   *  request, withdrawing one, zeroing a wap -- run so that it cannot stop
   *  the deletion itself. Until 2026-10-15 these were awaited plainly, and
   *  revoke_delegation throws for a poll this session never opened, which
   *  is a poll the person may well have: the deletion then rejected before
   *  it had deleted anything, the page reported the failure through a local
   *  notification the browser does not show, went back to where it came
   *  from, and every byte was still there (#367). The person asked for
   *  their data to go; a courtesy that cannot be paid is recorded, not an
   *  excuse to keep the data. */
  private async courtesy(what: string, step: () => any): Promise<void> {
    try {
      await step();
    } catch (error) {
      this.G.L.warn("DataService.delete_all could not " + what, error);
      this.deletion_left_undone.push(what);
    }
  }

  async delete_all(): Promise<any> {
      this.deletion_progress = {key: 'delete-all.progress-votes'};
      this.deletion_left_undone = [];
      // decline all not yet declined delegation requests:
      for (const [pid, cache] of Object.entries(this.G.D.incoming_dids_caches)) {
        if (cache) {
          for (const [did, [from, url, status]] of cache) {
            if (!status[0].startsWith('declined')) {
              this.G.L.trace("DataService.delete_all declining request", did);
              await this.courtesy(`decline the delegation request ${did} of ${pid}`,
                () => this.G.Del.decline(pid, did));
            }
          }
        }
      }
      // withdraw all own delegation requests:
      for (const [pid, cache] of Object.entries(this.G.D.outgoing_dids_caches)) {
        if (cache) {
          for (const [oid, did] of cache) {
            if (did) {
              this.G.L.trace("DataService.delete_all revoking request", did);
              await this.courtesy(`withdraw the delegation request ${did} of ${pid}`,
                () => this.G.Del.revoke_delegation(pid, did, oid));
            }
          }
        }
      }
      // the guard bot forgets the e-mail address in every poll (EmailService):
      if (this.G.E?.confirmed) {
        await this.courtesy("have the guard bot forget the e-mail address", () => this.G.E.stop());
      }
      // set all waps to zero:
      for (const [pid, p] of Object.entries(this.G.P.polls)) {
        this.G.L.trace("DataService.delete_all setting zero waps for", pid);
        for (const oid of p.oids) {
          await this.courtesy(`set the wap of ${oid} in ${pid} to zero`,
            () => p.set_my_own_rating(oid, 0, true));
        }
      }
      await Promise.all(Object.keys(this.G.P.polls).map(pid =>
        this.await_poll_mutations(pid).catch(error => {
          // a write that could not be finished must not keep the person's
          // data on the server: of the two, the deletion is what they asked
          // for, and it is the one that cannot simply be tried again
          this.G.L.warn("DataService.delete_all could not finish the writes of", pid, error);
        })));
      this.shutting_down = true;
      // stop syncing:
      if (!!this.user_db_sync_handler) {
        this.user_db_sync_handler.cancel();
        this.user_db_sync_handler = null;
      }
      // don't let the watchdog restart the cancelled sync (#292):
      this.replication_active['user'] = false;
      // delete everything this person has on the server:
      await this.delete_remote();
      this.deletion_progress = {key: 'delete-all.progress-local'};
      // left standing on success: the page restarts the app straight after,
      // and blanking it first would put the empty screen back for the last
      // moment of it. The page clears it if the deletion fails.
      return await this.clear_all_local();
  }

  delete_remote(): Promise<any> {
    /** Everything this person has on the server, removed.
     *
     * The Matrix backend has no remote_user_db — it is a PouchDB, and on
     * Matrix it is never connected — so this walked straight into a
     * TypeError on null and rejected, which the delete-all page reported as
     * a failure and answered by going back to the page the person came
     * from, with every last byte of their data still in place (#327). */
    return environment.useMatrixBackend
      ? this.delete_remote_matrix() : this.delete_remote_couchdb();
  }

  /** how long delete_all waits for what an account still owes the server
   *  before leaving its rooms anyway */
  private static readonly DELETE_DRAIN_MS = 10000;

  private async delete_remote_matrix(): Promise<void> {
    this.G.L.entry("DataService.delete_remote_matrix");
    // The polls first, each through its own account, which since the
    // accounts were split is the only one that is a member of its rooms
    // (#327). The zeroed ratings written a moment ago may still be on their
    // way, and a write into a room the account has left is refused for
    // good, so each queue is given a bounded chance to drain first.
    // every poll this device knows, not only the ones whose objects are
    // loaded: a poll the person never opened this session still has their
    // rooms, and its account is the only member that can leave them
    const pids = new Set([...Object.keys(this.G.P.polls || {}), ...(this._pids || [])]);
    let done = 0;
    for (const pid of pids) {
      this.deletion_progress = {key: 'delete-all.progress-polls',
                                params: {done: ++done, total: pids.size}};
      try {
        const service = await this.poll_matrix(pid);
        await this.drain_pending_writes(service);
        await service.leavePollRooms(pid);
      } catch (error) {
        this.G.L.warn("DataService.delete_remote_matrix could not clear the poll", pid, error);
      }
      await this.close_poll_matrix(pid);
    }
    // then the person's own account, whose user room is what the CouchDB
    // user database held: the settings, the poll memberships, the vids and
    // the poll passwords
    if (this.matrixService?.isLoggedIn()) {
      this.deletion_progress = {key: 'delete-all.progress-user'};
      await this.drain_pending_writes(this.matrixService);
      await this.matrixService.deleteAllUserData(Object.keys(this.user_cache || {}));
    }
    this.G.L.exit("DataService.delete_remote_matrix");
  }

  /** wait, for a bounded time, for what an account still owes the server */
  private async drain_pending_writes(service: MatrixService): Promise<void> {
    const deadline = Date.now() + DataService.DELETE_DRAIN_MS;
    while (service.pendingWriteCount > 0 && Date.now() < deadline) {
      // nudge it: a queue waiting out a retry delay would otherwise just
      // sit there for the whole of the deadline
      await service.processOfflineQueue().catch(() => 0);
      if (service.pendingWriteCount == 0) { break; }
      await new Promise(resolve => window.setTimeout(resolve, 200));
    }
    if (service.pendingWriteCount > 0) {
      this.G.L.warn("DataService.drain_pending_writes giving up with",
        service.pendingWriteCount, "writes outstanding");
    }
  }

  private delete_remote_couchdb(): Promise<any> {
    this.deletion_progress = {key: 'delete-all.progress-user'};
    const email_and_pw_hash = this.get_email_and_pw_hash();
    return new Promise((resolve, reject) => {
      this.remote_user_db.allDocs({
        include_docs: false,
        startkey: user_doc_id_prefix + email_and_pw_hash + "§",
        endkey: user_doc_id_prefix + email_and_pw_hash + '¨',
        inclusive_end: false
      }).then(res => {
        const bulkDocs = [];
        for (const row of res.rows) {
          bulkDocs.push({_id: row.id, _rev: row.value.rev, _deleted: true})
        }
        this.G.L.trace("DataService.delete_remote_couchdb trying to delete", bulkDocs);
        this.remote_user_db.bulkDocs(bulkDocs)
        .then(res => {
          this.G.L.trace("DataService.delete_remote_couchdb succeeded", res);
          resolve(true);
        })
        .catch(err => {
          this.G.L.error("DataService.delete_remote_couchdb failed", err);
          reject(err);
        });
      })
      .catch(reject);
    })
  }

  /** What this device already says about permission to show
   *  notifications, without asking for it. On the web the browser's own
   *  Notification.permission answers; Capacitor's plugin is only asked on a
   *  native platform, since its web implementation decides whether
   *  notifications are supported by constructing one. */
  private async notification_permission(): Promise<string> {
    try {
      if (!Capacitor.isNativePlatform()) {
        if (!('Notification' in window)) { return 'denied'; }
        const permission = window.Notification.permission;
        return permission == 'default' ? 'prompt' : permission;
      }
      return (await LocalNotifications.checkPermissions())['display'];
    } catch (error) {
      this.G.L.warn("DataService.notification_permission could not be read", error);
      return 'denied';
    }
  }

  /** Ask for permission to show notifications. Call this from a button's
   *  handler and nowhere else: a browser grants it only to a request a
   *  person's click started. */
  private async ask_for_notifications(): Promise<void> {
    try {
      const display = Capacitor.isNativePlatform()
        ? (await LocalNotifications.requestPermissions())['display']
        : await window.Notification.requestPermission();
      this.can_notify = display == 'granted';
      this.G.L.info("DataService.ask_for_notifications", display);
    } catch (error) {
      this.can_notify = false;
      this.G.L.warn("DataService.ask_for_notifications was refused", error);
    }
  }

  /** Whether this device may show notifications, and -- if `prompt` and
   *  nobody has been asked yet -- vodle's own explanation of what it would
   *  use them for, whose "yes" is what asks for the permission.
   *
   *  Start-up used to ask the browser outright: a permission prompt out of
   *  nowhere, before vodle had said what it wanted it for and before any
   *  click. Firefox refuses such a request and throws "The Notification
   *  permission may only be requested from inside a short running
   *  user-generated event handler", which was an uncaught error on every
   *  start in Mull. Reading the state costs nothing and asks nobody. */
  init_notifications(prompt:boolean) {
    this.notification_permission().then(async state => {
      if (state.startsWith('prompt') && prompt) {
        const dialog = await this.alertCtrl.create({ 
          header: this.translate.instant('data.notifications-permission-header'), 
          message: this.translate.instant('data.notifications-permission-intro'), 
          buttons: [
            { 
              text: this.translate.instant('no'), 
              role: 'cancel',
              handler: () => { 
                this.G.L.trace('DataService.init_notifications user No');
                this.can_notify = false;
              } 
            },
            { 
              text: this.translate.instant('yes'),
              role: 'ok', 
              handler: () => {
                this.G.L.trace('DataService.init_notifications user Yes');
                // inside the handler, so the browser sees a request its
                // user started
                this.ask_for_notifications();
              } 
            } 
          ] 
        }); 
        await dialog.present(); 
      } else if (state=='granted') {
        this.G.L.info("DataService.init_notifications granted");
        this.can_notify = true;
      } else {
        this.G.L.info("DataService.init_notifications not granted:", state);
        this.can_notify = false;
      }
    }).catch(err => {
      this.G.L.warn("DataService.init_notifications failed:", err);
    });
  }

  /** Voter data that arrived from a voter room and is not a rating: store it
   *  where getv finds it and let the delegation service act on it.
   *
   *  This is what doc2poll_cache does for CouchDB, and what the Matrix
   *  backend did not do at all: a delegation request was written into the
   *  requester's voter room and read by nobody, so the delegate's page said
   *  it was still waiting for data about the request — correctly, and for
   *  ever, since nothing was coming (#327).
   *
   *  The value is stored BEFORE the delegation service is told, and stays
   *  stored if that throws: the poll may not be loaded yet, and the next
   *  read of the same room (or the page's own re-decide) must find the
   *  value rather than a hole where doc2poll_cache would have rolled back.
   *
   *  A voter deletes data on Matrix by overwriting the state event with an
   *  empty content, so an empty value here is a deletion — which is how a
   *  revoked delegation reaches the delegate. doc2poll_cache routes that to
   *  process_deleted_request_from_db; this used to hand it to
   *  process_request_from_db instead, where an empty request only made the
   *  agreement look unfinished, so the revoked request stayed on the
   *  delegate's screen.
   */
  matrix_voter_data_arrived(pollId: string, vid: string, key: string, value: any): void {
    if (!this.poll_caches[pollId]) {
      this.poll_caches[pollId] = {};
    }
    const gone = (value === null || value === undefined || value === '');
    const cache_key = this.get_voter_key_prefix(pollId, vid) + key;
    if (gone) {
      delete this.poll_caches[pollId][cache_key];
    } else {
      this.poll_caches[pollId][cache_key] = String(value);
    }
    try {
      if (key.startsWith('del_request.')) {
        const did = key.slice('del_request.'.length);
        if (gone) {
          this.G.Del.process_deleted_request_from_db(pollId, did, vid);
        } else {
          this.G.Del.process_request_from_db(pollId, did, vid);
        }
      } else if (key.startsWith('del_response.') && !gone) {
        this.G.Del.process_signed_response_from_db(pollId, key.slice('del_response.'.length), vid);
      }
    } catch (err) {
      this.G.L.error("DataService.matrix_voter_data_arrived could not process", pollId, key, err);
    }
  }

  get_voter_key_prefix(pid: string, vid?: string): string {
    return 'voter.' + (vid ? vid : this.getp(pid, 'myvid')) + "§";
  }
  
  /** A date and time as people read it, with the time zone named.
   *
   *  The zone is the one the browser reports, which is not always the one
   *  the person is in: Mull, Tor Browser and Firefox with
   *  privacy.resistFingerprinting report UTC whatever the device says, and
   *  a poll that ends at 22:03 in Berlin then reads "20:03". Naming the
   *  zone -- "20:03 UTC", or "20:03 MESZ" where the zone is real -- makes
   *  the time right rather than silently two hours off. It also tells the
   *  recipient of an invitation, which carries this text, whose clock it
   *  is. */
  format_date(date: Date): string {
    // getCurrentLang() is null before a language is set (ngx-translate 18);
    // undefined lets the browser pick its own locale then
    return date ? date.toLocaleDateString(this.translate.getCurrentLang() || undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' }) : '';
  }

  /** The short name of the time zone times are shown in ("MESZ", "UTC"),
   *  for the places where a time is shown by a component that does not say
   *  it itself -- the date picker's button. */
  time_zone_name(date: Date = new Date()): string {
    try {
      const parts = new Intl.DateTimeFormat(this.translate.getCurrentLang() || undefined,
        { timeZoneName: 'short' }).formatToParts(date);
      return (parts.find(part => part.type == 'timeZoneName') || {value: ''}).value;
    } catch (error) {
      return '';
    }
  }

  hash(what): string {
    return myhash(what);
  }

  generate_id(length:number): string {
    // generates a random string of requested length
    return CryptoES.WordArray.random(length/2).toString();
  }

  email_is_valid(email: string): boolean {
    return !!String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  }

  async test_sodium() {
    this.G.L.entry("DataService.test_sodium waiting for sodium");
    await Sodium.ready;
    this.G.L.trace("DataService.test_sodium ready");
  
    // Initialize with random bytes:
    let message = "This is just an example string.";
  
    /*
    let key = Sodium.randombytes_buf(32);
    let nonce = Sodium.randombytes_buf(24);

    // Encrypt:
    let encrypted = Sodium.crypto_secretbox_easy(message, nonce, key);
    this.G.L.trace("DataService encrypted", encrypted);
  
    // Decrypt:
    let decrypted = Sodium.to_string(Sodium.crypto_secretbox_open_easy(encrypted, nonce, key));
    this.G.L.trace("DataService decrypted", decrypted);    
    */

    const keypair = this.generate_sign_keypair();
    this.G.L.trace("DataService.test_sodium keypair", keypair);

    const signed = this.sign(message, keypair.private);
    this.G.L.trace("DataService.test_sodium signed", signed);

    const result = this.open_signed(signed, keypair.public); 
    this.G.L.trace("DataService.test_sodium opened", result);
/*
    const encrypted = this.pgp_encrypt(message, keypair.public);
    this.G.L.trace("DataService.test_sodium encrypted", encrypted);

    const decrypted = this.pgp_decrypt(encrypted, keypair.private); 
    this.G.L.trace("DataService.test_sodium decrypted", decrypted);
*/
    const keypair2 = this.generate_sign_keypair();
    const result2 = this.open_signed(signed, keypair2.public); 
    if (result2) {
      this.G.L.error("DataService.test_sodium should not have been able to open signed msg with wrong key!", keypair.public, keypair2.public, result2);  
    } else {
      this.G.L.trace("DataService.test_sodium correctly refused wrong key");  
    }
    this.G.L.exit("DataService.test_sodium");
  }

  // SIGNING:

  generate_sign_keypair() {
    try {
      const keypair = Sodium.crypto_sign_keypair();
      return {
        public: Sodium.to_hex(keypair.publicKey), 
        private: Sodium.to_hex(keypair.privateKey)
      };
    } catch {
      return undefined;
    }
  }

  sign(message: string, private_key: string) {
    try {
      return Sodium.to_hex(Sodium.crypto_sign(message, Sodium.from_hex(private_key)));
    } catch {
      return undefined;
    }
  }

  open_signed(signed_message: string, public_key: string) {
    try {
      return Sodium.to_string(Sodium.crypto_sign_open(Sodium.from_hex(signed_message), Sodium.from_hex(public_key)));
    } catch {
      return undefined;
    }
  }

  // PGP ENCRYPTION:
/*
  // TODO: make this work!

  pgp_encrypt(message: string, public_key: string) {
    try {
      const nonce = Sodium.randombytes_buf(Sodium.crypto_box_NONCEBYTES);
      this.G.L.trace('sodium nonce, message, pubkey:', nonce, message, public_key);
      this.G.L.trace('sodium hex:', Sodium.crypto_box_easy(message, nonce, Sodium.from_hex(public_key)));
      return Sodium.to_hex(Sodium.crypto_box_easy(message, Sodium.from_hex(public_key)));
    } catch {
      return undefined;
    }
  }

  pgp_decrypt(message: string, private_key: string) {
    try {
      return Sodium.to_string(Sodium.crypto_box_open_easy(Sodium.from_hex(message), Sodium.from_hex(private_key)));
    } catch {
      return undefined;
    }
  }
*/
  // OTHER METHODS:

  str2rand(str: string): number {
    const len = str.length,
          seedstr = (len >= Sodium.crypto_box_SEEDBYTES) 
                      ? str.slice(str.length - Sodium.crypto_box_SEEDBYTES)
                      : "_".repeat(Sodium.crypto_box_SEEDBYTES - len) + str,
          seedbytes = Sodium.from_string(seedstr),
          randombytes =  Sodium.randombytes_buf_deterministic(4, seedbytes),
          rand = (((randombytes[0]/256 + randombytes[1])/256 + randombytes[2])/256 + randombytes[3])/256;
    return rand;
  }

}
